import type { ManifoldToplevel } from 'manifold-3d';
import type { ScadError, SourcePosition } from './errors';
import { didYouMean, BUILTIN_FUNCTIONS } from './suggest';
import type {
  Program, Statement, Expression, ModuleCallStatement,
  ModuleDefinitionStatement, ForStatement, FunctionDefinitionStatement,
} from './ast';
import { getFont } from './fonts';
import { isSketchValue, walkEntities, rotationForPlane, type SketchEntity } from './sketchEntities';
import earcut from 'earcut';

type Manifold = InstanceType<ManifoldToplevel['Manifold']>;
type CrossSection = InstanceType<ManifoldToplevel['CrossSection']>;
type Vec3 = [number, number, number];
type Vec2 = [number, number];

export interface GeometryPart {
  manifold: Manifold;
  fullManifold?: Manifold;
  stmt: Statement;
  from: SourcePosition;
  to: SourcePosition;
  children?: GeometryPart[];
  operation?: 'union' | 'difference' | 'intersection';
  isAttach?: boolean;
  isParentBody?: boolean;
  isCut?: boolean;                    // attach mode="cut" or "intersect"
  color?: [number, number, number];  // RGB 0-1
  opacity?: number;                   // 0-1
  /** Module-call instance this part belongs to: call-site offsets, outer/inner
   *  (e.g. "117/233"). Distinguishes the same module body in different uses. */
  instance?: string;
  /**
   * Selection ghosts only: column-major 4×4 transform (SCAD coords) from the
   * part's manifold into world space, accumulated from enclosing transforms.
   * Ghosts keep their ORIGINAL manifold and only carry this matrix — creating
   * transformed Manifold copies shares lazy CSG nodes with the rendered model
   * and corrupted it. Use partWorldMesh()/partWorldBounds() to read them.
   */
  matrix?: number[];
  /** Selection-only ghost from a deeper level (inside a module or nested
   *  group). The viewport never draws it — only the part's direct children
   *  form the part, nested ones would overlap them (and paint cutters). */
  nested?: boolean;
  /** Top-level parts: the colored pieces to DRAW instead of the single
   *  combined mesh (found through unions, { } blocks and module bodies).
   *  Each piece carries its own color and ghost matrix. */
  display?: GeometryPart[];
}

/**
 * A set of selectable (ghost) parts created by one statement: a boolean/group
 * (`bool`), a shape's attach/align/position children (`attach`) or one call
 * of a user module (`module`). Every group is kept in `ctx.groups` even when
 * its map key is later overwritten (a module used twice), so outer transforms
 * move every instance. `consumed` = copied into an outer attach group.
 */
interface GhostGroup {
  kind: 'bool' | 'attach' | 'module';
  parts: GeometryPart[];
  op: 'union' | 'difference' | 'intersection';
  consumed?: boolean;
  /** module groups: every group created while evaluating that call */
  nested?: GhostGroup[];
}

function registerGroup(ctx: EvalContext, g: GhostGroup): GhostGroup {
  ctx.groups.push(g);
  return g;
}

/** Live (not consumed) groups created since registry index `since`. */
function groupsSince(ctx: EvalContext, since: number): GhostGroup[] {
  return ctx.groups.slice(since).filter(g => !g.consumed);
}

export interface FrameBox {
  min: Vec3;
  max: Vec3;
}

export interface EvalResult {
  manifold: Manifold | null;
  parts: GeometryPart[];
  debugManifold: Manifold | null;
  errors: ScadError[];
  frameBoxes: FrameBox[];
  echoLog: string[];
}

interface UserModule {
  params: { name: string; defaultValue?: Expression }[];
  body: Statement[];
}

interface UserFunction {
  params: { name: string; defaultValue?: Expression }[];
  body: Expression;
}

interface EvalContext {
  wasm: ManifoldToplevel;
  vars: Map<string, unknown>;
  fn: number;
  errors: ScadError[];
  intermediates: Manifold[];
  modules: Map<string, UserModule>;
  functions: Map<string, UserFunction>;
  callerChildren: Statement[];
  debugManifolds: Manifold[];
  booleanChildParts: Map<Statement, { op: 'union' | 'difference' | 'intersection'; parts: GeometryPart[] }>;
  attachChildParts: Map<Statement, GeometryPart[]>;
  bodyOnlyResults: Map<Statement, Manifold>;
  /** All ghost groups in creation order (see GhostGroup). */
  groups: GhostGroup[];
  /** Module call statement → its instance group (last call wins for loops). */
  moduleInstances: Map<Statement, GhostGroup>;
  /** Union-like result manifold → the group of pieces it is made of (union,
   *  { } block, module body), forwarded through transforms. Used to find the
   *  colored pieces to draw (GeometryPart.display). */
  subGroupOf: Map<Manifold, GhostGroup>;
  /** Instance path of the module call currently being evaluated ('' at top level). */
  instancePath: string;
  frameBoxes: FrameBox[];
  phantomBBox: { min: Vec3; max: Vec3 } | null;
  recursionDepth: number;
  currentColor?: [number, number, number];  // RGB 0-1
  currentOpacity?: number;                   // 0-1
  manifoldColors: Map<Manifold, { color?: [number, number, number]; opacity?: number }>;
  importedManifolds: Map<string, Manifold>;
  echoLog: string[];
  operations: number;
  deadline: number;
  // Set by handlePolygon2D when its input is a sketch value; consumed by
  // handleLinearExtrude to apply plane rotation + origin translation to the
  // resulting Manifold. Cleared after consumption.
  pendingSketchPlane?: { plane: string | Vec3; origin: Vec3 };
}

const MAX_RECURSION_DEPTH = 500;
const EVAL_TIMEOUT_MS = 5000; // 5 seconds max for evaluation

class EvalTimeoutError extends Error {
  constructor() {
    super('Evaluation timeout: computation took too long (>5s). Check for infinite loops or very large iterations.');
    this.name = 'EvalTimeoutError';
  }
}

function checkTimeout(ctx: EvalContext): void {
  ctx.operations++;
  // Check every 4096 operations to minimize overhead
  if ((ctx.operations & 0xFFF) === 0 && performance.now() > ctx.deadline) {
    throw new EvalTimeoutError();
  }
}

function evalError(ctx: EvalContext, message: string, from: SourcePosition, to: SourcePosition): void {
  ctx.errors.push({ message, from, to, severity: 'error' });
}

/**
 * Give a boolean result the color its inputs imply (results are created
 * outside the color() that tagged the inputs): difference/intersection keep
 * the FIRST child's color — the material that remains; union only when every
 * child has the same color (mixed colors are drawn per child by the viewport).
 * An enclosing color() still overrides this afterwards.
 */
function inheritColor(ctx: EvalContext, result: Manifold, op: 'union' | 'difference' | 'intersection', inputs: Manifold[]): void {
  if (ctx.manifoldColors.has(result) || inputs.length === 0) return;
  const first = ctx.manifoldColors.get(inputs[0]);
  if (op !== 'union') {
    if (first) ctx.manifoldColors.set(result, first);
    return;
  }
  if (!first) return;
  const same = inputs.every(m => {
    const c = ctx.manifoldColors.get(m);
    return !!c && c.opacity === first.opacity && (c.color ?? []).join() === (first.color ?? []).join();
  });
  if (same) ctx.manifoldColors.set(result, first);
}

/** Error/warning for an unknown name, with a "did you mean" quick fix when a close match exists. */
function unknownNameError(
  ctx: EvalContext, what: string, name: string, candidates: Iterable<string>,
  from: SourcePosition, to: SourcePosition, severity: 'error' | 'warning' = 'error',
): void {
  // Report each unknown name at a given location once (loops/recursion re-evaluate it)
  if (ctx.errors.some(e => e.from.offset === from.offset && e.message.includes(`'${name}'`))) return;
  const guess = didYouMean(name, candidates);
  ctx.errors.push({
    message: `${what} '${name}'${guess ? ` — did you mean '${guess}'?` : ''}`,
    from, to, severity,
    ...(guess ? { suggestion: { word: name, replacement: guess } } : {}),
  });
}

/** Source range of a module call's name (the call starts with it). */
function callNameRange(stmt: { from: SourcePosition; name: string }): [SourcePosition, SourcePosition] {
  const f = stmt.from;
  return [f, { line: f.line, col: f.col + stmt.name.length, offset: f.offset + stmt.name.length }];
}

function trackIntermediate(ctx: EvalContext, m: Manifold): Manifold {
  ctx.intermediates.push(m);
  return m;
}

function cleanupIntermediates(intermediates: Manifold[], keep: (Manifold | null)[]): void {
  const keepSet = new Set(keep);
  for (const m of intermediates) {
    if (!keepSet.has(m)) {
      try { m.delete(); } catch { /* already freed */ }
    }
  }
}

// --- Expression Evaluation ---

function evalExpr(expr: Expression, ctx: EvalContext): unknown {
  checkTimeout(ctx);
  switch (expr.type) {
    case 'NumberLiteral': return expr.value;
    case 'BooleanLiteral': return expr.value;
    case 'StringLiteral': return expr.value;

    case 'VectorExpression': {
      const result: unknown[] = [];
      for (const el of expr.elements) {
        if (el.type === 'EachExpression') {
          const val = evalExpr(el.expr, ctx);
          if (Array.isArray(val)) {
            result.push(...val);
          } else {
            result.push(val);
          }
        } else {
          result.push(evalExpr(el, ctx));
        }
      }
      return result;
    }

    case 'IdentifierExpression': {
      if (expr.name === 'undef') return undefined;
      if (ctx.vars.has(expr.name)) return ctx.vars.get(expr.name);
      unknownNameError(ctx, 'Undefined variable', expr.name, ctx.vars.keys(), expr.from, expr.to);
      return 0;
    }

    case 'UnaryExpression': {
      const val = evalExpr(expr.operand, ctx);
      if (expr.operator === '-') {
        // OpenSCAD negates vectors element-wise (e.g. translate(-[a,b,c]))
        const neg = (v: unknown): unknown =>
          Array.isArray(v) ? v.map(neg) : typeof v === 'number' ? -v : undefined;
        return neg(val);
      }
      if (expr.operator === '!') return !val;
      return 0;
    }

    case 'BinaryExpression': {
      const left = evalExpr(expr.left, ctx);
      const right = evalExpr(expr.right, ctx);
      const la = Array.isArray(left);
      const ra = Array.isArray(right);
      switch (expr.operator) {
        case '+':
          if (la && ra) return (left as number[]).map((v, i) => v + ((right as number[])[i] ?? 0));
          if (!la && !ra) return (left as number) + (right as number);
          break;
        case '-':
          if (la && ra) return (left as number[]).map((v, i) => v - ((right as number[])[i] ?? 0));
          if (!la && !ra) return (left as number) - (right as number);
          break;
        case '*':
          if (!la && ra) return (right as number[]).map(v => (left as number) * v);
          if (la && !ra) return (left as number[]).map(v => v * (right as number));
          if (!la && !ra) return (left as number) * (right as number);
          break;
        case '/':
          if (!la && !ra) return (right as number) !== 0 ? (left as number) / (right as number) : 0;
          break;
        case '%':
          if (!la && !ra) return (right as number) !== 0 ? (left as number) % (right as number) : 0;
          break;
        case '^':
          if (!la && !ra) return Math.pow(left as number, right as number);
          break;
        case '<': return (left as number) < (right as number);
        case '>': return (left as number) > (right as number);
        case '<=': return (left as number) <= (right as number);
        case '>=': return (left as number) >= (right as number);
        case '==': return left === right;
        case '!=': return left !== right;
        case '&&': return left && right;
        case '||': return left || right;
      }
      return 0;
    }

    case 'TernaryExpression': {
      const cond = evalExpr(expr.condition, ctx);
      return cond ? evalExpr(expr.ifExpr, ctx) : evalExpr(expr.elseExpr, ctx);
    }

    case 'RangeExpression': {
      const start = evalExpr(expr.start, ctx) as number;
      const end = evalExpr(expr.end, ctx) as number;
      const step = expr.step ? (evalExpr(expr.step, ctx) as number) : (start <= end ? 1 : -1);
      if (step === 0) {
        evalError(ctx, 'Range step cannot be zero', expr.from, expr.to);
        return [];
      }
      const result: number[] = [];
      const limit = 10000;
      if (step > 0) {
        for (let i = start; i <= end && result.length < limit; i += step) {
          result.push(i);
        }
      } else {
        for (let i = start; i >= end && result.length < limit; i += step) {
          result.push(i);
        }
      }
      return result;
    }

    case 'IndexExpression': {
      const obj = evalExpr(expr.object, ctx);
      const idx = evalExpr(expr.index, ctx) as number;
      if (Array.isArray(obj)) {
        const i = Math.floor(idx);
        if (i >= 0 && i < obj.length) return obj[i];
        return 0;
      }
      evalError(ctx, 'Index operator requires an array', expr.from, expr.to);
      return 0;
    }

    case 'FunctionCallExpression': {
      const name = expr.name;

      // Array built-in functions
      if (name === 'len') {
        const arg = evalExpr(expr.args[0].value, ctx);
        if (Array.isArray(arg)) return arg.length;
        if (typeof arg === 'string') return arg.length;
        evalError(ctx, 'len() requires an array or string', expr.from, expr.to);
        return 0;
      }
      if (name === 'concat') {
        const result: unknown[] = [];
        for (const a of expr.args) {
          const val = evalExpr(a.value, ctx);
          if (Array.isArray(val)) {
            result.push(...val);
          } else {
            result.push(val);
          }
        }
        return result;
      }

      // String functions
      if (name === 'str') {
        return expr.args.map(a => {
          const v = evalExpr(a.value, ctx);
          if (v === undefined) return 'undef';
          return String(v);
        }).join('');
      }
      if (name === 'chr') {
        const code = evalExpr(expr.args[0].value, ctx) as number;
        if (Array.isArray(code)) return (code as number[]).map(c => String.fromCharCode(c)).join('');
        return String.fromCharCode(code);
      }
      if (name === 'ord') {
        const s = evalExpr(expr.args[0].value, ctx) as string;
        if (typeof s === 'string' && s.length > 0) return s.charCodeAt(0);
        return undefined;
      }
      if (name === 'search') {
        const match = evalExpr(expr.args[0].value, ctx);
        const source = evalExpr(expr.args[1].value, ctx);
        if (typeof match === 'string' && typeof source === 'string') {
          // Search each character in match within source
          return Array.from(match).map(ch => {
            const indices: number[] = [];
            for (let i = 0; i < source.length; i++) {
              if (source[i] === ch) indices.push(i);
            }
            return indices.length === 1 ? indices[0] : indices;
          });
        }
        if (Array.isArray(source)) {
          if (typeof match === 'string') {
            // Search string chars in array of strings
            return Array.from(match).map(ch => {
              const indices: number[] = [];
              for (let i = 0; i < source.length; i++) {
                if (source[i] === ch) indices.push(i);
              }
              return indices.length === 1 ? indices[0] : indices;
            });
          }
          // Search value in array
          const idx = source.indexOf(match);
          return idx >= 0 ? [idx] : [];
        }
        return [];
      }

      // Type check functions
      if (name === 'is_num') {
        const val = evalExpr(expr.args[0].value, ctx);
        return typeof val === 'number' && isFinite(val);
      }
      if (name === 'is_string') return typeof evalExpr(expr.args[0].value, ctx) === 'string';
      if (name === 'is_list') return Array.isArray(evalExpr(expr.args[0].value, ctx));
      if (name === 'is_bool') return typeof evalExpr(expr.args[0].value, ctx) === 'boolean';
      if (name === 'is_undef') return evalExpr(expr.args[0].value, ctx) === undefined;
      if (name === 'is_function') return false; // functions are not first-class values

      // Vector math functions
      if (name === 'norm') {
        const v = evalExpr(expr.args[0].value, ctx) as number[];
        if (Array.isArray(v)) {
          return Math.sqrt(v.reduce((sum, x) => sum + x * x, 0));
        }
        return Math.abs(v as unknown as number);
      }
      if (name === 'cross') {
        const a = evalExpr(expr.args[0].value, ctx) as number[];
        const b = evalExpr(expr.args[1].value, ctx) as number[];
        if (Array.isArray(a) && Array.isArray(b) && a.length >= 3 && b.length >= 3) {
          return [
            a[1] * b[2] - a[2] * b[1],
            a[2] * b[0] - a[0] * b[2],
            a[0] * b[1] - a[1] * b[0],
          ];
        }
        return [0, 0, 0];
      }

      // rands(min, max, count, seed?) — returns array of random numbers
      if (name === 'rands') {
        const rmin = evalExpr(expr.args[0].value, ctx) as number;
        const rmax = evalExpr(expr.args[1].value, ctx) as number;
        const count = evalExpr(expr.args[2].value, ctx) as number;
        return Array.from({ length: count }, () => rmin + Math.random() * (rmax - rmin));
      }

      // 2D sketch entities — tagged arrays, consumed by polygon().
      // Optional `construction=true` flag appends a trailing marker
      // `{ construction: true }`; walkEntities skips such entities so they
      // serve as reference geometry only and never enter the extruded profile.
      if (name === 'line') {
        const from = getArg(expr.args, 'from', 0, ctx) ?? [0, 0];
        const to = getArg(expr.args, 'to', 1, ctx) ?? [0, 0];
        const out: unknown[] = ['line', from, to];
        if (getArg(expr.args, 'construction', -1, ctx)) out.push({ construction: true });
        return out;
      }
      if (name === 'arc') {
        const center = getArg(expr.args, 'center', 0, ctx) ?? [0, 0];
        const r = getArg(expr.args, 'r', 1, ctx) ?? 1;
        const start = getArg(expr.args, 'start', 2, ctx) ?? 0;
        const end = getArg(expr.args, 'end', 3, ctx) ?? 90;
        const out: unknown[] = ['arc', center, r, start, end];
        if (getArg(expr.args, 'construction', -1, ctx)) out.push({ construction: true });
        return out;
      }
      if (name === 'circle_entity' || name === 'full_circle') {
        const center = getArg(expr.args, 'center', 0, ctx) ?? [0, 0];
        const r = getArg(expr.args, 'r', 1, ctx) ?? 1;
        const out: unknown[] = ['circle', center, r];
        if (getArg(expr.args, 'construction', -1, ctx)) out.push({ construction: true });
        return out;
      }
      if (name === 'rect') {
        const a = getArg(expr.args, 'a', 0, ctx) ?? [0, 0];
        const b = getArg(expr.args, 'b', 1, ctx) ?? [0, 0];
        const out: unknown[] = ['rect', a, b];
        if (getArg(expr.args, 'construction', -1, ctx)) out.push({ construction: true });
        return out;
      }
      if (name === 'bezier') {
        const p0 = getArg(expr.args, 'p0', 0, ctx) ?? [0, 0];
        const c1 = getArg(expr.args, 'c1', 1, ctx) ?? [0, 0];
        const c2 = getArg(expr.args, 'c2', 2, ctx) ?? [0, 0];
        const p3 = getArg(expr.args, 'p3', 3, ctx) ?? [0, 0];
        const out: unknown[] = ['bezier', p0, c1, c2, p3];
        if (getArg(expr.args, 'construction', -1, ctx)) out.push({ construction: true });
        return out;
      }
      if (name === 'spline') {
        const pts = getArg(expr.args, 'points', 0, ctx) ?? [];
        const out: unknown[] = ['spline', pts];
        if (getArg(expr.args, 'construction', -1, ctx)) out.push({ construction: true });
        return out;
      }
      if (name === 'image') {
        // Reference-image entity for the 2D sketch editor. Pure data: the
        // sketch evaluator ignores it (walker skips 'image' tags). src is
        // typically a localStorage key pointing at a data URL.
        const src = getArg(expr.args, 'src', 0, ctx) ?? '';
        const corner = getArg(expr.args, 'corner', 1, ctx) ?? [0, 0];
        const size = getArg(expr.args, 'size', 2, ctx) ?? [10, 10];
        const opacity = getArg(expr.args, 'opacity', 3, ctx) ?? 1;
        const rotation = getArg(expr.args, 'rotation', 4, ctx) ?? 0;
        return ['image', src, corner, size, opacity, rotation];
      }
      if (name === 'dim') {
        // Pure data record; polygon() skips these. Preserve all args in the
        // order given so the 2D editor can round-trip them later.
        const payload: unknown[] = ['dim'];
        for (const a of expr.args) {
          payload.push(a.name ? [a.name, evalExpr(a.value, ctx)] : evalExpr(a.value, ctx));
        }
        return payload;
      }
      if (name === 'sketch') {
        const plane = getArg(expr.args, 'plane', 0, ctx) ?? 'XY';
        const origin = getArg(expr.args, 'origin', 1, ctx) ?? [0, 0, 0];
        const entities = getArg(expr.args, 'entities', 2, ctx) ?? [];
        return ['sketch', plane, origin, entities];
      }

      // Vector built-ins (accept array args)
      if (name === 'norm') {
        const v = evalExpr(expr.args[0].value, ctx) as number[];
        if (!Array.isArray(v)) return 0;
        let s = 0;
        for (const x of v) s += (x as number) * (x as number);
        return Math.sqrt(s);
      }
      if (name === 'cross') {
        const a = evalExpr(expr.args[0].value, ctx) as number[];
        const b = evalExpr(expr.args[1].value, ctx) as number[];
        if (!Array.isArray(a) || !Array.isArray(b)) return 0;
        return [
          (a[1] ?? 0) * (b[2] ?? 0) - (a[2] ?? 0) * (b[1] ?? 0),
          (a[2] ?? 0) * (b[0] ?? 0) - (a[0] ?? 0) * (b[2] ?? 0),
          (a[0] ?? 0) * (b[1] ?? 0) - (a[1] ?? 0) * (b[0] ?? 0),
        ] as unknown as number;
      }

      // Math built-in functions
      // OpenSCAD clamps inputs to asin/acos to [-1,1] and sqrt to [0,∞)
      // to avoid NaN from floating-point imprecision at boundaries.
      const mathFns: Record<string, (...args: number[]) => number> = {
        sin: (a: number) => Math.sin(a * Math.PI / 180),
        cos: (a: number) => Math.cos(a * Math.PI / 180),
        tan: (a: number) => Math.tan(a * Math.PI / 180),
        asin: (a: number) => Math.asin(Math.max(-1, Math.min(1, a))) * 180 / Math.PI,
        acos: (a: number) => Math.acos(Math.max(-1, Math.min(1, a))) * 180 / Math.PI,
        atan: (a: number) => Math.atan(a) * 180 / Math.PI,
        atan2: (y: number, x: number) => Math.atan2(y, x) * 180 / Math.PI,
        abs: Math.abs,
        sign: Math.sign,
        ceil: Math.ceil,
        floor: Math.floor,
        round: Math.round,
        sqrt: (a: number) => Math.sqrt(Math.max(0, a)),
        pow: Math.pow,
        exp: Math.exp,
        min: (...a: number[]) => a.length === 1 && Array.isArray(a[0]) ? Math.min(...(a[0] as unknown as number[])) : Math.min(...a),
        max: (...a: number[]) => a.length === 1 && Array.isArray(a[0]) ? Math.max(...(a[0] as unknown as number[])) : Math.max(...a),
        ln: Math.log,
        log: (a: number) => Math.log(a) / Math.log(10),
      };
      if (mathFns[name]) {
        const args = expr.args.map(a => evalExpr(a.value, ctx) as number);
        return mathFns[name](...args);
      }

      // User-defined functions
      const userFn = ctx.functions.get(name);
      if (userFn) {
        if (ctx.recursionDepth >= MAX_RECURSION_DEPTH) {
          evalError(ctx, `Maximum recursion depth (${MAX_RECURSION_DEPTH}) exceeded in function '${name}'`, expr.from, expr.to);
          return 0;
        }
        const prevVars = ctx.vars;
        const newVars = new Map(prevVars);
        for (let i = 0; i < userFn.params.length; i++) {
          const param = userFn.params[i];
          // Named argument lookup
          const namedArg = expr.args.find(a => a.name === param.name);
          if (namedArg) {
            newVars.set(param.name, evalExpr(namedArg.value, ctx));
          } else if (i < expr.args.length && !expr.args[i].name) {
            newVars.set(param.name, evalExpr(expr.args[i].value, ctx));
          } else if (param.defaultValue) {
            newVars.set(param.name, evalExpr(param.defaultValue, ctx));
          }
        }
        ctx.vars = newVars;
        ctx.recursionDepth++;
        const result = evalExpr(userFn.body, ctx);
        ctx.recursionDepth--;
        ctx.vars = prevVars;
        return result;
      }

      unknownNameError(ctx, 'Unknown function', name, [...BUILTIN_FUNCTIONS, ...ctx.functions.keys()],
        expr.from, callNameRange(expr)[1]);
      return 0;
    }

    case 'ListCompExpression': {
      const rangeVal = evalExpr(expr.range, ctx);
      if (!Array.isArray(rangeVal)) {
        evalError(ctx, 'List comprehension range must be an array', expr.from, expr.to);
        return [];
      }
      const result: unknown[] = [];
      const prevVars = ctx.vars;
      for (const item of rangeVal) {
        const childVars = new Map(prevVars);
        childVars.set(expr.variable, item);
        ctx.vars = childVars;
        if (expr.condition) {
          const cond = evalExpr(expr.condition, ctx);
          if (!cond) continue;
        }
        const val = evalExpr(expr.body, ctx);
        if (expr.body.type === 'EachExpression' && Array.isArray(val)) {
          result.push(...val);
        } else {
          result.push(val);
        }
      }
      ctx.vars = prevVars;
      return result;
    }

    case 'EachExpression': {
      const val = evalExpr(expr.expr, ctx);
      return val; // spreading is handled by VectorExpression
    }

    case 'LetExpression': {
      const prevVars = ctx.vars;
      const newVars = new Map(prevVars);
      for (const binding of expr.bindings) {
        newVars.set(binding.name, evalExpr(binding.value, { ...ctx, vars: newVars }));
      }
      ctx.vars = newVars;
      const result = evalExpr(expr.body, ctx);
      ctx.vars = prevVars;
      return result;
    }
  }
}

// --- Argument Helpers ---

function getArg(args: { name?: string; value: Expression }[], name: string, positionalIndex: number, ctx: EvalContext): unknown | undefined {
  // Named lookup
  const named = args.find(a => a.name === name);
  if (named) return evalExpr(named.value, ctx);
  // Positional
  if (positionalIndex >= 0 && positionalIndex < args.length && !args[positionalIndex].name) {
    return evalExpr(args[positionalIndex].value, ctx);
  }
  return undefined;
}

function getArgNum(args: { name?: string; value: Expression }[], name: string, positionalIndex: number, ctx: EvalContext, defaultVal: number): number {
  const val = getArg(args, name, positionalIndex, ctx);
  if (val === undefined) return defaultVal;
  return val as number;
}

function getArgBool(args: { name?: string; value: Expression }[], name: string, positionalIndex: number, ctx: EvalContext, defaultVal: boolean): boolean {
  const val = getArg(args, name, positionalIndex, ctx);
  if (val === undefined) return defaultVal;
  return !!val;
}

function getArgVec3(args: { name?: string; value: Expression }[], name: string, positionalIndex: number, ctx: EvalContext, defaultVal: Vec3): Vec3 {
  const val = getArg(args, name, positionalIndex, ctx);
  if (val === undefined) return defaultVal;
  if (Array.isArray(val)) {
    return [
      (val[0] as number) ?? defaultVal[0],
      (val[1] as number) ?? defaultVal[1],
      (val[2] as number) ?? defaultVal[2],
    ];
  }
  if (typeof val === 'number') return [val, val, val];
  return defaultVal;
}

function getArgStr(args: { name?: string; value: Expression }[], name: string, positionalIndex: number, ctx: EvalContext, defaultVal: string): string {
  const val = getArg(args, name, positionalIndex, ctx);
  if (val === undefined) return defaultVal;
  if (typeof val === 'string') return val;
  return defaultVal;
}

// --- Module Handlers ---

type ModuleHandler = (stmt: ModuleCallStatement, ctx: EvalContext) => Manifold | null;

function hoistDefinitions(stmts: Statement[], ctx: EvalContext): void {
  for (const s of stmts) {
    if (s.type === 'ModuleDefinitionStatement') {
      ctx.modules.set(s.name, { params: s.params, body: s.body });
    } else if (s.type === 'FunctionDefinitionStatement') {
      ctx.functions.set(s.name, { params: s.params, body: s.body });
    }
  }
}

/** A transform produced `m` from `child`: carry its piece partition along. */
function forwardSubGroup(ctx: EvalContext, child: Manifold, m: Manifold): void {
  const g = ctx.subGroupOf.get(child);
  if (g) ctx.subGroupOf.set(m, g);
}

function evalChildren(children: Statement[], ctx: EvalContext): Manifold | null {
  hoistDefinitions(children, ctx);
  const results: Manifold[] = [];
  const blockParts: GeometryPart[] = [];
  for (const child of children) {
    const m = evalStatement(child, ctx);
    if (m) {
      results.push(m);
      blockParts.push({ manifold: m, stmt: child, from: child.from, to: child.to });
    }
  }
  if (results.length === 0) return null;
  if (results.length === 1) return results[0];
  // Implicit union
  let combined = results[0];
  for (let i = 1; i < results.length; i++) {
    const next = combined.add(results[i]);
    trackIntermediate(ctx, next);
    combined = next;
  }
  inheritColor(ctx, combined, 'union', results);
  // { a; b; } block: remember its pieces (selection inside modules, coloring)
  ctx.subGroupOf.set(combined, registerGroup(ctx, { kind: 'bool', op: 'union', parts: blockParts }));
  return combined;
}

// --- Bevel / Chamfer edge model ---

interface EdgeSpec {
  dir: number;  // 0=X, 1=Y, 2=Z — axis the edge runs along
  s1: number;   // sign on first perpendicular axis
  s2: number;   // sign on second perpendicular axis
}

// Perpendicular axis pairs for each edge direction
// dir=0 (X-edge): perp axes are Y(1), Z(2)
// dir=1 (Y-edge): perp axes are X(0), Z(2)
// dir=2 (Z-edge): perp axes are X(0), Y(1)
const PERP_AXES: [number, number][] = [[1, 2], [0, 2], [0, 1]];

const ALL_12_EDGES: EdgeSpec[] = [];
for (let dir = 0; dir < 3; dir++) {
  for (const s1 of [-1, 1]) {
    for (const s2 of [-1, 1]) {
      ALL_12_EDGES.push({ dir, s1, s2 });
    }
  }
}

/** Convert an anchor vector to a list of EdgeSpecs. */
function anchorToEdges(anchor: number[]): EdgeSpec[] {
  const nz = [anchor[0] !== 0, anchor[1] !== 0, anchor[2] !== 0];
  const count = nz.filter(Boolean).length;

  if (count === 2) {
    // Two non-zero components → one specific edge
    const dir = nz.indexOf(false);
    const [pa1, pa2] = PERP_AXES[dir];
    return [{ dir, s1: Math.sign(anchor[pa1]), s2: Math.sign(anchor[pa2]) }];
  }

  if (count === 1) {
    // One non-zero component → all 4 edges of that face
    const faceAxis = nz.indexOf(true);
    const sign = Math.sign(anchor[faceAxis]);
    const edges: EdgeSpec[] = [];
    // The 4 edges bordering this face: 2 directions × 2 signs
    for (let d = 0; d < 3; d++) {
      if (d === faceAxis) continue;
      const [pa1, pa2] = PERP_AXES[d];
      // This edge borders our face if one of its perp axes is faceAxis with the right sign
      for (const otherSign of [-1, 1]) {
        let s1: number, s2: number;
        if (pa1 === faceAxis) {
          s1 = sign; s2 = otherSign;
        } else {
          s1 = otherSign; s2 = sign;
        }
        edges.push({ dir: d, s1, s2 });
      }
    }
    return edges;
  }

  if (count === 3) {
    // Three non-zero components → 3 edges meeting at that corner
    const edges: EdgeSpec[] = [];
    for (let dir = 0; dir < 3; dir++) {
      const [pa1, pa2] = PERP_AXES[dir];
      edges.push({ dir, s1: Math.sign(anchor[pa1]), s2: Math.sign(anchor[pa2]) });
    }
    return edges;
  }

  return [];
}

/** Apply fillet (round), concave fillet (negative), or chamfer (flat cut) to selected edges of a cube. */
function applyBevelChamfer(
  cube: Manifold,
  ManifoldCtor: ManifoldToplevel['Manifold'],
  sx: number, sy: number, sz: number, center: boolean,
  edges: EdgeSpec[], filletR: number, chamferC: number, fn: number,
  ctx: EvalContext
): Manifold {
  const halfSizes = [sx / 2, sy / 2, sz / 2];
  const offset: Vec3 = center ? [0, 0, 0] : [sx / 2, sy / 2, sz / 2];
  // fillet > 0 = convex round, fillet < 0 = concave scoop, chamfer > 0 = flat cut
  const mode = filletR !== 0 ? (filletR > 0 ? 'bevel' : 'ibevel') : 'chamfer';
  const rawSize = filletR !== 0 ? Math.abs(filletR) : chamferC;

  // Deduplicate edges
  const edgeSet = new Set(edges.map(e => `${e.dir},${e.s1},${e.s2}`));

  let result = cube;
  for (const key of edgeSet) {
    const [dir, s1, s2] = key.split(',').map(Number);
    const [pa1, pa2] = PERP_AXES[dir];

    const r = rawSize;
    if (r <= 0) continue;

    // Length along edge direction (with small overshoot to ensure clean cut)
    const edgeLen = halfSizes[dir] * 2 + 2;

    // Position of the edge in world space
    const edgePos: Vec3 = [offset[0], offset[1], offset[2]];
    edgePos[pa1] += s1 * halfSizes[pa1];
    edgePos[pa2] += s2 * halfSizes[pa2];

    let cutter: Manifold;

    if (mode === 'bevel') {
      // Convex round fillet: create a corner block, subtract a cylinder, leaving a concave corner piece
      // The corner block sits at the edge corner, sized r × r × edgeLen
      const blockSize: Vec3 = [0, 0, 0];
      blockSize[dir] = edgeLen;
      blockSize[pa1] = r;
      blockSize[pa2] = r;

      const blockPos: Vec3 = [offset[0], offset[1], offset[2]];
      blockPos[dir] = offset[dir];  // centered along edge
      blockPos[pa1] = edgePos[pa1] - s1 * (r / 2);
      blockPos[pa2] = edgePos[pa2] - s2 * (r / 2);

      let block = ManifoldCtor.cube(blockSize, true).translate(blockPos);
      trackIntermediate(ctx, block);

      // Cylinder along the edge direction, positioned at corner inset by r
      const cylPos: Vec3 = [offset[0], offset[1], offset[2]];
      cylPos[dir] = offset[dir];
      cylPos[pa1] = edgePos[pa1] - s1 * r;
      cylPos[pa2] = edgePos[pa2] - s2 * r;

      // Manifold.cylinder is Z-aligned by default. Rotate to align with edge direction.
      let cyl = ManifoldCtor.cylinder(edgeLen, r, r, Math.max(fn, 8), true);
      if (dir === 0) {
        // Z→X: rotate 90° around Y
        cyl = cyl.rotate([0, 90, 0]);
      } else if (dir === 1) {
        // Z→Y: rotate -90° around X
        cyl = cyl.rotate([-90, 0, 0]);
      }
      // dir === 2: already Z-aligned
      cyl = cyl.translate(cylPos);
      trackIntermediate(ctx, cyl);

      cutter = block.subtract(cyl);
      trackIntermediate(ctx, cutter);
    } else if (mode === 'ibevel') {
      // Concave fillet: subtract a cylinder centered right at the edge
      // This scoops a quarter-cylinder groove into the cube's edge
      let cyl = ManifoldCtor.cylinder(edgeLen, r, r, Math.max(fn, 8), true);
      if (dir === 0) {
        cyl = cyl.rotate([0, 90, 0]);
      } else if (dir === 1) {
        cyl = cyl.rotate([-90, 0, 0]);
      }
      cyl = cyl.translate(edgePos);
      trackIntermediate(ctx, cyl);

      cutter = cyl;
    } else {
      // Chamfer: rotated box that cuts a right triangle with legs c from the corner
      const boxSize: Vec3 = [0, 0, 0];
      boxSize[dir] = edgeLen;
      // The diagonal box needs to be sqrt(2)*c to cut legs of length c
      const diagSize = r * Math.SQRT2;
      boxSize[pa1] = diagSize;
      boxSize[pa2] = diagSize;

      let box = ManifoldCtor.cube(boxSize, true);
      // Rotate 45° in the plane perpendicular to the edge direction
      const rot: Vec3 = [0, 0, 0];
      if (dir === 0) {
        rot[0] = 45;  // rotate around X in YZ plane
      } else if (dir === 1) {
        rot[1] = 45;  // rotate around Y in XZ plane
      } else {
        rot[2] = 45;  // rotate around Z in XY plane
      }
      box = box.rotate(rot).translate(edgePos);
      trackIntermediate(ctx, box);

      cutter = box;
    }

    const next = result.subtract(cutter);
    trackIntermediate(ctx, next);
    result = next;
  }

  return result;
}

function handleCube(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const { Manifold } = ctx.wasm;
  const sizeArg = getArg(stmt.args, 'size', 0, ctx);
  const center = getArgBool(stmt.args, 'center', 1, ctx, false);

  let sx: number, sy: number, sz: number;
  if (Array.isArray(sizeArg)) {
    sx = (sizeArg[0] as number) || 1;
    sy = (sizeArg[1] as number) || 1;
    sz = (sizeArg[2] as number) || 1;
  } else {
    const s = (sizeArg as number) || 1;
    sx = sy = sz = s;
  }

  let m = Manifold.cube([sx, sy, sz], center);

  // Fillet (positive = convex round, negative = concave scoop) or chamfer (flat cut)
  const filletR = getArgNum(stmt.args, 'fillet', -1, ctx, 0);
  const chamferC = getArgNum(stmt.args, 'chamfer', -1, ctx, 0);

  if (filletR !== 0 || chamferC > 0) {
    const edgesArg = getArg(stmt.args, 'edges', -1, ctx);
    let edgeSpecs: EdgeSpec[];
    if (edgesArg === undefined) {
      edgeSpecs = ALL_12_EDGES;
    } else if (Array.isArray(edgesArg) && edgesArg.length > 0 && Array.isArray(edgesArg[0])) {
      // Array of anchors: [TOP+FRONT, BOTTOM+BACK]
      edgeSpecs = (edgesArg as number[][]).flatMap(anchorToEdges);
    } else if (Array.isArray(edgesArg)) {
      // Single anchor: TOP+FRONT
      edgeSpecs = anchorToEdges(edgesArg as number[]);
    } else {
      edgeSpecs = ALL_12_EDGES;
    }

    const fn = getArgNum(stmt.args, '$fn', -1, ctx, ctx.fn);
    m = applyBevelChamfer(m, Manifold, sx, sy, sz, center, edgeSpecs, filletR, chamferC, Math.max(fn, 8), ctx);
  }

  trackIntermediate(ctx, m);
  return tryApplyCutPattern(m, stmt, ctx);
}

function handleSphere(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const { Manifold } = ctx.wasm;
  const d = getArgNum(stmt.args, 'd', -1, ctx, -1);
  const r = d >= 0 ? d / 2 : getArgNum(stmt.args, 'r', 0, ctx, 1);
  const fn = getArgNum(stmt.args, '$fn', -1, ctx, ctx.fn);

  const m = Manifold.sphere(r, Math.max(fn, 8));
  trackIntermediate(ctx, m);
  return tryApplyCutPattern(m, stmt, ctx);
}

function handleCylinder(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const { Manifold, CrossSection } = ctx.wasm;
  const h = getArgNum(stmt.args, 'h', 0, ctx, 1);
  const d = getArgNum(stmt.args, 'd', -1, ctx, -1);
  const d1 = getArgNum(stmt.args, 'd1', -1, ctx, -1);
  const d2 = getArgNum(stmt.args, 'd2', -1, ctx, -1);
  const r = getArgNum(stmt.args, 'r', 1, ctx, -1);
  const r1 = getArgNum(stmt.args, 'r1', -1, ctx, -1);
  const r2 = getArgNum(stmt.args, 'r2', -1, ctx, -1);
  const center = getArgBool(stmt.args, 'center', -1, ctx, false);
  const fn = getArgNum(stmt.args, '$fn', -1, ctx, ctx.fn);

  let radiusLow: number, radiusHigh: number;
  if (d1 >= 0 || d2 >= 0) {
    radiusLow = d1 >= 0 ? d1 / 2 : 1;
    radiusHigh = d2 >= 0 ? d2 / 2 : 1;
  } else if (r1 >= 0 || r2 >= 0) {
    radiusLow = r1 >= 0 ? r1 : 1;
    radiusHigh = r2 >= 0 ? r2 : 1;
  } else if (d >= 0) {
    radiusLow = radiusHigh = d / 2;
  } else if (r >= 0) {
    radiusLow = radiusHigh = r;
  } else {
    radiusLow = radiusHigh = 1;
  }

  const segments = Math.max(fn, 8);

  // Fillet/chamfer edge treatment
  const filletAll = getArgNum(stmt.args, 'fillet', -1, ctx, 0);
  const filletTop = getArgNum(stmt.args, 'fillet_top', -1, ctx, filletAll);
  const filletBot = getArgNum(stmt.args, 'fillet_bot', -1, ctx, filletAll);
  const chamferAll = getArgNum(stmt.args, 'chamfer', -1, ctx, 0);
  const chamferTop = getArgNum(stmt.args, 'chamfer_top', -1, ctx, chamferAll);
  const chamferBot = getArgNum(stmt.args, 'chamfer_bot', -1, ctx, chamferAll);

  // Fillet takes priority over chamfer
  const ft = filletTop > 0 ? Math.min(filletTop, radiusHigh, h / 2) : 0;
  const fb = filletBot > 0 ? Math.min(filletBot, radiusLow, h / 2) : 0;
  const ct = ft > 0 ? 0 : (chamferTop > 0 ? Math.min(chamferTop, radiusHigh, h / 2) : 0);
  const cb = fb > 0 ? 0 : (chamferBot > 0 ? Math.min(chamferBot, radiusLow, h / 2) : 0);

  const hasEdge = ft > 0 || fb > 0 || ct > 0 || cb > 0;

  if (!hasEdge) {
    // Fast path: no edge treatment
    const m = Manifold.cylinder(h, radiusLow, radiusHigh, segments, center);
    trackIntermediate(ctx, m);
    return tryApplyCutPattern(m, stmt, ctx);
  }

  // Proportionally reduce if total edge sizes exceed height
  let botSize = fb > 0 ? fb : cb;
  let topSize = ft > 0 ? ft : ct;
  if (botSize + topSize > h) {
    const scale = h / (botSize + topSize);
    botSize *= scale;
    topSize *= scale;
  }
  const adjFb = fb > 0 ? botSize : 0;
  const adjCb = cb > 0 ? botSize : 0;
  const adjFt = ft > 0 ? topSize : 0;
  const adjCt = ct > 0 ? topSize : 0;

  const filletSteps = Math.max(Math.round(fn / 4), 4);

  // Build profile: [0,0] → bottom-edge → side → top-edge → [0,h]
  const profile: [number, number][] = [];
  profile.push([0, 0]);

  // Bottom edge
  if (adjFb > 0) {
    // Quarter-circle fillet from (rLow-fb, 0) to (rLow, fb)
    const arcPts = generateArcPoints(radiusLow - adjFb, adjFb, adjFb, Math.PI * 1.5, Math.PI * 2, filletSteps);
    for (const p of arcPts) profile.push(p);
  } else if (adjCb > 0) {
    profile.push([radiusLow - adjCb, 0]);
    profile.push([radiusLow, adjCb]);
  } else {
    profile.push([radiusLow, 0]);
  }

  // Top edge (skip top treatment if pointed cone)
  if (radiusHigh <= 0) {
    profile.push([0, h]);
  } else if (adjFt > 0) {
    profile.push([radiusHigh, h - adjFt]);
    const arcPts = generateArcPoints(radiusHigh - adjFt, h - adjFt, adjFt, 0, Math.PI / 2, filletSteps);
    for (const p of arcPts) profile.push(p);
    profile.push([0, h]);
  } else if (adjCt > 0) {
    profile.push([radiusHigh, h - adjCt]);
    profile.push([radiusHigh - adjCt, h]);
    profile.push([0, h]);
  } else {
    profile.push([radiusHigh, h]);
    profile.push([0, h]);
  }

  const cs = CrossSection.ofPolygons([profile]);
  let m: Manifold;
  try {
    m = cs.revolve(segments, 360);
  } finally {
    cs.delete();
  }
  trackIntermediate(ctx, m);

  if (center) {
    const shifted = m.translate([0, 0, -h / 2]);
    trackIntermediate(ctx, shifted);
    return tryApplyCutPattern(shifted, stmt, ctx);
  }
  return tryApplyCutPattern(m, stmt, ctx);
}

function handleChamferedCylinder(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const { Manifold, CrossSection } = ctx.wasm;
  const h = getArgNum(stmt.args, 'h', 0, ctx, 1);
  const d = getArgNum(stmt.args, 'd', -1, ctx, -1);
  const d1 = getArgNum(stmt.args, 'd1', -1, ctx, -1);
  const d2 = getArgNum(stmt.args, 'd2', -1, ctx, -1);
  const r = getArgNum(stmt.args, 'r', 1, ctx, -1);
  const r1arg = getArgNum(stmt.args, 'r1', -1, ctx, -1);
  const r2arg = getArgNum(stmt.args, 'r2', -1, ctx, -1);
  const chamfer = getArgNum(stmt.args, 'chamfer', -1, ctx, 0);
  const chamferTop = getArgNum(stmt.args, 'chamfer_top', -1, ctx, chamfer);
  const chamferBot = getArgNum(stmt.args, 'chamfer_bot', -1, ctx, chamfer);
  const center = getArgBool(stmt.args, 'center', -1, ctx, false);
  const fn = getArgNum(stmt.args, '$fn', -1, ctx, ctx.fn);

  let radiusLow: number, radiusHigh: number;
  if (d1 >= 0 || d2 >= 0) {
    radiusLow = d1 >= 0 ? d1 / 2 : 1;
    radiusHigh = d2 >= 0 ? d2 / 2 : 1;
  } else if (r1arg >= 0 || r2arg >= 0) {
    radiusLow = r1arg >= 0 ? r1arg : 1;
    radiusHigh = r2arg >= 0 ? r2arg : 1;
  } else if (d >= 0) {
    radiusLow = radiusHigh = d / 2;
  } else if (r >= 0) {
    radiusLow = radiusHigh = r;
  } else {
    radiusLow = radiusHigh = 1;
  }

  const ct = Math.min(chamferTop, Math.min(radiusHigh, h / 2));
  const cb = Math.min(chamferBot, Math.min(radiusLow, h / 2));

  const profile: [number, number][] = [];
  profile.push([0, 0]);
  if (cb > 0) {
    profile.push([radiusLow - cb, 0]);
    profile.push([radiusLow, cb]);
  } else {
    profile.push([radiusLow, 0]);
  }
  if (ct > 0) {
    profile.push([radiusHigh, h - ct]);
    profile.push([radiusHigh - ct, h]);
  } else {
    profile.push([radiusHigh, h]);
  }
  profile.push([0, h]);

  const segments = Math.max(fn, 8);
  const cs = CrossSection.ofPolygons([profile]);
  let m: Manifold;
  try {
    m = cs.revolve(segments, 360);
  } finally {
    cs.delete();
  }
  trackIntermediate(ctx, m);

  if (center) {
    const shifted = m.translate([0, 0, -h / 2]);
    trackIntermediate(ctx, shifted);
    return shifted;
  }
  return m;
}

// ====================================================================
// Simple 3D Shapes — torus, pipe, ellipsoid, wedge
// ====================================================================

function handleTorus(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const { CrossSection } = ctx.wasm;
  const r1 = getArgNum(stmt.args, 'r1', 0, ctx, 5);
  const r2 = getArgNum(stmt.args, 'r2', 1, ctx, 1);
  const angle = getArgNum(stmt.args, 'angle', -1, ctx, 360);
  const fn = getArgNum(stmt.args, '$fn', -1, ctx, ctx.fn);
  const segments = Math.max(fn, 16);

  // Circle profile at offset (r1, 0)
  const circleSteps = Math.max(Math.round(segments / 2), 8);
  const profile: Vec2[] = [];
  for (let i = 0; i < circleSteps; i++) {
    const a = (i / circleSteps) * 2 * Math.PI;
    profile.push([r1 + r2 * Math.cos(a), r2 * Math.sin(a)]);
  }

  const cs = CrossSection.ofPolygons([profile]);
  let m: Manifold;
  try {
    m = cs.revolve(segments, angle);
  } finally {
    cs.delete();
  }
  trackIntermediate(ctx, m);
  return m;
}

function handlePipe(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const { CrossSection } = ctx.wasm;
  const h = getArgNum(stmt.args, 'h', 0, ctx, 10);
  const r = getArgNum(stmt.args, 'r', 1, ctx, 5);
  const tArg = getArgNum(stmt.args, 't', -1, ctx, -1);
  const rInner = getArgNum(stmt.args, 'r_inner', -1, ctx, -1);
  const center = getArgBool(stmt.args, 'center', -1, ctx, false);
  const fn = getArgNum(stmt.args, '$fn', -1, ctx, ctx.fn);
  const segments = Math.max(fn, 16);

  let inner: number;
  if (rInner >= 0) {
    inner = Math.min(rInner, r * 0.99);
  } else if (tArg >= 0) {
    inner = Math.max(r - tArg, r * 0.01);
  } else {
    inner = r * 0.8; // default wall = 20% of r
  }

  // Fillet/chamfer edge treatment
  const filletAll = getArgNum(stmt.args, 'fillet', -1, ctx, 0);
  const filletTop = getArgNum(stmt.args, 'fillet_top', -1, ctx, filletAll);
  const filletBot = getArgNum(stmt.args, 'fillet_bot', -1, ctx, filletAll);
  const chamferAll = getArgNum(stmt.args, 'chamfer', -1, ctx, 0);
  const chamferTop = getArgNum(stmt.args, 'chamfer_top', -1, ctx, chamferAll);
  const chamferBot = getArgNum(stmt.args, 'chamfer_bot', -1, ctx, chamferAll);

  // Fillet takes priority over chamfer
  const wall = r - inner;
  const maxEdge = Math.min(wall / 2, h / 2);
  const ft = filletTop > 0 ? Math.min(filletTop, maxEdge) : 0;
  const fb = filletBot > 0 ? Math.min(filletBot, maxEdge) : 0;
  const ct = ft > 0 ? 0 : (chamferTop > 0 ? Math.min(chamferTop, maxEdge) : 0);
  const cb = fb > 0 ? 0 : (chamferBot > 0 ? Math.min(chamferBot, maxEdge) : 0);

  const hasEdge = ft > 0 || fb > 0 || ct > 0 || cb > 0;
  const arcSegs = Math.max(Math.round(fn / 4), 4);

  // Build profile CCW: bottom-inner → bottom-outer → top-outer → top-inner
  const profile: Vec2[] = [];

  // Bottom-inner corner [inner, 0]
  if (fb > 0) {
    profile.push([inner, fb]);
    const arcPts = generateArcPoints(inner + fb, fb, fb, Math.PI, Math.PI * 1.5, arcSegs);
    for (const p of arcPts) profile.push(p);
  } else if (cb > 0) {
    profile.push([inner, cb]);
    profile.push([inner + cb, 0]);
  } else {
    profile.push([inner, 0]);
  }

  // Bottom-outer corner [r, 0]
  if (fb > 0) {
    const arcPts = generateArcPoints(r - fb, fb, fb, Math.PI * 1.5, Math.PI * 2, arcSegs);
    for (const p of arcPts) profile.push(p);
  } else if (cb > 0) {
    profile.push([r - cb, 0]);
    profile.push([r, cb]);
  } else {
    profile.push([r, 0]);
  }

  // Top-outer corner [r, h]
  if (ft > 0) {
    profile.push([r, h - ft]);
    const arcPts = generateArcPoints(r - ft, h - ft, ft, 0, Math.PI / 2, arcSegs);
    for (const p of arcPts) profile.push(p);
  } else if (ct > 0) {
    profile.push([r, h - ct]);
    profile.push([r - ct, h]);
  } else {
    profile.push([r, h]);
  }

  // Top-inner corner [inner, h]
  if (ft > 0) {
    const arcPts = generateArcPoints(inner + ft, h - ft, ft, Math.PI / 2, Math.PI, arcSegs);
    for (const p of arcPts) profile.push(p);
  } else if (ct > 0) {
    profile.push([inner + ct, h]);
    profile.push([inner, h - ct]);
  } else {
    profile.push([inner, h]);
  }

  const cs = CrossSection.ofPolygons([profile]);
  let m: Manifold;
  try {
    m = cs.revolve(segments, 360);
  } finally {
    cs.delete();
  }
  trackIntermediate(ctx, m);

  if (center) {
    const shifted = m.translate([0, 0, -h / 2]);
    trackIntermediate(ctx, shifted);
    return shifted;
  }
  return m;
}

function handleEllipsoid(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const { Manifold } = ctx.wasm;
  const rArg = getArg(stmt.args, 'r', 0, ctx);
  const fn = getArgNum(stmt.args, '$fn', -1, ctx, ctx.fn);
  const segments = Math.max(fn, 16);

  let rx: number, ry: number, rz: number;
  if (Array.isArray(rArg)) {
    rx = (rArg[0] as number) || 1;
    ry = (rArg[1] as number) || 1;
    rz = (rArg[2] as number) || 1;
  } else {
    const scalar = (rArg as number) || 1;
    rx = ry = rz = scalar;
  }

  // Per-axis overrides
  const rxO = getArgNum(stmt.args, 'rx', -1, ctx, -1);
  const ryO = getArgNum(stmt.args, 'ry', -1, ctx, -1);
  const rzO = getArgNum(stmt.args, 'rz', -1, ctx, -1);
  if (rxO >= 0) rx = rxO;
  if (ryO >= 0) ry = ryO;
  if (rzO >= 0) rz = rzO;

  const sphere = Manifold.sphere(1, segments);
  trackIntermediate(ctx, sphere);
  const m = sphere.scale([rx, ry, rz]);
  trackIntermediate(ctx, m);
  return m;
}

function handleWedge(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const { CrossSection } = ctx.wasm;
  const sizeArg = getArg(stmt.args, 'size', 0, ctx);
  const center = getArgBool(stmt.args, 'center', -1, ctx, false);

  let w: number, d: number, h: number;
  if (Array.isArray(sizeArg)) {
    w = (sizeArg[0] as number) || 1;
    d = (sizeArg[1] as number) || 1;
    h = (sizeArg[2] as number) || 1;
  } else {
    const s = (sizeArg as number) || 1;
    w = d = h = s;
  }

  // Triangle in XZ plane: (0,0), (w,0), (0,h) — extruded along Y (depth = d)
  const profile: Vec2[] = [[0, 0], [w, 0], [0, h]];
  const cs = CrossSection.ofPolygons([profile]);
  let m: Manifold;
  try {
    m = cs.extrude(d);
  } finally {
    cs.delete();
  }
  trackIntermediate(ctx, m);

  if (center) {
    const shifted = m.translate([-w / 2, -h / 2, -d / 2]);
    trackIntermediate(ctx, shifted);
    return shifted;
  }
  return m;
}

// ====================================================================
// Compound 3D Shapes — capsule, rounded_cube
// ====================================================================

function handleCapsule(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const { CrossSection, Manifold } = ctx.wasm;
  const h = getArgNum(stmt.args, 'h', 0, ctx, 10);
  const r = getArgNum(stmt.args, 'r', 1, ctx, 2);
  const center = getArgBool(stmt.args, 'center', -1, ctx, false);
  const fn = getArgNum(stmt.args, '$fn', -1, ctx, ctx.fn);
  const segments = Math.max(fn, 16);

  // Degenerate case: sphere
  if (h <= 2 * r) {
    const m = Manifold.sphere(r, segments);
    trackIntermediate(ctx, m);
    if (!center) {
      const shifted = m.translate([0, 0, r]);
      trackIntermediate(ctx, shifted);
      return shifted;
    }
    return m;
  }

  // Stadium profile (half-circle bottom + straight + half-circle top), revolved
  const straight = h - 2 * r;
  const circSteps = Math.max(Math.round(segments / 4), 4);
  const profile: Vec2[] = [];

  // Bottom semicircle: from (0, r) CCW to (r, 0)... actually from (0,0)
  // We need profile in XY where X=radial, Y=height
  // Bottom hemisphere: center at (0, r), sweeping from angle -PI/2 to 0 (bottom to right)
  for (let i = 0; i <= circSteps; i++) {
    const a = -Math.PI / 2 + (i / circSteps) * (Math.PI / 2);
    profile.push([r * Math.cos(a), r + r * Math.sin(a)]);
  }

  // Straight section: from (r, r) to (r, r + straight)
  profile.push([r, r + straight]);

  // Top hemisphere: center at (0, r + straight), sweeping from 0 to PI/2
  for (let i = 0; i <= circSteps; i++) {
    const a = (i / circSteps) * (Math.PI / 2);
    profile.push([r * Math.cos(a), r + straight + r * Math.sin(a)]);
  }

  // Close back to axis
  profile.push([0, h]);

  const cs = CrossSection.ofPolygons([profile]);
  let m: Manifold;
  try {
    m = cs.revolve(segments, 360);
  } finally {
    cs.delete();
  }
  trackIntermediate(ctx, m);

  if (center) {
    const shifted = m.translate([0, 0, -h / 2]);
    trackIntermediate(ctx, shifted);
    return shifted;
  }
  return m;
}

function handleRoundedCube(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const { Manifold } = ctx.wasm;
  const sizeArg = getArg(stmt.args, 'size', 0, ctx);
  const r = getArgNum(stmt.args, 'r', 1, ctx, 0.5);
  const center = getArgBool(stmt.args, 'center', -1, ctx, false);
  const fn = getArgNum(stmt.args, '$fn', -1, ctx, ctx.fn);
  const segments = Math.max(fn, 8);

  let sx: number, sy: number, sz: number;
  if (Array.isArray(sizeArg)) {
    sx = (sizeArg[0] as number) || 1;
    sy = (sizeArg[1] as number) || 1;
    sz = (sizeArg[2] as number) || 1;
  } else {
    const s = (sizeArg as number) || 1;
    sx = sy = sz = s;
  }

  // Clamp radius to half the smallest dimension
  const maxR = Math.min(sx, sy, sz) / 2;
  const cr = Math.min(r, maxR);

  // Hull of 8 spheres at inset corners
  const spheres: Manifold[] = [];
  for (const dx of [cr, sx - cr]) {
    for (const dy of [cr, sy - cr]) {
      for (const dz of [cr, sz - cr]) {
        const s = Manifold.sphere(cr, segments);
        trackIntermediate(ctx, s);
        const moved = s.translate([dx, dy, dz]);
        trackIntermediate(ctx, moved);
        spheres.push(moved);
      }
    }
  }

  const m = Manifold.hull(spheres);
  trackIntermediate(ctx, m);

  if (center) {
    const shifted = m.translate([-sx / 2, -sy / 2, -sz / 2]);
    trackIntermediate(ctx, shifted);
    return shifted;
  }
  return m;
}

// ====================================================================
// Mechanical Shapes — thread, spring, bolt, nut
// ====================================================================

// Metric bolt/nut standard dimensions
// head_h_din: DIN 931 nominal (k_min), head_h_iso: ISO 4014 average (k_max)
// All other dimensions are shared between DIN and ISO
interface MetricEntry {
  d: number; pitch: number;
  head_h_din: number; head_h_iso: number;
  head_d: number; nut_h: number; s: number;
}
const METRIC_BOLTS: Record<string, MetricEntry> = {
  //                                    DIN 931   ISO 4014
  M1:   { d: 1,    pitch: 0.25,  head_h_din: 0.7,   head_h_iso: 0.7,    head_d: 2,     nut_h: 0.8,  s: 2     },
  M1_5: { d: 1.5,  pitch: 0.35,  head_h_din: 1.05,  head_h_iso: 1.05,   head_d: 3,     nut_h: 1.2,  s: 3     },
  M2:   { d: 2,    pitch: 0.4,   head_h_din: 1.4,   head_h_iso: 1.53,   head_d: 4,     nut_h: 1.6,  s: 4     },
  M2_5: { d: 2.5,  pitch: 0.45,  head_h_din: 1.75,  head_h_iso: 1.83,   head_d: 5,     nut_h: 2,    s: 5     },
  M3:   { d: 3,    pitch: 0.5,   head_h_din: 2,     head_h_iso: 2.13,   head_d: 5.5,   nut_h: 2.4,  s: 5.5   },
  M4:   { d: 4,    pitch: 0.7,   head_h_din: 2.8,   head_h_iso: 2.93,   head_d: 7,     nut_h: 3.2,  s: 7     },
  M5:   { d: 5,    pitch: 0.8,   head_h_din: 3.5,   head_h_iso: 3.65,   head_d: 8,     nut_h: 4.7,  s: 8     },
  M6:   { d: 6,    pitch: 1.0,   head_h_din: 4,     head_h_iso: 4.15,   head_d: 10,    nut_h: 5.2,  s: 10    },
  M8:   { d: 8,    pitch: 1.25,  head_h_din: 5.3,   head_h_iso: 5.45,   head_d: 13,    nut_h: 6.8,  s: 13    },
  M10:  { d: 10,   pitch: 1.5,   head_h_din: 6.4,   head_h_iso: 6.58,   head_d: 16,    nut_h: 8.4,  s: 16    },
  M12:  { d: 12,   pitch: 1.75,  head_h_din: 7.5,   head_h_iso: 7.68,   head_d: 18,    nut_h: 10.8, s: 18    },
  M14:  { d: 14,   pitch: 2.0,   head_h_din: 8.8,   head_h_iso: 9.09,   head_d: 21,    nut_h: 12.8, s: 21    },
  M16:  { d: 16,   pitch: 2.0,   head_h_din: 10,    head_h_iso: 10.18,  head_d: 24,    nut_h: 14.8, s: 24    },
  M20:  { d: 20,   pitch: 2.5,   head_h_din: 12.5,  head_h_iso: 12.715, head_d: 30,    nut_h: 18,   s: 30    },
  M24:  { d: 24,   pitch: 3.0,   head_h_din: 15,    head_h_iso: 15.215, head_d: 36,    nut_h: 21.5, s: 36    },
  M30:  { d: 30,   pitch: 3.5,   head_h_din: 18.7,  head_h_iso: 19.12,  head_d: 46,    nut_h: 25.6, s: 46    },
};

function lookupMetricSize(sizeStr: string): MetricEntry | undefined {
  // "M10" → "M10", "M2.5" → "M2_5"
  const key = sizeStr.replace('.', '_');
  return METRIC_BOLTS[key];
}

/** Creates a thread manifold (reusable for bolt, nut, and standalone thread) */
function makeThreadManifold(
  ctx: EvalContext, d: number, pitch: number, length: number,
  starts: number, profileAngle: number, fn: number, center: boolean
): Manifold {
  const { Manifold, CrossSection } = ctx.wasm;
  const r = d / 2;
  const paRad = (profileAngle / 2) * Math.PI / 180;
  const toothH = pitch / 2;
  const outerR = r;
  const rootR = Math.max(r - toothH, 0.5);
  const totalTwist = (length / pitch) * 360;
  const slices = Math.max(Math.ceil(Math.abs(totalTwist) / 6), 4);

  const tanPA = Math.tan(paRad);
  const sectorAngle = 2 * Math.PI / starts;
  const tipHalf = Math.max(sectorAngle * 0.1, 0.03);
  const rootHalf = Math.min(tipHalf + (outerR - rootR) * tanPA / ((outerR + rootR) / 2), sectorAngle * 0.4);

  const circSteps = Math.max(Math.ceil(Math.max(fn, 32) / starts / 2), 3);
  const points: Vec2[] = [];

  for (let i = 0; i < starts; i++) {
    const tc = i * sectorAngle;
    points.push([rootR * Math.cos(tc - rootHalf), rootR * Math.sin(tc - rootHalf)]);
    points.push([outerR * Math.cos(tc - tipHalf), outerR * Math.sin(tc - tipHalf)]);
    points.push([outerR * Math.cos(tc + tipHalf), outerR * Math.sin(tc + tipHalf)]);
    points.push([rootR * Math.cos(tc + rootHalf), rootR * Math.sin(tc + rootHalf)]);

    const arcStart = tc + rootHalf;
    const arcEnd = tc + sectorAngle - rootHalf;
    for (let s = 1; s < circSteps; s++) {
      const a = arcStart + (s / circSteps) * (arcEnd - arcStart);
      points.push([rootR * Math.cos(a), rootR * Math.sin(a)]);
    }
  }

  const cs = CrossSection.ofPolygons([points]);
  let m: Manifold;
  try {
    m = cs.extrude(length, slices, totalTwist, [1, 1], center);
  } finally {
    cs.delete();
  }
  trackIntermediate(ctx, m);

  // Clip top and bottom with a cylinder
  const clipCyl = Manifold.cylinder(length, outerR, outerR, Math.max(fn, 16), center);
  trackIntermediate(ctx, clipCyl);
  const clipped = m.intersect(clipCyl);
  trackIntermediate(ctx, clipped);

  return clipped;
}

function handleThread(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const d = getArgNum(stmt.args, 'd', 0, ctx, 10);
  const pitch = getArgNum(stmt.args, 'pitch', 1, ctx, 2);
  const length = getArgNum(stmt.args, 'length', -1, ctx, 10);
  const starts = Math.max(Math.round(getArgNum(stmt.args, 'starts', -1, ctx, 1)), 1);
  const profileAngle = getArgNum(stmt.args, 'profile_angle', -1, ctx, 60);
  const center = getArgBool(stmt.args, 'center', -1, ctx, false);
  const fn = getArgNum(stmt.args, '$fn', -1, ctx, ctx.fn);
  return makeThreadManifold(ctx, d, pitch, length, starts, profileAngle, fn, center);
}

/** Build a hex prism (6-sided polygon extruded to height) */
function makeHexPrism(ctx: EvalContext, acrossFlats: number, height: number, fn?: number): Manifold {
  const { Manifold, CrossSection } = ctx.wasm;
  // across-flats → across-corners: r_corners = s / (2 * cos(30°))
  const r = acrossFlats / (2 * Math.cos(Math.PI / 6));
  const points: Vec2[] = [];
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * 2 * Math.PI + Math.PI / 6; // flat-to-flat aligned
    points.push([r * Math.cos(a), r * Math.sin(a)]);
  }
  const cs = CrossSection.ofPolygons([points]);
  let m: Manifold;
  try {
    m = cs.extrude(height);
  } finally {
    cs.delete();
  }
  trackIntermediate(ctx, m);
  return m;
}

/** Build a chamfered cylinder for bevel-cutting hex heads/nuts */
function makeChamferEnvelope(ctx: EvalContext, r: number, h: number, chamfer: number, fn: number): Manifold {
  const { Manifold, CrossSection } = ctx.wasm;
  const c = Math.min(chamfer, Math.min(r, h / 2));
  const profile: [number, number][] = [];
  profile.push([0, 0]);
  if (c > 0) {
    profile.push([r - c, 0]);
    profile.push([r, c]);
  } else {
    profile.push([r, 0]);
  }
  if (c > 0) {
    profile.push([r, h - c]);
    profile.push([r - c, h]);
  } else {
    profile.push([r, h]);
  }
  profile.push([0, h]);
  const cs = CrossSection.ofPolygons([profile]);
  let m: Manifold;
  try {
    m = cs.revolve(Math.max(fn, 16), 360);
  } finally {
    cs.delete();
  }
  trackIntermediate(ctx, m);
  return m;
}

function handleBolt(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const { Manifold } = ctx.wasm;
  const fn = getArgNum(stmt.args, '$fn', -1, ctx, ctx.fn);

  // Size lookup
  const sizeStr = getArgStr(stmt.args, 'size', 0, ctx, '');
  const metric = sizeStr ? lookupMetricSize(sizeStr) : undefined;
  if (sizeStr && !metric) {
    evalError(ctx, `Unknown bolt size "${sizeStr}". Use M1..M30 (e.g. "M10", "M2.5").`, stmt.from, stmt.to);
    return null;
  }

  // Standard selection: "din" (DIN 931 nominal) or "iso" (ISO 4014)
  const standard = getArgStr(stmt.args, 'standard', -1, ctx, 'din').toLowerCase();
  const useIso = standard === 'iso';
  const defaultHeadH = metric ? (useIso ? metric.head_h_iso : metric.head_h_din) : undefined;

  // Parameters with metric defaults, then proportional fallbacks
  const d = getArgNum(stmt.args, 'd', -1, ctx, metric?.d ?? 10);
  const length = getArgNum(stmt.args, 'length', 1, ctx, d * 2);
  const head_h = getArgNum(stmt.args, 'head_h', -1, ctx, defaultHeadH ?? d * 0.7);
  const head_d = getArgNum(stmt.args, 'head_d', -1, ctx, metric?.head_d ?? d * 1.7);
  const pitch = getArgNum(stmt.args, 'pitch', -1, ctx, metric?.pitch ?? d * 0.15);
  const threadLength = getArgNum(stmt.args, 'thread_length', -1, ctx, 0);
  const chamfer = getArgNum(stmt.args, 'chamfer', -1, ctx, head_h * 0.1);
  const center = getArgBool(stmt.args, 'center', -1, ctx, false);

  // Across-flats for hex head: head_d is across-flats (wrench size)
  const s = head_d;

  // 1. Hex head — hex prism intersected with chamfer envelope
  const hexHead = makeHexPrism(ctx, s, head_h);
  const headR = s / (2 * Math.cos(Math.PI / 6)); // across-corners radius
  const headEnv = makeChamferEnvelope(ctx, headR, head_h, chamfer, fn);
  const chamferedHead = hexHead.intersect(headEnv);
  trackIntermediate(ctx, chamferedHead);

  // 2. Shank (smooth cylinder portion)
  const smoothLen = Math.max(length - threadLength, 0);
  let shank: Manifold | null = null;
  if (smoothLen > 0) {
    shank = Manifold.cylinder(smoothLen, d / 2, d / 2, Math.max(fn, 16), false);
    trackIntermediate(ctx, shank);
    const shankMoved = shank.translate([0, 0, head_h]);
    trackIntermediate(ctx, shankMoved);
    shank = shankMoved;
  }

  // 3. Thread portion
  let threadPart: Manifold | null = null;
  if (threadLength > 0) {
    threadPart = makeThreadManifold(ctx, d, pitch, threadLength, 1, 60, fn, false);
    const threadZ = head_h + smoothLen;
    const threadMoved = threadPart.translate([0, 0, threadZ]);
    trackIntermediate(ctx, threadMoved);
    threadPart = threadMoved;
  }

  // 4. Union all parts
  let result: Manifold = chamferedHead;
  if (shank) {
    const u1 = result.add(shank);
    trackIntermediate(ctx, u1);
    result = u1;
  }
  if (threadPart) {
    const u2 = result.add(threadPart);
    trackIntermediate(ctx, u2);
    result = u2;
  }

  // Center: shift so bolt center is at origin (along the full length including head)
  if (center) {
    const totalH = head_h + length;
    const shifted = result.translate([0, 0, -totalH / 2]);
    trackIntermediate(ctx, shifted);
    result = shifted;
  }

  return result;
}

function handleNut(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const { Manifold } = ctx.wasm;
  const fn = getArgNum(stmt.args, '$fn', -1, ctx, ctx.fn);

  // Size lookup
  const sizeStr = getArgStr(stmt.args, 'size', 0, ctx, '');
  const metric = sizeStr ? lookupMetricSize(sizeStr) : undefined;
  if (sizeStr && !metric) {
    evalError(ctx, `Unknown nut size "${sizeStr}". Use M1..M30 (e.g. "M10", "M2.5").`, stmt.from, stmt.to);
    return null;
  }

  // Standard selection: "din" or "iso" (nut dims are the same, accepted for consistency)
  const _standard = getArgStr(stmt.args, 'standard', -1, ctx, 'din');

  // Parameters with metric defaults
  const d = getArgNum(stmt.args, 'd', -1, ctx, metric?.d ?? 10);
  const h = getArgNum(stmt.args, 'h', 1, ctx, metric?.nut_h ?? d * 0.8);
  const s = getArgNum(stmt.args, 's', -1, ctx, metric?.s ?? d * 1.7);
  const thread = getArgBool(stmt.args, 'thread', -1, ctx, false);
  const pitch = getArgNum(stmt.args, 'pitch', -1, ctx, metric?.pitch ?? d * 0.15);
  const chamfer = getArgNum(stmt.args, 'chamfer', -1, ctx, h * 0.1);
  const center = getArgBool(stmt.args, 'center', -1, ctx, false);

  // 1. Hex body — hex prism intersected with chamfer envelope
  const hexBody = makeHexPrism(ctx, s, h);
  const bodyR = s / (2 * Math.cos(Math.PI / 6)); // across-corners radius
  const bodyEnv = makeChamferEnvelope(ctx, bodyR, h, chamfer, fn);
  const chamferedBody = hexBody.intersect(bodyEnv);
  trackIntermediate(ctx, chamferedBody);

  // 2. Hole — threaded or smooth cylinder
  let hole: Manifold;
  if (thread) {
    hole = makeThreadManifold(ctx, d, pitch, h + 0.2, 1, 60, fn, false);
    const holeMoved = hole.translate([0, 0, -0.1]);
    trackIntermediate(ctx, holeMoved);
    hole = holeMoved;
  } else {
    hole = Manifold.cylinder(h + 0.2, d / 2, d / 2, Math.max(fn, 16), false);
    trackIntermediate(ctx, hole);
    const holeMoved = hole.translate([0, 0, -0.1]);
    trackIntermediate(ctx, holeMoved);
    hole = holeMoved;
  }

  // 3. Subtract hole from body
  let result = chamferedBody.subtract(hole);
  trackIntermediate(ctx, result);

  // Center
  if (center) {
    const shifted = result.translate([0, 0, -h / 2]);
    trackIntermediate(ctx, shifted);
    result = shifted;
  }

  return result;
}

// ====================================================================
// Standoff — mounting pillar with optional hole
// ====================================================================

function handleStandoff(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const { Manifold } = ctx.wasm;
  const fn = getArgNum(stmt.args, '$fn', -1, ctx, ctx.fn);
  const segs = Math.max(fn, 16);

  // Screw size lookup
  const screwSize = getArgStr(stmt.args, 'screw_size', -1, ctx, '');
  const metric = screwSize ? lookupMetricSize(screwSize) : undefined;
  if (screwSize && !metric) {
    evalError(ctx, `Unknown screw size "${screwSize}". Use M1..M30 (e.g. "M3", "M2.5").`, stmt.from, stmt.to);
    return null;
  }

  const h = getArgNum(stmt.args, 'h', 0, ctx, 10);
  const hole = getArgStr(stmt.args, 'hole', -1, ctx, 'pilot').toLowerCase();

  // Hole diameter: auto from screw_size, or explicit
  let holeD = getArgNum(stmt.args, 'hole_d', -1, ctx, 0);
  if (holeD <= 0) {
    if (metric) {
      holeD = hole === 'through' ? metric.d : metric.d * 0.8;
    } else {
      holeD = 2.5; // sensible default
    }
  }

  // Outer radius: explicit d or r, else auto from screw_size
  const dArg = getArgNum(stmt.args, 'd', -1, ctx, 0);
  let r: number;
  if (dArg > 0) {
    r = dArg / 2;
  } else {
    const rArg = getArgNum(stmt.args, 'r', 1, ctx, 0);
    if (rArg > 0) {
      r = rArg;
    } else {
      // Auto: enough wall around the hole
      r = Math.max(holeD * 0.9, 3);
      if (r < 4) r = 4;
    }
  }

  // Ensure outer radius > hole radius
  if (hole !== 'none' && r <= holeD / 2) {
    r = holeD / 2 + 1;
  }

  const holeDepth = getArgNum(stmt.args, 'hole_depth', -1, ctx, 0);
  const center = getArgBool(stmt.args, 'center', -1, ctx, false);

  // 1. Outer cylinder
  let result = Manifold.cylinder(h, r, r, segs, false);
  trackIntermediate(ctx, result);

  // 2. Subtract hole from top
  if (hole !== 'none') {
    const depth = holeDepth > 0 ? Math.min(holeDepth, h) : h;
    const holeCyl = Manifold.cylinder(depth + 0.1, holeD / 2, holeD / 2, segs, false);
    trackIntermediate(ctx, holeCyl);
    const holeMoved = holeCyl.translate([0, 0, h - depth]);
    trackIntermediate(ctx, holeMoved);
    const cut = result.subtract(holeMoved);
    trackIntermediate(ctx, cut);
    result = cut;
  }

  // Center on Z
  if (center) {
    const shifted = result.translate([0, 0, -h / 2]);
    trackIntermediate(ctx, shifted);
    result = shifted;
  }

  return result;
}

// ====================================================================
// Enclosure — parametric box with lid, lip, and optional screw/bolt mounts
// ====================================================================

/** Edge profile for top/bottom of a rounded box: fillet (rounded), chamfer (flat), or sharp */
interface BoxEdgeProfile {
  fillet: number;   // rounded edge radius (0 = none)
  chamfer: number;  // flat 45° cut size (0 = none); ignored if fillet > 0
}

/** Hull-based rounded rectangular solid.
 *  `r` rounds vertical corners; bot/top profiles control horizontal edge treatment.
 *  Uses cylinders for straight sections + revolved profiles for fillet/chamfer caps. */
function makeRoundedBoxSolid(
  ctx: EvalContext, w: number, d: number, h: number,
  r: number, fn: number,
  bot: BoxEdgeProfile = { fillet: 0, chamfer: 0 },
  top: BoxEdgeProfile = { fillet: 0, chamfer: 0 },
): Manifold {
  const { Manifold } = ctx.wasm;
  const segments = Math.max(fn, 16);
  const maxEdge = Math.min(r, h / 2);

  // Effective bottom/top edge sizes (fillet takes priority over chamfer)
  const fb = Math.min(Math.max(bot.fillet, 0), maxEdge);
  const cb = fb > 0 ? 0 : Math.min(Math.max(bot.chamfer, 0), maxEdge);
  const ft = Math.min(Math.max(top.fillet, 0), maxEdge);
  const ct = ft > 0 ? 0 : Math.min(Math.max(top.chamfer, 0), maxEdge);
  const botSize = fb || cb;  // how much the bottom cap consumes in Z
  const topSize = ft || ct;

  if (botSize <= 0 && topSize <= 0) {
    // No edge treatment: hull of 4 cylinders (sharp horizontal edges)
    const cyls: Manifold[] = [];
    for (const [cx, cy] of [[r, r], [w - r, r], [w - r, d - r], [r, d - r]] as Vec2[]) {
      const c = Manifold.cylinder(h, r, r, segments, false);
      trackIntermediate(ctx, c);
      const moved = c.translate([cx, cy, 0]);
      trackIntermediate(ctx, moved);
      cyls.push(moved);
    }
    const solid = Manifold.hull(cyls);
    trackIntermediate(ctx, solid);
    return solid;
  }

  // Build per-corner: cylinder for the straight middle + revolved caps for edges.
  const { CrossSection } = ctx.wasm;
  const filletSteps = Math.max(Math.min(segments, 16) / 4, 3) | 0;
  const parts: Manifold[] = [];

  for (const [cx, cy] of [[r, r], [w - r, r], [w - r, d - r], [r, d - r]] as Vec2[]) {
    // Straight cylinder section between edge zones
    const cylH = h - botSize - topSize;
    if (cylH > 0) {
      const c = Manifold.cylinder(cylH, r, r, segments, false);
      trackIntermediate(ctx, c);
      const moved = c.translate([cx, cy, botSize]);
      trackIntermediate(ctx, moved);
      parts.push(moved);
    }

    // Bottom cap
    if (fb > 0) {
      // Fillet: quarter-circle arc profile
      const profile: Vec2[] = [[0, 0]];
      for (let i = 0; i <= filletSteps; i++) {
        const a = (Math.PI / 2) * (i / filletSteps);
        profile.push([r - fb + fb * Math.sin(a), fb - fb * Math.cos(a)]);
      }
      profile.push([0, fb]);
      const cs = CrossSection.ofPolygons([profile]);
      let cap: Manifold;
      try { cap = cs.revolve(segments, 360); } finally { cs.delete(); }
      trackIntermediate(ctx, cap);
      const moved = cap.translate([cx, cy, 0]);
      trackIntermediate(ctx, moved);
      parts.push(moved);
    } else if (cb > 0) {
      // Chamfer: straight diagonal profile
      const profile: Vec2[] = [[0, 0], [r - cb, 0], [r, cb], [0, cb]];
      const cs = CrossSection.ofPolygons([profile]);
      let cap: Manifold;
      try { cap = cs.revolve(segments, 360); } finally { cs.delete(); }
      trackIntermediate(ctx, cap);
      const moved = cap.translate([cx, cy, 0]);
      trackIntermediate(ctx, moved);
      parts.push(moved);
    }

    // Top cap
    if (ft > 0) {
      // Fillet: quarter-circle arc profile — CCW winding
      // Arc from (r, 0) to (r-ft, ft) at the top of the box
      const profile: Vec2[] = [[0, 0]];
      for (let i = 0; i <= filletSteps; i++) {
        const a = (Math.PI / 2) * (i / filletSteps);
        profile.push([(r - ft) + ft * Math.cos(a), ft * Math.sin(a)]);
      }
      profile.push([0, ft]);
      const cs = CrossSection.ofPolygons([profile]);
      let cap: Manifold;
      try { cap = cs.revolve(segments, 360); } finally { cs.delete(); }
      trackIntermediate(ctx, cap);
      const moved = cap.translate([cx, cy, h - ft]);
      trackIntermediate(ctx, moved);
      parts.push(moved);
    } else if (ct > 0) {
      // Chamfer: straight diagonal profile (inverted)
      const profile: Vec2[] = [[0, 0], [r, 0], [r - ct, ct], [0, ct]];
      const cs = CrossSection.ofPolygons([profile]);
      let cap: Manifold;
      try { cap = cs.revolve(segments, 360); } finally { cs.delete(); }
      trackIntermediate(ctx, cap);
      const moved = cap.translate([cx, cy, h - ct]);
      trackIntermediate(ctx, moved);
      parts.push(moved);
    }
  }

  const solid = Manifold.hull(parts);
  trackIntermediate(ctx, solid);
  return solid;
}

/** Rounded rectangular shell: outer hull minus inner cavity */
function makeRoundedBoxShell(
  ctx: EvalContext, w: number, d: number, h: number,
  r: number, wall: number, fn: number, openTop: boolean,
  bot: BoxEdgeProfile = { fillet: 0, chamfer: 0 },
  top: BoxEdgeProfile = { fillet: 0, chamfer: 0 },
): Manifold {
  const outer = makeRoundedBoxSolid(ctx, w, d, h, r, fn, bot, top);
  const ri = Math.max(r - wall, 0.01);
  // Inner edge profile: reduce by wall thickness
  const innerBot: BoxEdgeProfile = {
    fillet: Math.max(bot.fillet - wall, 0),
    chamfer: Math.max(bot.chamfer - wall, 0),
  };
  const innerH = openTop ? h + 0.01 : h - wall;
  if (innerH <= 0) return outer;
  const noEdge: BoxEdgeProfile = { fillet: 0, chamfer: 0 };
  const inner = makeRoundedBoxSolid(ctx, w - 2 * wall, d - 2 * wall, innerH, ri, fn, innerBot, noEdge);
  const innerMoved = inner.translate([wall, wall, wall]);
  trackIntermediate(ctx, innerMoved);
  const shell = outer.subtract(innerMoved);
  trackIntermediate(ctx, shell);
  return shell;
}

function handleEnclosure(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const { Manifold } = ctx.wasm;
  const fn = getArgNum(stmt.args, '$fn', -1, ctx, ctx.fn);

  // Size: [width, depth, total_height]
  const sizeArg = getArg(stmt.args, 'size', 0, ctx);
  let w: number, d: number, hTotal: number;
  if (Array.isArray(sizeArg)) {
    w = (sizeArg[0] as number) || 60;
    d = (sizeArg[1] as number) || 40;
    hTotal = (sizeArg[2] as number) || 25;
  } else if (typeof sizeArg === 'number') {
    w = d = hTotal = sizeArg;
  } else {
    w = 60; d = 40; hTotal = 25;
  }

  const wall = getArgNum(stmt.args, 'wall', -1, ctx, 2);
  let r = getArgNum(stmt.args, 'r', -1, ctx, 3);
  r = Math.min(r, Math.min(w, d) / 2);
  const fillet_top = getArgNum(stmt.args, 'fillet_top', -1, ctx, 0);
  const fillet_bot = getArgNum(stmt.args, 'fillet_bottom', -1, ctx, 0);
  const chamfer_top = getArgNum(stmt.args, 'chamfer_top', -1, ctx, 0);
  const chamfer_bot = getArgNum(stmt.args, 'chamfer_bottom', -1, ctx, 0);
  // Edge profiles for body bottom, lid top, lid bottom
  const bodyBotEdge: BoxEdgeProfile = { fillet: fillet_bot, chamfer: chamfer_bot };
  const lidTopEdge: BoxEdgeProfile = { fillet: fillet_top, chamfer: chamfer_top };
  const lidBotEdge: BoxEdgeProfile = { fillet: fillet_bot, chamfer: chamfer_bot };
  const noEdge: BoxEdgeProfile = { fillet: 0, chamfer: 0 };
  const lidHArg = getArgNum(stmt.args, 'lid_h', -1, ctx, 0);
  const lidH = lidHArg > 0 ? lidHArg : wall;
  const lipH = getArgNum(stmt.args, 'lip_h', -1, ctx, 2);
  const lipGap = getArgNum(stmt.args, 'lip_gap', -1, ctx, 0.2);
  const mount = getArgStr(stmt.args, 'mount', -1, ctx, 'none').toLowerCase();
  const screwSize = getArgStr(stmt.args, 'screw_size', -1, ctx, 'M3');
  const screwDArg = getArgNum(stmt.args, 'screw_d', -1, ctx, 0);
  const screwInsetArg = getArgNum(stmt.args, 'screw_inset', -1, ctx, 0);
  const bossRArg = getArgNum(stmt.args, 'boss_r', -1, ctx, 0);
  const holeDArg = getArgNum(stmt.args, 'hole_depth', -1, ctx, 0);
  const nutPocket = getArgBool(stmt.args, 'nut_pocket', -1, ctx, false);
  const part = getArgStr(stmt.args, 'part', -1, ctx, 'assembly').toLowerCase();
  const center = getArgBool(stmt.args, 'center', -1, ctx, false);

  // Custom mount points: array of [x, y] positions
  const mountPointsRaw = getArg(stmt.args, 'mount_points', -1, ctx);
  const mountPoints: Vec2[] = [];
  if (Array.isArray(mountPointsRaw)) {
    for (const pt of mountPointsRaw) {
      if (Array.isArray(pt) && pt.length >= 2 && typeof pt[0] === 'number' && typeof pt[1] === 'number') {
        mountPoints.push([pt[0] as number, pt[1] as number]);
      }
    }
  }

  // Resolve screw diameter from size or override
  let screwD = screwDArg;
  if (screwD <= 0) {
    const metric = lookupMetricSize(screwSize);
    screwD = metric ? metric.d : 3;
  }

  const bodyH = hTotal - lidH;
  if (bodyH <= wall) {
    evalError(ctx, `Enclosure body height (${bodyH.toFixed(1)}) must exceed wall thickness (${wall})`, stmt.from, stmt.to);
    return null;
  }

  // Mount boss positions
  // boss_r is a radius; auto-calc: 90% of screw diameter as radius (boss dia ≈ 1.8× screw dia)
  const bossR = bossRArg > 0 ? bossRArg : Math.max(screwD * 0.9, wall * 1.5);
  const screwInset = screwInsetArg > 0 ? screwInsetArg : (r + bossR / 2);
  // Each boss position + the inner-wall X/Y it connects to via ribs
  const bossCorners: { pos: Vec2; wallX: number; wallY: number }[] = [
    { pos: [screwInset, screwInset],         wallX: wall,     wallY: wall },
    { pos: [w - screwInset, screwInset],     wallX: w - wall, wallY: wall },
    { pos: [w - screwInset, d - screwInset], wallX: w - wall, wallY: d - wall },
    { pos: [screwInset, d - screwInset],     wallX: wall,     wallY: d - wall },
  ];
  const bossPositions: Vec2[] = bossCorners.map(c => c.pos);

  function buildBody(): Manifold {
    const segs = Math.max(fn, 16);
    // 1. Shell: open-top rounded box (body bottom edge profile, top is open)
    let shell = makeRoundedBoxShell(ctx, w, d, bodyH, r, wall, fn, true, bodyBotEdge, noEdge);

    // 2. Lip ledge: widen the cavity at the top to create a shelf for the lid lip.
    if (lipH > 0) {
      const lipW = wall / 2;
      const lipCut = makeRoundedBoxSolid(
        ctx, w - 2 * lipW, d - 2 * lipW, lipH, Math.max(r - lipW, 0.01), fn
      );
      const lipCutMoved = lipCut.translate([lipW, lipW, bodyH - lipH]);
      trackIntermediate(ctx, lipCutMoved);
      const afterCut = shell.subtract(lipCutMoved);
      trackIntermediate(ctx, afterCut);
      shell = afterCut;
    }

    // 3. Mount bosses with wall ribs — each boss is a cylinder connected
    //    to the two nearest inner walls via thin rectangular ribs
    //    Boss stops at lip shelf (bodyH - lipH) so the lid lip can drop in
    if (mount !== 'none') {
      const bossTop = bodyH - lipH;
      const bossH = bossTop - wall;
      const ribW = Math.min(wall, bossR);  // rib thickness
      const bossParts: Manifold[] = [];

      for (const { pos: [cx, cy], wallX, wallY } of bossCorners) {
        // Boss cylinder
        const boss = Manifold.cylinder(bossH, bossR, bossR, segs, false);
        trackIntermediate(ctx, boss);
        const bossMoved = boss.translate([cx, cy, wall]);
        trackIntermediate(ctx, bossMoved);
        bossParts.push(bossMoved);

        // X-rib: connects boss to nearest X-wall (thin in Y)
        const xMin = Math.min(cx, wallX);
        const xMax = Math.max(cx, wallX);
        const xLen = xMax - xMin;
        if (xLen > 0.1) {
          const xRib = Manifold.cube([xLen, ribW, bossH], false);
          trackIntermediate(ctx, xRib);
          const xRibMoved = xRib.translate([xMin, cy - ribW / 2, wall]);
          trackIntermediate(ctx, xRibMoved);
          bossParts.push(xRibMoved);
        }

        // Y-rib: connects boss to nearest Y-wall (thin in X)
        const yMin = Math.min(cy, wallY);
        const yMax = Math.max(cy, wallY);
        const yLen = yMax - yMin;
        if (yLen > 0.1) {
          const yRib = Manifold.cube([ribW, yLen, bossH], false);
          trackIntermediate(ctx, yRib);
          const yRibMoved = yRib.translate([cx - ribW / 2, yMin, wall]);
          trackIntermediate(ctx, yRibMoved);
          bossParts.push(yRibMoved);
        }
      }

      // Batch union all boss parts, then add to shell once
      let allBosses = bossParts[0];
      for (let i = 1; i < bossParts.length; i++) {
        const merged = allBosses.add(bossParts[i]);
        trackIntermediate(ctx, merged);
        allBosses = merged;
      }
      const withBosses = shell.add(allBosses);
      trackIntermediate(ctx, withBosses);
      shell = withBosses;
    }

    // 3b. Custom mount_points bosses (no ribs, independent of mount param)
    if (mountPoints.length > 0) {
      const bossTop = bodyH - lipH;
      const bossH = bossTop - wall;
      if (bossH > 0) {
        const mpBossParts: Manifold[] = [];
        for (const [cx, cy] of mountPoints) {
          const boss = Manifold.cylinder(bossH, bossR, bossR, segs, false);
          trackIntermediate(ctx, boss);
          const bossMoved = boss.translate([cx, cy, wall]);
          trackIntermediate(ctx, bossMoved);
          mpBossParts.push(bossMoved);
        }
        let allMpBosses = mpBossParts[0];
        for (let i = 1; i < mpBossParts.length; i++) {
          const merged = allMpBosses.add(mpBossParts[i]);
          trackIntermediate(ctx, merged);
          allMpBosses = merged;
        }
        const withMpBosses = shell.add(allMpBosses);
        trackIntermediate(ctx, withMpBosses);
        shell = withMpBosses;
      }
    }

    // 4. Mount holes — batch union all cuts, then subtract once
    //    Boss top is at bodyH - lipH (below lip shelf)
    if (mount !== 'none' || mountPoints.length > 0) {
      const bossTop = bodyH - lipH;
      const bossH = bossTop - wall;
      const cutList: Manifold[] = [];

      // Corner boss holes (only when mount != 'none')
      if (mount !== 'none') {
        for (const [cx, cy] of bossPositions) {
          // Shaft hole — different radius per mode
          // screw: small pilot hole for self-tapping screw
          // bolt: clearance hole for bolt shaft (nut goes on outside bottom)
          const holeR = mount === 'screw' ? screwD * 0.4 : (screwD + 0.5) / 2;
          // hole_depth: 0 = default (through floor), >0 = blind depth from boss top
          // values >= bossH+wall go through the floor (add overshoot)
          const maxDepth = bossH + wall;
          const holeDepth = holeDArg > 0
            ? (holeDArg >= maxDepth ? maxDepth + 0.2 : holeDArg)
            : maxDepth + 0.2;
          const hole = Manifold.cylinder(holeDepth + 0.1, holeR, holeR, segs, false);
          trackIntermediate(ctx, hole);
          const holeMoved = hole.translate([cx, cy, bossTop - holeDepth]);
          trackIntermediate(ctx, holeMoved);
          cutList.push(holeMoved);

          // Hex nut pocket on outside bottom of floor
          if (nutPocket) {
            const boltMetric = lookupMetricSize(screwSize);
            const nutS = boltMetric ? boltMetric.s : screwD * 1.75;
            const nutH = boltMetric ? boltMetric.nut_h : screwD * 0.8;
            const nutPocketH = nutH + 0.3;
            const pocket = makeHexPrism(ctx, nutS + 0.4, nutPocketH + 0.2);
            const pocketMoved = pocket.translate([cx, cy, -0.1]);
            trackIntermediate(ctx, pocketMoved);
            cutList.push(pocketMoved);
          }
        }
      }

      // Custom mount_points holes (pilot holes, through-floor)
      if (mountPoints.length > 0) {
        const pilotR = screwD * 0.4;
        const maxDepth = bossH + wall;
        const holeDepth = holeDArg > 0
          ? (holeDArg >= maxDepth ? maxDepth + 0.2 : holeDArg)
          : maxDepth + 0.2;
        for (const [cx, cy] of mountPoints) {
          const hole = Manifold.cylinder(holeDepth + 0.1, pilotR, pilotR, segs, false);
          trackIntermediate(ctx, hole);
          const holeMoved = hole.translate([cx, cy, bossTop - holeDepth]);
          trackIntermediate(ctx, holeMoved);
          cutList.push(holeMoved);
        }
      }

      if (cutList.length > 0) {
        let allCuts = cutList[0];
        for (let i = 1; i < cutList.length; i++) {
          const merged = allCuts.add(cutList[i]);
          trackIntermediate(ctx, merged);
          allCuts = merged;
        }
        const withHoles = shell.subtract(allCuts);
        trackIntermediate(ctx, withHoles);
        shell = withHoles;
      }
    }

    return shell;
  }

  function buildLid(): Manifold {
    const segs = Math.max(fn, 16);
    // 1. Outer plate — bottom edge + top edge profiles
    let lid = makeRoundedBoxSolid(ctx, w, d, lidH, r, fn, lidBotEdge, lidTopEdge);

    // 2. Inner lip that drops into body
    if (lipH > 0) {
      const lipInset = wall / 2 + lipGap;
      const lipRi = Math.max(r - lipInset, 0.01);
      const lipSolid = makeRoundedBoxSolid(ctx, w - 2 * lipInset, d - 2 * lipInset, lipH, lipRi, fn);
      const lipMoved = lipSolid.translate([lipInset, lipInset, -lipH]);
      trackIntermediate(ctx, lipMoved);
      const u = lid.add(lipMoved);
      trackIntermediate(ctx, u);
      lid = u;
    }

    // 3. Mount holes — batch all cuts, then subtract once
    if (mount !== 'none' || mountPoints.length > 0) {
      const clearR = (screwD + 0.5) / 2;
      const cutList: Manifold[] = [];

      // Corner mount holes (only when mount != 'none')
      if (mount !== 'none') {
        // Resolve head dims once (outside loop)
        const metric = lookupMetricSize(screwSize);
        const headD = metric ? metric.head_d : screwD * 1.7;
        const headH = metric ? metric.head_h_din : screwD * 0.7;

        for (const [cx, cy] of bossPositions) {
          // Through-hole for shaft
          const hole = Manifold.cylinder(lidH + lipH + 1, clearR, clearR, segs, false);
          trackIntermediate(ctx, hole);
          const holeMoved = hole.translate([cx, cy, -lipH - 0.5]);
          trackIntermediate(ctx, holeMoved);
          cutList.push(holeMoved);

          if (mount === 'bolt') {
            // Hex counterbore for bolt head — extends 0.2 above lid top
            const counterbore = makeHexPrism(ctx, headD + 0.4, headH + 0.3 + 0.2);
            const cboreMoved = counterbore.translate([cx, cy, lidH - headH - 0.3]);
            trackIntermediate(ctx, cboreMoved);
            cutList.push(cboreMoved);
          } else {
            // Cylindrical counterbore for screw head
            const cboreR = (headD + 0.4) / 2;
            const cboreH = headH + 0.3;
            const cbore = Manifold.cylinder(cboreH + 0.2, cboreR, cboreR, segs, false);
            trackIntermediate(ctx, cbore);
            const cboreMoved = cbore.translate([cx, cy, lidH - cboreH]);
            trackIntermediate(ctx, cboreMoved);
            cutList.push(cboreMoved);
          }
        }
      }

      // Custom mount_points through-holes in lid (for screw access)
      if (mountPoints.length > 0) {
        for (const [cx, cy] of mountPoints) {
          const hole = Manifold.cylinder(lidH + lipH + 1, clearR, clearR, segs, false);
          trackIntermediate(ctx, hole);
          const holeMoved = hole.translate([cx, cy, -lipH - 0.5]);
          trackIntermediate(ctx, holeMoved);
          cutList.push(holeMoved);
        }
      }

      if (cutList.length > 0) {
        let allCuts = cutList[0];
        for (let i = 1; i < cutList.length; i++) {
          const merged = allCuts.add(cutList[i]);
          trackIntermediate(ctx, merged);
          allCuts = merged;
        }
        const withCuts = lid.subtract(allCuts);
        trackIntermediate(ctx, withCuts);
        lid = withCuts;
      }
    }

    return lid;
  }

  let result: Manifold | null = null;

  if (part === 'body') {
    result = buildBody();
  } else if (part === 'lid') {
    result = buildLid();
  } else {
    // assembly: body + lid positioned on top
    const body = buildBody();
    const lid = buildLid();
    const lidMoved = lid.translate([0, 0, bodyH]);
    trackIntermediate(ctx, lidMoved);
    result = body.add(lidMoved);
    trackIntermediate(ctx, result);
  }

  if (center && result) {
    const shifted = result.translate([-w / 2, -d / 2, -hTotal / 2]);
    trackIntermediate(ctx, shifted);
    result = shifted;
  }

  return result;
}

function handleShell(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const { Manifold } = ctx.wasm;
  const thickness = getArgNum(stmt.args, 'thickness', 0, ctx, 2);
  const openArg = getArg(stmt.args, 'open', -1, ctx) as number[] | undefined;

  // Evaluate children to get outer shape
  const outer = evalChildren(stmt.children, ctx);
  if (!outer) return null;

  const bbox = outer.boundingBox();
  const sx = bbox.max[0] - bbox.min[0];
  const sy = bbox.max[1] - bbox.min[1];
  const sz = bbox.max[2] - bbox.min[2];

  if (sx <= 2 * thickness || sy <= 2 * thickness || sz <= 2 * thickness) {
    evalError(ctx, `Shell thickness (${thickness}) is too large for shape dimensions`, stmt.from, stmt.to);
    return null;
  }

  // Scale from bounding box center to create inner cavity
  const cx = (bbox.min[0] + bbox.max[0]) / 2;
  const cy = (bbox.min[1] + bbox.max[1]) / 2;
  const cz = (bbox.min[2] + bbox.max[2]) / 2;
  const scaleX = (sx - 2 * thickness) / sx;
  const scaleY = (sy - 2 * thickness) / sy;
  const scaleZ = (sz - 2 * thickness) / sz;

  // Translate to origin, scale, translate back
  const toOrigin = outer.translate([-cx, -cy, -cz]);
  trackIntermediate(ctx, toOrigin);
  const scaled = toOrigin.scale([scaleX, scaleY, scaleZ]);
  trackIntermediate(ctx, scaled);
  const inner = scaled.translate([cx, cy, cz]);
  trackIntermediate(ctx, inner);

  // Open face: extend the cavity straight out through that face by one wall
  // thickness (+ overshoot), so only the lid is removed and the walls keep
  // their full height (an open-top 15 mm box stays 15 mm tall).
  let cavity = inner;
  if (openArg && Array.isArray(openArg)) {
    const shift = [0, 1, 2].map(i => Math.sign(openArg[i] ?? 0) * (thickness + 0.05)) as Vec3;
    if (shift.some(v => v !== 0)) {
      const moved = inner.translate(shift);
      trackIntermediate(ctx, moved);
      cavity = inner.add(moved);
      trackIntermediate(ctx, cavity);
    }
  }

  const result = outer.subtract(cavity);
  trackIntermediate(ctx, result);
  return result;
}


// ISO metric bolt specs: {d: shank, cs: countersunk [head_d, head_h] (DIN 7991 / ISO 10642),
//                         cb: counterbore / socket head cap [head_d, head_h] (DIN 912 / ISO 4762)}
const BOLT_SPECS: Record<string, { d: number; cs: [number, number]; cb: [number, number] }> = {
  'M1':   { d: 1,    cs: [1.9, 0.6],  cb: [1.85, 1  ] },
  'M1.6': { d: 1.6,  cs: [3,   1  ],  cb: [3,    1.6] },
  'M2':   { d: 2,    cs: [4,   1.2],  cb: [3.8,  2  ] },
  'M2.5': { d: 2.5,  cs: [5,   1.5],  cb: [4.5,  2.5] },
  'M3':   { d: 3,    cs: [6,   1.7],  cb: [5.5,  3  ] },
  'M4':   { d: 4,    cs: [8,   2.3],  cb: [7,    4  ] },
  'M5':   { d: 5,    cs: [10,  2.8],  cb: [8.5,  5  ] },
  'M6':   { d: 6,    cs: [12,  3.3],  cb: [10,   6  ] },
  'M8':   { d: 8,    cs: [16,  4.4],  cb: [13,   8  ] },
  'M10':  { d: 10,   cs: [20,  5.5],  cb: [16,  10  ] },
  'M12':  { d: 12,   cs: [24,  6.4],  cb: [18,  12  ] },
  'M14':  { d: 14,   cs: [27,  7.5],  cb: [21,  14  ] },
  'M16':  { d: 16,   cs: [30,  8.8],  cb: [24,  16  ] },
  'M20':  { d: 20,   cs: [36, 10.2],  cb: [30,  20  ] },
};

interface BoltHoleDims {
  type: 'through' | 'countersunk' | 'counterbore';
  shankR: number;
  headR: number;
  headH: number;
}

/**
 * Resolve bolt-hole dimensions from user args + ISO metric spec table.
 * Returns shank radius, head radius, and head pocket depth (0 for through-holes).
 * Includes optional clearance added to shank diameter.
 */
function resolveBoltHoleDims(
  type: string,
  sizeStr: string,
  dOverride: number,
  headDOverride: number,
  headHOverride: number,
  clearance: number,
): BoltHoleDims {
  const spec = BOLT_SPECS[sizeStr.toUpperCase()];
  const t = (type || 'through').toLowerCase();
  const isCS = t === 'countersunk' || t === 'flat' || t === 'flush' || t === 'cs';
  const isCB = t === 'counterbore' || t === 'socket' || t === 'cap' || t === 'cbore' || t === 'cb';
  const kind: BoltHoleDims['type'] = isCS ? 'countersunk' : (isCB ? 'counterbore' : 'through');

  const shankD = dOverride > 0 ? dOverride : (spec ? spec.d : 5);
  const shankR = (shankD + clearance) / 2;

  if (kind === 'through') {
    return { type: kind, shankR, headR: 0, headH: 0 };
  }

  const [defaultHeadD, defaultHeadH] = kind === 'countersunk'
    ? (spec ? spec.cs : [shankD * 2, (shankD * 2 - shankD) / 2])
    : (spec ? spec.cb : [shankD * 1.7, shankD]);

  const headD = headDOverride > 0 ? headDOverride : defaultHeadD;
  const headH = headHOverride > 0 ? headHOverride : defaultHeadH;
  const headR = (headD + clearance) / 2;
  return { type: kind, shankR, headR, headH };
}

/**
 * Build a 3D drill shape for a bolt hole, Z-aligned and centered on Z.
 * Shank extends full height `h`. Head pocket (if any) is at the top,
 * extending `headH` downward from the top face.
 *   - countersunk: conical pocket (wider at top)
 *   - counterbore: cylindrical pocket (constant headR for headH depth)
 *   - through: just the shank
 */
function buildBoltHoleManifold(
  dims: BoltHoleDims,
  h: number,
  segs: number,
  ctx: EvalContext,
): Manifold {
  const { Manifold } = ctx.wasm;
  const shank = Manifold.cylinder(h, dims.shankR, dims.shankR, segs, true);
  trackIntermediate(ctx, shank);
  if (dims.type === 'through' || dims.headH <= 0) return shank;

  // Head pocket: from shankR (inner end) to headR (face end) for countersunk,
  // or constant headR for counterbore. Placed at top of shank.
  const topRadius = dims.headR;
  const bottomRadius = dims.type === 'countersunk' ? dims.shankR : dims.headR;
  const pocket = Manifold.cylinder(dims.headH, bottomRadius, topRadius, segs, false);
  trackIntermediate(ctx, pocket);
  const placed = pocket.translate([0, 0, h / 2 - dims.headH]);
  trackIntermediate(ctx, placed);
  const union: Manifold = shank.add(placed);
  trackIntermediate(ctx, union);
  return union;
}

/**
 * Standalone `bolt_hole(...)` module. Takes optional children to subtract from.
 * Without children, returns the drill manifold for use in a user's own difference().
 */
function handleBoltHole(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const typeArg = getArgStr(stmt.args, 'type', 0, ctx, 'countersunk');
  const sizeArg = getArgStr(stmt.args, 'size', 1, ctx, 'M5');
  const dOverride = getArgNum(stmt.args, 'd', -1, ctx, 0);
  const headDOverride = getArgNum(stmt.args, 'head_d', -1, ctx, 0);
  const headHOverride = getArgNum(stmt.args, 'head_h', -1, ctx, 0);
  const clearance = getArgNum(stmt.args, 'clearance', -1, ctx, 0.2);
  const lengthArg = getArgNum(stmt.args, 'length', -1, ctx, 0);
  const through = getArgBool(stmt.args, 'through', -1, ctx, true);
  const anchorArg = (getArg(stmt.args, 'anchor', -1, ctx) as number[] | undefined) ?? [0, 0, 1];
  const fn = getArgNum(stmt.args, '$fn', -1, ctx, ctx.fn);
  const segs = Math.max(fn, 16);

  const dims = resolveBoltHoleDims(typeArg, sizeArg, dOverride, headDOverride, headHOverride, clearance);

  // Resolve length — from children bbox if present, else explicit or fallback
  const hasChildren = stmt.children.length > 0;
  let parent: Manifold | null = null;
  let bbox: { min: Vec3; max: Vec3 } | null = null;
  if (hasChildren) {
    parent = evalChildren(stmt.children, ctx);
    if (!parent) return null;
    bbox = parent.boundingBox();
  }

  const anchor: Vec3 = [anchorArg[0] ?? 0, anchorArg[1] ?? 0, anchorArg[2] ?? 1];
  const [ax, ay, az] = anchor;
  const axLen = Math.abs(ax), ayLen = Math.abs(ay), azLen = Math.abs(az);
  const axisIdx = azLen >= axLen && azLen >= ayLen ? 2 : (ayLen >= axLen ? 1 : 0);
  const extent = bbox ? [bbox.max[0] - bbox.min[0], bbox.max[1] - bbox.min[1], bbox.max[2] - bbox.min[2]][axisIdx] : 10;
  const h = lengthArg > 0 ? lengthArg : (through ? extent + 0.2 : extent);

  // Build drill
  const drill = buildBoltHoleManifold(dims, h, segs, ctx);

  // Orient Z→anchor
  const anchorN = anchorNormal(anchor);
  const rotMat = rotationFromTo([0, 0, 1] as Vec3, anchorN);
  const oriented = drill.transform(mat3ToMat4Transform(rotMat));
  trackIntermediate(ctx, oriented);

  if (!hasChildren || !parent || !bbox) return oriented;

  // Position at parent's bbox center (drill spans full parent along anchor axis)
  const bboxCenter: Vec3 = [
    (bbox.min[0] + bbox.max[0]) / 2,
    (bbox.min[1] + bbox.max[1]) / 2,
    (bbox.min[2] + bbox.max[2]) / 2,
  ];
  const placed = oriented.translate(bboxCenter);
  trackIntermediate(ctx, placed);
  const result: Manifold = parent.subtract(placed);
  trackIntermediate(ctx, result);
  return result;
}

/**
 * Standalone `nut_hole(...)` module. Cuts a hex pocket sized to capture an ISO
 * metric nut, plus a through-hole for the bolt shank. Without children, returns
 * the drill manifold for use in a user's own difference().
 *
 * Args: size ("M3"..), d (shank dia override), s (hex flats override),
 * nut_h (pocket depth override), clearance, length, through, anchor, $fn.
 */
function handleNutHole(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const { Manifold } = ctx.wasm;
  const sizeArg = getArgStr(stmt.args, 'size', 0, ctx, 'M5');
  const dOverride = getArgNum(stmt.args, 'd', -1, ctx, 0);
  const sOverride = getArgNum(stmt.args, 's', -1, ctx, 0);
  const nutHOverride = getArgNum(stmt.args, 'nut_h', -1, ctx, 0);
  const clearance = getArgNum(stmt.args, 'clearance', -1, ctx, 0.2);
  const lengthArg = getArgNum(stmt.args, 'length', -1, ctx, 0);
  const through = getArgBool(stmt.args, 'through', -1, ctx, true);
  const anchorArg = (getArg(stmt.args, 'anchor', -1, ctx) as number[] | undefined) ?? [0, 0, 1];
  const fn = getArgNum(stmt.args, '$fn', -1, ctx, ctx.fn);
  const segs = Math.max(fn, 16);

  const metric = lookupMetricSize(sizeArg);
  if (sizeArg && !metric && dOverride <= 0) {
    evalError(ctx, `Unknown nut_hole size "${sizeArg}". Use M1..M30 (e.g. "M5").`, stmt.from, stmt.to);
    return null;
  }
  const shankD = dOverride > 0 ? dOverride : (metric?.d ?? 5);
  const flats = sOverride > 0 ? sOverride : (metric?.s ?? shankD * 1.7);
  const nutH = nutHOverride > 0 ? nutHOverride : (metric?.nut_h ?? shankD * 0.8);
  const shankR = (shankD + clearance) / 2;
  const pocketFlats = flats + clearance;

  const hasChildren = stmt.children.length > 0;
  let parent: Manifold | null = null;
  let bbox: { min: Vec3; max: Vec3 } | null = null;
  if (hasChildren) {
    parent = evalChildren(stmt.children, ctx);
    if (!parent) return null;
    bbox = parent.boundingBox();
  }

  const anchor: Vec3 = [anchorArg[0] ?? 0, anchorArg[1] ?? 0, anchorArg[2] ?? 1];
  const [ax, ay, az] = anchor;
  const axLen = Math.abs(ax), ayLen = Math.abs(ay), azLen = Math.abs(az);
  const axisIdx = azLen >= axLen && azLen >= ayLen ? 2 : (ayLen >= axLen ? 1 : 0);
  const extent = bbox ? [bbox.max[0] - bbox.min[0], bbox.max[1] - bbox.min[1], bbox.max[2] - bbox.min[2]][axisIdx] : 10;
  const h = lengthArg > 0 ? lengthArg : (through ? extent + 0.2 : extent);

  // Build drill: shank along full length, hex pocket at the top (anchor side)
  const shank = Manifold.cylinder(h, shankR, shankR, segs, true);
  trackIntermediate(ctx, shank);
  const pocketDepth = Math.min(nutH, h);
  const pocket = makeHexPrism(ctx, pocketFlats, pocketDepth);
  const pocketPlaced = pocket.translate([0, 0, h / 2 - pocketDepth]);
  trackIntermediate(ctx, pocketPlaced);
  const drill: Manifold = shank.add(pocketPlaced);
  trackIntermediate(ctx, drill);

  // Orient Z→anchor
  const anchorN = anchorNormal(anchor);
  const rotMat = rotationFromTo([0, 0, 1] as Vec3, anchorN);
  const oriented = drill.transform(mat3ToMat4Transform(rotMat));
  trackIntermediate(ctx, oriented);

  if (!hasChildren || !parent || !bbox) return oriented;

  const bboxCenter: Vec3 = [
    (bbox.min[0] + bbox.max[0]) / 2,
    (bbox.min[1] + bbox.max[1]) / 2,
    (bbox.min[2] + bbox.max[2]) / 2,
  ];
  const placed = oriented.translate(bboxCenter);
  trackIntermediate(ctx, placed);
  const result: Manifold = parent.subtract(placed);
  trackIntermediate(ctx, result);
  return result;
}


/**
 * Build a union of cutters laid out according to a 2D pattern on a face.
 * Oriented by `anchor`. Returns a Manifold in world space ready to subtract
 * from `parent` (or null if no cutters were produced).
 *
 * Reads pattern parameters from `stmt.args`. When `prefix="hole_"`, looks up
 * `hole_d`, `hole_count`, etc. — used by `cut_pattern` on primitives so it
 * doesn't collide with the primitive's own `d`/`r`/`h`/`center` args.
 *
 *   cut_pattern / pattern    - "grid" | "hex" | "ring" | "isogrid"
 *   cut_shape / shape        - "circle" (default) | "hex" | "triangle" | "square"
 *   [prefix]d | [prefix]r    - hole diameter / radius (d wins)
 *   [prefix]h                - hole cutter height; 0 = auto from bbox
 *   [prefix]count            - ring: number of holes
 *   [prefix]radius           - ring: ring radius
 *   [prefix]center           - [x, y] in-plane offset of the whole pattern
 *   spacing | [sx, sy]       - grid/hex/isogrid pitch (no prefix; no collision)
 *   rows, cols               - 0 = auto-fit from parent bbox
 *   margin                   - inset from bbox edge in fill mode
 *   start_angle              - ring: starting angle, degrees
 *   through                  - through-hole overshoot (default true)
 */
interface CutterCenter { x: number; y: number; rotDeg?: number }
interface CutterEdge { chamfer: number; fillet: number }

/** Build a plus/cross 2D profile centered at origin. `size` = arm length, `thickness` = arm width. */
function plusCrossSection(
  size: number,
  thickness: number,
  cornerR: number,
  ctx: EvalContext,
): InstanceType<ManifoldToplevel['CrossSection']> {
  const { CrossSection } = ctx.wasm;
  const hs = size / 2;
  const ht = thickness / 2;
  // Construct as union of two rectangles (simpler & always valid)
  const hBar = CrossSection.ofPolygons([[[-hs, -ht], [hs, -ht], [hs, ht], [-hs, ht]]]);
  const vBar = CrossSection.ofPolygons([[[-ht, -hs], [ht, -hs], [ht, hs], [-ht, hs]]]);
  const plus = hBar.add(vBar);
  hBar.delete();
  vBar.delete();
  if (cornerR > 0) {
    // Round/chamfer the 8 convex + 4 concave corners via double offset
    const inner = plus.offset(-cornerR, 'Round');
    const outer = inner.offset(cornerR, 'Round');
    inner.delete();
    plus.delete();
    return outer;
  }
  return plus;
}

/** Build an X 2D profile — two overlapping bars rotated ±45° from horizontal. */
function xCrossSection(
  size: number,
  thickness: number,
  cornerR: number,
  ctx: EvalContext,
): InstanceType<ManifoldToplevel['CrossSection']> {
  const { CrossSection } = ctx.wasm;
  const hs = size / 2;
  const ht = thickness / 2;
  // Bar at +45°: rotate (-hs..hs × -ht..ht) by 45° CCW around origin
  const rot = (deg: number, x: number, y: number): [number, number] => {
    const a = deg * Math.PI / 180;
    const c = Math.cos(a), s = Math.sin(a);
    return [c * x - s * y, s * x + c * y];
  };
  const bar = (angleDeg: number): Vec2[] => [
    rot(angleDeg, -hs, -ht),
    rot(angleDeg, hs, -ht),
    rot(angleDeg, hs, ht),
    rot(angleDeg, -hs, ht),
  ];
  const b1 = CrossSection.ofPolygons([bar(45)]);
  const b2 = CrossSection.ofPolygons([bar(-45)]);
  const x = b1.add(b2);
  b1.delete();
  b2.delete();
  if (cornerR > 0) {
    const inner = x.offset(-cornerR, 'Round');
    const outer = inner.offset(cornerR, 'Round');
    inner.delete();
    x.delete();
    return outer;
  }
  return x;
}

/** Build a bowtie/hourglass 2D profile (`><` — two triangles apex-to-apex at origin).
 *  `size` = overall length (left-apex to right-apex on the minor-axis ends); the
 *  shape actually has bases on the outer ends and apexes meeting at the center. */
function bowtieCrossSection(
  size: number,
  thickness: number,
  cornerR: number,
  ctx: EvalContext,
): InstanceType<ManifoldToplevel['CrossSection']> {
  const { CrossSection } = ctx.wasm;
  const hs = size / 2;
  const ht = thickness / 2;
  // Left triangle: base on left edge (x=-hs), apex at origin
  const left: Vec2[] = [[-hs, -ht], [0, 0], [-hs, ht]];
  // Right triangle: base on right edge (x=hs), apex at origin
  const right: Vec2[] = [[hs, -ht], [hs, ht], [0, 0]];
  const l = CrossSection.ofPolygons([left]);
  const r = CrossSection.ofPolygons([right]);
  const bowtie = l.add(r);
  l.delete();
  r.delete();
  if (cornerR > 0) {
    const inner = bowtie.offset(-cornerR, 'Round');
    const outer = inner.offset(cornerR, 'Round');
    inner.delete();
    bowtie.delete();
    return outer;
  }
  return bowtie;
}

/** Build a radial N-spoke 2D profile. `spokes` = number of triangles arranged
 *  around center; each has apex at origin (or offset by gap/2 outward along its
 *  radial direction) and a base of width `thickness` centered on a point at
 *  distance `size/2` from origin. */
function radialCrossSection(
  size: number,
  thickness: number,
  gap: number,
  spokes: number,
  cornerR: number,
  ctx: EvalContext,
): InstanceType<ManifoldToplevel['CrossSection']> {
  const { CrossSection } = ctx.wasm;
  const pieces = decomposeCutterShape('radial', size, thickness, gap, spokes);
  let acc: InstanceType<ManifoldToplevel['CrossSection']> | null = null;
  for (const poly of pieces) {
    const cs = CrossSection.ofPolygons([poly]);
    if (acc === null) {
      acc = cs;
    } else {
      const merged: InstanceType<ManifoldToplevel['CrossSection']> = acc.add(cs);
      acc.delete();
      cs.delete();
      acc = merged;
    }
  }
  if (!acc) acc = CrossSection.ofPolygons([]);
  if (cornerR > 0) {
    const inner = acc.offset(-cornerR, 'Round');
    const outer = inner.offset(cornerR, 'Round');
    inner.delete();
    acc.delete();
    return outer;
  }
  return acc;
}

/** Build a diamond 2D profile (`<>` — two triangles pointing OUTWARD with a gap between their bases).
 *  `size` = overall length (outer apex to outer apex), `thickness` = base width,
 *  `gap` = space between the inner bases (0 = solid rhombus, >0 = split). */
function diamondCrossSection(
  size: number,
  thickness: number,
  gap: number,
  cornerR: number,
  ctx: EvalContext,
): InstanceType<ManifoldToplevel['CrossSection']> {
  const { CrossSection } = ctx.wasm;
  const hs = size / 2;
  const ht = thickness / 2;
  const hg = gap / 2;
  // Left triangle: outer apex at (-hs, 0), inner base at x = -hg
  const left: Vec2[] = [[-hs, 0], [-hg, -ht], [-hg, ht]];
  // Right triangle: outer apex at (hs, 0), inner base at x = hg
  const right: Vec2[] = [[hs, 0], [hg, ht], [hg, -ht]];
  const l = CrossSection.ofPolygons([left]);
  const r = CrossSection.ofPolygons([right]);
  const diamond = l.add(r);
  l.delete();
  r.delete();
  if (cornerR > 0) {
    const inner = diamond.offset(-cornerR, 'Round');
    const outer = inner.offset(cornerR, 'Round');
    inner.delete();
    diamond.delete();
    return outer;
  }
  return diamond;
}

/**
 * Build a 3D chamfered/filleted prism from a CONVEX 2D polygon.
 * Uses Manifold.hull() between thin profile slabs to produce FLAT frustum faces
 * (no staircase). Only works for convex polygons — complex non-convex shapes
 * must be decomposed into convex pieces first.
 */
function buildConvexPieceFrustum(
  polygon: Vec2[],
  h: number,
  topEdge: CutterEdge,
  botEdge: CutterEdge,
  ctx: EvalContext,
): Manifold {
  const { Manifold, CrossSection } = ctx.wasm;
  const topDepth = topEdge.fillet > 0 ? topEdge.fillet : topEdge.chamfer;
  const botDepth = botEdge.fillet > 0 ? botEdge.fillet : botEdge.chamfer;
  const topW = topDepth;
  const botW = botDepth;
  const midH = h - topDepth - botDepth;

  const basePoly = CrossSection.ofPolygons([polygon]);
  const pieces: Manifold[] = [];
  const eps = 0.002;

  // Middle straight section, centered to handle asymmetric chamfers.
  if (midH > 0) {
    const mid = basePoly.extrude(midH, 0, 0, [1, 1], true);
    trackIntermediate(ctx, mid);
    const midShift = (botDepth - topDepth) / 2;
    const placed = midShift !== 0 ? mid.translate([0, 0, midShift]) : mid;
    if (midShift !== 0) trackIntermediate(ctx, placed);
    pieces.push(placed);
  } else if (topDepth <= 0 && botDepth <= 0) {
    // No treatment → plain extrude
    const m = basePoly.extrude(h, 0, 0, [1, 1], true);
    basePoly.delete();
    trackIntermediate(ctx, m);
    return m;
  }

  const buildEndFrustum = (depth: number, w: number, filletMode: boolean, innerZ: number, outerZ: number): void => {
    if (depth <= 0 || w <= 0) return;
    const joinType: 'Round' | 'Miter' = filletMode ? 'Round' : 'Miter';
    // Chamfer: one flat frustum (linear taper, truly flat slanted faces).
    // Fillet: N stacked hull frustums approximating a quarter-circle profile.
    //   Each slice has flat frustum sides (hull between two offset profiles at
    //   different radii + z), and the curve emerges from the radii progression.
    const slices = filletMode ? 10 : 1;
    for (let i = 0; i < slices; i++) {
      const t0 = i / slices;
      const t1 = (i + 1) / slices;
      const frac0 = filletMode ? (1 - Math.sqrt(1 - t0 * t0)) : t0;
      const frac1 = filletMode ? (1 - Math.sqrt(1 - t1 * t1)) : t1;
      const off0 = w * frac0;
      const off1 = w * frac1;
      const prf0 = off0 > 0 ? basePoly.offset(off0, joinType) : basePoly;
      const prf1 = off1 > 0 ? basePoly.offset(off1, joinType) : basePoly;
      const z0 = innerZ + (outerZ - innerZ) * t0;
      const z1 = innerZ + (outerZ - innerZ) * t1;
      const slab0 = prf0.extrude(eps, 0, 0, [1, 1], false);
      const slab1 = prf1.extrude(eps, 0, 0, [1, 1], false);
      trackIntermediate(ctx, slab0);
      trackIntermediate(ctx, slab1);
      const slab0Placed = slab0.translate([0, 0, z0 - eps / 2]);
      const slab1Placed = slab1.translate([0, 0, z1 - eps / 2]);
      trackIntermediate(ctx, slab0Placed);
      trackIntermediate(ctx, slab1Placed);
      const frustum = Manifold.hull([slab0Placed, slab1Placed]);
      trackIntermediate(ctx, frustum);
      if (off0 > 0 && prf0 !== basePoly) prf0.delete();
      if (off1 > 0 && prf1 !== basePoly) prf1.delete();
      pieces.push(frustum);
    }
  };

  // Top end: inner at h/2 - topDepth, outer face at h/2
  buildEndFrustum(topDepth, topW, topEdge.fillet > 0, h / 2 - topDepth, h / 2);
  // Bottom end: inner at -h/2 + botDepth, outer face at -h/2
  buildEndFrustum(botDepth, botW, botEdge.fillet > 0, -h / 2 + botDepth, -h / 2);

  basePoly.delete();

  // Union all pieces
  let acc: Manifold = pieces[0];
  for (let i = 1; i < pieces.length; i++) {
    const merged: Manifold = acc.add(pieces[i]);
    trackIntermediate(ctx, merged);
    acc = merged;
  }
  return acc;
}

/** Decompose a complex cutter shape into convex polygon pieces. */
function decomposeCutterShape(
  shape: string,
  size: number,
  thickness: number,
  gap: number,
  spokes = 4,
): Vec2[][] {
  const hs = size / 2;
  const ht = thickness / 2;
  const rotPt = (deg: number, x: number, y: number): [number, number] => {
    const a = deg * Math.PI / 180;
    const c = Math.cos(a), s = Math.sin(a);
    return [c * x - s * y, s * x + c * y];
  };
  if (shape === 'plus' || shape === 'cross') {
    return [
      [[-hs, -ht], [hs, -ht], [hs, ht], [-hs, ht]],       // horizontal bar
      [[-ht, -hs], [ht, -hs], [ht, hs], [-ht, hs]],       // vertical bar
    ];
  }
  if (shape === 'x') {
    // Two bars rotated ±45°
    const bar = (deg: number): Vec2[] => [
      rotPt(deg, -hs, -ht), rotPt(deg, hs, -ht), rotPt(deg, hs, ht), rotPt(deg, -hs, ht),
    ];
    return [bar(45), bar(-45)];
  }
  if (shape === 'bowtie') {
    return [
      [[-hs, -ht], [0, 0], [-hs, ht]],                    // left triangle (apex at origin)
      [[hs, -ht], [hs, ht], [0, 0]],                      // right triangle
    ];
  }
  if (shape === 'diamond' || shape === 'rhombus') {
    const hg = gap / 2;
    return [
      [[-hs, 0], [-hg, -ht], [-hg, ht]],                  // left triangle
      [[hs, 0], [hg, ht], [hg, -ht]],                     // right triangle
    ];
  }
  if (shape === 'radial' || shape === 'spokes' || shape === 'star') {
    // N triangles arranged radially around center. Each has its apex at origin
    // (or offset by gap/2 along its radial direction), and a base chord of
    // length `thickness` centered on a point at distance `size/2` from origin.
    const n = Math.max(1, Math.floor(spokes));
    const R = hs;
    const hg = gap / 2;
    const pieces: Vec2[][] = [];
    for (let i = 0; i < n; i++) {
      const theta = (i / n) * 2 * Math.PI;
      const c = Math.cos(theta), s = Math.sin(theta);
      // Apex (inner tip), offset outward by hg along radial direction
      const apex: Vec2 = [hg * c, hg * s];
      // Outer center at distance R along radial
      const ocx = R * c, ocy = R * s;
      // Perpendicular (90° CCW from radial)
      const px = -s, py = c;
      // Two outer vertices — chord of length `thickness` centered on outer point
      const outerR: Vec2 = [ocx - ht * px, ocy - ht * py];
      const outerL: Vec2 = [ocx + ht * px, ocy + ht * py];
      // CCW order: apex, outerR (cw side), outerL (ccw side)
      pieces.push([apex, outerR, outerL]);
    }
    return pieces;
  }
  return [];
}

/**
 * Extrude a 2D CrossSection into a 3D cutter with optional per-end chamfer or
 * fillet at top and/or bottom. Edge treatment is applied via stacked slices
 * with CrossSection.offset to widen the profile toward the face. Works for any
 * 2D shape — used as a fallback for shapes that can't be decomposed into
 * convex pieces (e.g. after `cornerR` rounding).
 */
function extrudeProfileWithEdges(
  profile: InstanceType<ManifoldToplevel['CrossSection']>,
  h: number,
  topEdge: CutterEdge,
  botEdge: CutterEdge,
  ctx: EvalContext,
): Manifold {
  const topDepth = topEdge.fillet > 0 ? topEdge.fillet : topEdge.chamfer;
  const botDepth = botEdge.fillet > 0 ? botEdge.fillet : botEdge.chamfer;
  const topFilletMode = topEdge.fillet > 0;
  const botFilletMode = botEdge.fillet > 0;
  const topW = topEdge.fillet > 0 ? topEdge.fillet : topEdge.chamfer;
  const botW = botEdge.fillet > 0 ? botEdge.fillet : botEdge.chamfer;

  // No edge treatment → plain centered extrude
  if (topDepth <= 0 && botDepth <= 0) {
    const m = profile.extrude(h, 0, 0, [1, 1], true);
    trackIntermediate(ctx, m);
    return m;
  }
  const midH = h - topDepth - botDepth;
  if (midH <= 0) {
    const m = profile.extrude(h, 0, 0, [1, 1], true);
    trackIntermediate(ctx, m);
    return m;
  }

  // Middle section (straight), centered so its z-center is at midway
  // between the top and bottom taper zones.
  const midCenterZ = (botDepth - topDepth) / 2;
  let acc: Manifold = profile.extrude(midH, 0, 0, [1, 1], true);
  trackIntermediate(ctx, acc);
  if (midCenterZ !== 0) {
    const shifted = acc.translate([0, 0, midCenterZ]);
    trackIntermediate(ctx, shifted);
    acc = shifted;
  }

  // Builds stacked tapered slices between a narrow inner end and a wide outer face.
  // `baseZ` = z of the inner (narrow) edge of the taper;
  // `endZ`  = z of the outer (wide) face;
  // Positive (baseZ < endZ) → taper grows with z (top end).
  // Negative (baseZ > endZ) → taper grows as z decreases (bottom end).
  const buildTaper = (depth: number, w: number, filletMode: boolean, baseZ: number, endZ: number): Manifold | null => {
    if (depth <= 0 || w <= 0) return null;
    // Chamfer uses few slices since the ideal shape IS flat — but many slices
    // makes the staircase approximation visually close to flat. Fillet is
    // inherently curved so 16 gives a reasonable quarter-circle.
    const steps = filletMode ? 16 : 32;
    const sliceH = depth / steps;
    const direction = endZ > baseZ ? 1 : -1; // +1 = top, -1 = bottom
    let taper: Manifold | null = null;
    for (let i = 0; i < steps; i++) {
      const tMid = (i + 0.5) / steps; // 0..1 from inner to outer
      const frac = filletMode ? (1 - Math.sqrt(1 - tMid * tMid)) : tMid;
      const off = w * frac;
      const slicePrf = off > 0 ? profile.offset(off, filletMode ? 'Round' : 'Square') : profile;
      const sliceExt = slicePrf.extrude(sliceH, 0, 0, [1, 1], false);
      trackIntermediate(ctx, sliceExt);
      // Slice sits from baseZ + i*sliceH*direction to baseZ + (i+1)*sliceH*direction
      // Since extrude extends upward from 0 to sliceH, translate by the lower z.
      const sliceLowerZ = direction > 0
        ? baseZ + i * sliceH
        : baseZ - (i + 1) * sliceH;
      const placed = sliceExt.translate([0, 0, sliceLowerZ]);
      trackIntermediate(ctx, placed);
      if (off > 0 && slicePrf !== profile) slicePrf.delete();
      if (taper === null) {
        taper = placed;
      } else {
        const merged: Manifold = taper.add(placed);
        trackIntermediate(ctx, merged);
        taper = merged;
      }
    }
    return taper;
  };

  // Top taper: inner edge at h/2 - topDepth, outer face at h/2
  if (topDepth > 0 && topW > 0) {
    const top = buildTaper(topDepth, topW, topFilletMode, h / 2 - topDepth, h / 2);
    if (top) {
      const u = acc.add(top);
      trackIntermediate(ctx, u);
      acc = u;
    }
  }
  // Bottom taper: inner edge at -h/2 + botDepth, outer face at -h/2
  if (botDepth > 0 && botW > 0) {
    const bot = buildTaper(botDepth, botW, botFilletMode, -h / 2 + botDepth, -h / 2);
    if (bot) {
      const u = acc.add(bot);
      trackIntermediate(ctx, u);
      acc = u;
    }
  }
  return acc;
}

function makeCutter(
  shape: string,
  h: number,
  d: number,
  segsForCircle: number,
  ctx: EvalContext,
  topEdge: CutterEdge = { chamfer: 0, fillet: 0 },
  botEdge: CutterEdge = { chamfer: 0, fillet: 0 },
  thickness = 0,
  cornerR = 0,
  gap = 0,
  spokes = 4,
  // Overshoot inset — how deep into the cutter (from each end) the parent's
  // face sits. Chamfer/fillet regions are positioned so their wide end
  // coincides with the parent face, not the cutter face. Defaults 0 preserve
  // legacy "chamfer at cutter face" behavior for non-pattern callers.
  topOvershoot = 0,
  botOvershoot = 0,
): Manifold {
  const { Manifold } = ctx.wasm;
  const r = d / 2;

  // Complex 2D-profile-based shapes (plus/cross, x, bowtie, diamond, radial).
  // For flat chamfer/fillet, decompose into convex pieces and build each via
  // Manifold.hull() for truly flat frustum sides. Only used when cornerR == 0 —
  // corner-rounded shapes fall back to stacked offset-extrusions (which produce
  // visible stairs but work for arbitrary 2D profiles).
  if (shape === 'plus' || shape === 'cross' || shape === 'x'
      || shape === 'bowtie' || shape === 'diamond' || shape === 'rhombus'
      || shape === 'radial' || shape === 'spokes' || shape === 'star') {
    const t = thickness > 0 ? thickness : d / 3;
    // Hull-based edge treatment: flat frustum chamfer + quarter-circle-sliced fillet.
    // Activates for both chamfer AND fillet on complex shapes. cornerR is ignored
    // — flat edges take priority over 2D corner rounding when both are requested.
    const hasEdge = topEdge.chamfer > 0 || topEdge.fillet > 0
        || botEdge.chamfer > 0 || botEdge.fillet > 0;
    if (hasEdge) {
      const pieces = decomposeCutterShape(shape, d, t, gap, spokes);
      if (pieces.length > 0) {
        let acc: Manifold | null = null;
        for (const poly of pieces) {
          const piece = buildConvexPieceFrustum(poly, h, topEdge, botEdge, ctx);
          if (acc === null) {
            acc = piece;
          } else {
            const merged: Manifold = acc.add(piece);
            trackIntermediate(ctx, merged);
            acc = merged;
          }
        }
        if (acc) return acc;
      }
    }
    // Fallback: CrossSection-based path (straight extrusion or stacked slices)
    let cs: InstanceType<ManifoldToplevel['CrossSection']>;
    if (shape === 'x') {
      cs = xCrossSection(d, t, cornerR, ctx);
    } else if (shape === 'bowtie') {
      cs = bowtieCrossSection(d, t, cornerR, ctx);
    } else if (shape === 'diamond' || shape === 'rhombus') {
      cs = diamondCrossSection(d, t, gap, cornerR, ctx);
    } else if (shape === 'radial' || shape === 'spokes' || shape === 'star') {
      cs = radialCrossSection(d, t, gap, spokes, cornerR, ctx);
    } else {
      cs = plusCrossSection(d, t, cornerR, ctx);
    }
    const m = extrudeProfileWithEdges(cs, h, topEdge, botEdge, ctx);
    cs.delete();
    return m;
  }

  const segs = shape === 'hex' ? 6 : shape === 'triangle' ? 3 : shape === 'square' ? 4 : segsForCircle;

  // Determine the depth reserved for each end (0 = flat / no treatment)
  const topDepth = topEdge.fillet > 0 ? topEdge.fillet : topEdge.chamfer;
  const botDepth = botEdge.fillet > 0 ? botEdge.fillet : botEdge.chamfer;
  // Positioning:
  //   Top chamfer spans [h/2 - topOvershoot - topDepth, h/2 - topOvershoot]
  //   Bot chamfer spans [-h/2 + botOvershoot, -h/2 + botOvershoot + botDepth]
  // If an end has NO chamfer, the middle must extend all the way to that
  // face (-h/2 or h/2), otherwise we leave an empty zone in the overshoot
  // region and the CSG subtract produces slivers against the parent face.
  // Extend middle by a 0.01mm OVERLAP into the chamfer region so Manifold
  // merges them seamlessly without a coincident inner face (which causes
  // sliver geometry at certain chamfer depths).
  const mergeOverlap = 0.01;
  const midTop = topDepth > 0 ? h / 2 - topOvershoot - topDepth + mergeOverlap : h / 2;
  const midBot = botDepth > 0 ? -h / 2 + botOvershoot + botDepth - mergeOverlap : -h / 2;
  const midH = midTop - midBot;
  const noTreatment = topDepth <= 0 && botDepth <= 0;

  if (noTreatment || midH <= 0) {
    const m = Manifold.cylinder(h, r, r, segs, true);
    trackIntermediate(ctx, m);
    return applyShapeRotation(m, shape, ctx);
  }

  // Build middle section — the plain straight shaft
  const mid = Manifold.cylinder(midH, r, r, segs, true);
  trackIntermediate(ctx, mid);
  // Shift middle so it sits between the two end regions (accounting for overshoot)
  const midCenterZ = (midTop + midBot) / 2;
  let acc: Manifold = midCenterZ !== 0 ? mid.translate([0, 0, midCenterZ]) : mid;
  if (midCenterZ !== 0) trackIntermediate(ctx, acc);

  const addEnd = (edge: CutterEdge, baseZ: number, isTop: boolean): void => {
    if (edge.chamfer > 0 && edge.fillet <= 0) {
      // Single frustum from r (interior) to r+chamfer (at face)
      const depth = edge.chamfer;
      const r1 = isTop ? r : r + depth; // bottom-of-slice radius
      const r2 = isTop ? r + depth : r; // top-of-slice radius
      const frust = Manifold.cylinder(depth, r1, r2, segs, false).translate([0, 0, baseZ]);
      trackIntermediate(ctx, frust);
      const u = acc.add(frust);
      trackIntermediate(ctx, u);
      acc = u;
    } else if (edge.fillet > 0) {
      // Stepped fillet: 6-slice quarter-circle approximation
      const depth = edge.fillet;
      const steps = 6;
      for (let k = 0; k < steps; k++) {
        const t1 = k / steps;
        const t2 = (k + 1) / steps;
        const off1 = depth * (1 - Math.sqrt(1 - t1 * t1));
        const off2 = depth * (1 - Math.sqrt(1 - t2 * t2));
        const sliceH = depth * (t2 - t1);
        // For top end: interior at t1, face at t2. For bottom: inverted.
        const r1 = isTop ? r + off1 : r + off2;
        const r2 = isTop ? r + off2 : r + off1;
        const sliceZ = isTop ? baseZ + depth * t1 : baseZ + depth * (1 - t2);
        const slice = Manifold.cylinder(sliceH, r1, r2, segs, false).translate([0, 0, sliceZ]);
        trackIntermediate(ctx, slice);
        const u = acc.add(slice);
        trackIntermediate(ctx, u);
        acc = u;
      }
    }
    // else: flat end, nothing to add
  };

  // Bottom end sits from -h/2 + botOvershoot to -h/2 + botOvershoot + botDepth
  if (botDepth > 0) addEnd(botEdge, -h / 2 + botOvershoot, false);
  // Top end sits from h/2 - topOvershoot - topDepth to h/2 - topOvershoot
  if (topDepth > 0) addEnd(topEdge, h / 2 - topOvershoot - topDepth, true);

  // Overshoot caps — straight cylinder at the chamfered/filleted radius
  // filling the region between the wide end of the chamfer and the cutter
  // face. Without this, the cutter has a hollow gap in the overshoot zone
  // that causes Manifold to produce sliver geometry during subtract.
  const topR = r + topDepth;
  const botR = r + botDepth;
  if (topDepth > 0 && topOvershoot > 0) {
    const cap = Manifold.cylinder(topOvershoot, topR, topR, segs, false)
      .translate([0, 0, h / 2 - topOvershoot]);
    trackIntermediate(ctx, cap);
    const u = acc.add(cap);
    trackIntermediate(ctx, u);
    acc = u;
  }
  if (botDepth > 0 && botOvershoot > 0) {
    const cap = Manifold.cylinder(botOvershoot, botR, botR, segs, false)
      .translate([0, 0, -h / 2]);
    trackIntermediate(ctx, cap);
    const u = acc.add(cap);
    trackIntermediate(ctx, u);
    acc = u;
  }

  return applyShapeRotation(acc, shape, ctx);
}

function applyShapeRotation(m: Manifold, shape: string, ctx: EvalContext): Manifold {

  // Shape-specific orientation so flat sides/vertices look right
  if (shape === 'hex') {
    const rot = m.rotate([0, 0, 30]);
    trackIntermediate(ctx, rot);
    return rot;
  }
  if (shape === 'square') {
    const rot = m.rotate([0, 0, 45]);
    trackIntermediate(ctx, rot);
    return rot;
  }
  if (shape === 'triangle') {
    const rot = m.rotate([0, 0, 90]);
    trackIntermediate(ctx, rot);
    return rot;
  }
  return m;
}
function buildHolePatternUnion(
  patternName: string,
  anchor: Vec3,
  bbox: { min: Vec3; max: Vec3 } | null,
  stmt: ModuleCallStatement,
  ctx: EvalContext,
  prefix = '',
  parent: Manifold | null = null,
): Manifold | null {
  const fn = getArgNum(stmt.args, '$fn', -1, ctx, ctx.fn);
  const segs = Math.max(fn, 16);

  const through = getArgBool(stmt.args, `${prefix}through`, -1, ctx, true);
  // When clip=true (default), drop cutters that don't intersect the parent.
  // Only meaningful if we have a parent to test against.
  const clip = getArgBool(stmt.args, 'clip', -1, ctx, true);
  // Edge treatment on each cutter: chamfer (flat taper) or fillet (rounded).
  // Read via prefix so `hole_chamfer` / `hole_fillet` work on primitives.
  // Per-end overrides: *_top / *_bot default to the global value if not set,
  // so `hole_chamfer=1.5` affects both ends; pass 0 to leave that end flat.
  const chamferAll = getArgNum(stmt.args, `${prefix}chamfer`, -1, ctx, 0);
  const chamferTop = getArgNum(stmt.args, `${prefix}chamfer_top`, -1, ctx, chamferAll);
  const chamferBot = getArgNum(stmt.args, `${prefix}chamfer_bot`, -1, ctx, chamferAll);
  const filletAll = getArgNum(stmt.args, `${prefix}fillet`, -1, ctx, 0);
  const filletTop = getArgNum(stmt.args, `${prefix}fillet_top`, -1, ctx, filletAll);
  const filletBot = getArgNum(stmt.args, `${prefix}fillet_bot`, -1, ctx, filletAll);
  const topEdge: CutterEdge = { chamfer: chamferTop, fillet: filletTop };
  const botEdge: CutterEdge = { chamfer: chamferBot, fillet: filletBot };
  // For plus/cross/x/bowtie/diamond/radial cutters: arm thickness, 2D corner rounding, diamond gap, radial spoke count
  let thickness = getArgNum(stmt.args, `${prefix}thickness`, -1, ctx, 0);
  const cornerR = getArgNum(stmt.args, `${prefix}corner_r`, -1, ctx, 0);
  const gapRaw = getArg(stmt.args, `${prefix}gap`, -1, ctx);
  let gap = typeof gapRaw === 'number' ? gapRaw : 0;
  const gapWasSet = gapRaw !== undefined;
  const spokes = Math.max(1, Math.floor(getArgNum(stmt.args, `${prefix}spokes`, -1, ctx, 4)));
  // One-time compatibility note when both corner_r and chamfer are used on complex shapes
  const hasChamferOnly = chamferAll + chamferTop + chamferBot > 0 && filletAll + filletTop + filletBot <= 0;
  const shapeHintRaw = getArgStr(stmt.args, 'cut_shape', -1, ctx, '') || getArgStr(stmt.args, 'shape', -1, ctx, '');
  const shapeHint = shapeHintRaw.toLowerCase();
  const COMPLEX_SHAPES = new Set(['plus', 'cross', 'x', 'bowtie', 'diamond', 'rhombus', 'radial', 'spokes', 'star']);
  if (cornerR > 0 && hasChamferOnly && COMPLEX_SHAPES.has(shapeHint)) {
    ctx.echoLog.push(`NOTE: hole_corner_r is ignored when chamfer is used on cut_shape="${shapeHint}" — flat chamfer takes priority.`);
  }
  const dArg = getArgNum(stmt.args, `${prefix}d`, -1, ctx, 0);
  const rArg = getArgNum(stmt.args, `${prefix}r`, -1, ctx, 0);
  let holeR = dArg > 0 ? dArg / 2 : (rArg > 0 ? rArg : 2.5);
  const centerArg = (getArg(stmt.args, `${prefix}center`, -1, ctx) as number[] | undefined) ?? [0, 0];
  const centerOffset: [number, number] = [centerArg[0] ?? 0, centerArg[1] ?? 0];
  let margin = getArgNum(stmt.args, 'margin', -1, ctx, 0);

  // Pick anchor axis (dominant component)
  const [ax, ay, az] = anchor;
  const axLen = Math.abs(ax), ayLen = Math.abs(ay), azLen = Math.abs(az);
  const axisIdx = azLen >= axLen && azLen >= ayLen ? 2 : (ayLen >= axLen ? 1 : 0);
  const inPlane: [number, number] = axisIdx === 2 ? [0, 1] : axisIdx === 1 ? [0, 2] : [1, 2];

  // Hole cutter height. Overshoot extends the cutter past the parent face so
  // CSG cleanly punches through, BUT too much overshoot on an end that has a
  // chamfer/fillet pushes the edge-treatment region outside the material and
  // makes it invisible. So per-end overshoot:
  //   - no edge treatment → 1mm (generous CSG margin)
  //   - with chamfer/fillet → ~10% of the chamfer depth (min 0.1mm), so the
  //     frustum has real material on both sides of the parent top face and
  //     Manifold won't produce coplanar-face slivers even for small chamfers.
  const userH = getArgNum(stmt.args, `${prefix}h`, -1, ctx, 0);
  const bboxExtent = bbox ? [bbox.max[0] - bbox.min[0], bbox.max[1] - bbox.min[1], bbox.max[2] - bbox.min[2]][axisIdx] : 10;
  const topEdgeSet = topEdge.chamfer > 0 || topEdge.fillet > 0;
  const botEdgeSet = botEdge.chamfer > 0 || botEdge.fillet > 0;
  const topEdgeDepth = Math.max(topEdge.chamfer, topEdge.fillet);
  const botEdgeDepth = Math.max(botEdge.chamfer, botEdge.fillet);
  // Chamfered ends: 0.5mm overshoot gives the cutter enough margin above/below
  // the parent face for clean CSG, while makeCutter positions the chamfer
  // wide end at the parent face (so the full chamfer depth stays inside the
  // material and is visually present).
  const overshootTop = through ? (topEdgeSet ? 0.5 : 1.0) : 0;
  const overshootBot = through ? (botEdgeSet ? 0.5 : 1.0) : 0;
  void topEdgeDepth; void botEdgeDepth;
  let h: number;
  if (userH > 0) h = userH;
  else if (bbox) h = bboxExtent + overshootTop + overshootBot;
  else h = 10;
  // Cutter shift: the cutter is built symmetric around origin. If the top
  // overshoot differs from the bottom, we need to shift the local-z center
  // so cutter top face lands at parent top + overshootTop (and similarly
  // for the bottom). Shift = (overshootTop - overshootBot) / 2 in local +Z.
  const localZShift = (overshootTop - overshootBot) / 2;
  // Probe height (no overshoot) — used for "fully-inside" clip test
  const probeH = Math.max(bboxExtent - 0.02, 0.01);

  // In-plane extents for fill mode
  let W = 0, Hh = 0;
  if (bbox) {
    const extents = [bbox.max[0] - bbox.min[0], bbox.max[1] - bbox.min[1], bbox.max[2] - bbox.min[2]];
    W = extents[inPlane[0]];
    Hh = extents[inPlane[1]];
  }

  // Auto-fit mode: when `rib` is set (material width between cells), compute
  // spacing, hole_d, thickness, and margin from the parent bbox. User just
  // specifies count (rows/cols for grid/hex, hole_count for linear/ring) and
  // the rib width; everything else is derived. Triggers only when rib > 0.
  // `rib` and `equilateral` are unprefixed — they don't conflict with primitive args.
  const ribArg = getArgNum(stmt.args, 'rib', -1, ctx, -1);
  const equilateral = getArgBool(stmt.args, 'equilateral', -1, ctx, false);
  let autoSpacingX: number | null = null;
  let autoSpacingY: number | null = null;
  const lcPattern = patternName.toLowerCase();
  if (ribArg > 0 && bbox) {
    // Rows/cols default to 1 (single row/column) when user doesn't specify
    const userRows = Math.max(0, getArgNum(stmt.args, 'rows', -1, ctx, 0) | 0);
    const userCols = Math.max(0, getArgNum(stmt.args, 'cols', -1, ctx, 0) | 0);
    const effRows = userRows > 0 ? userRows : 1;
    const effCols = userCols > 0 ? userCols : 1;
    autoSpacingX = W / effCols;
    autoSpacingY = Hh / effRows;
    // For grid/hex: holeR derives from the bbox cell. For linear and ring,
    // each pattern branch computes its own cell size (projected length / count
    // for linear; arc length / count for ring) and overrides holeR later.
    if (lcPattern !== 'ring' && lcPattern !== 'linear') {
      const cellMin = Math.min(autoSpacingX, autoSpacingY);
      const autoD = Math.max(0.1, cellMin - ribArg);
      if (dArg <= 0 && rArg <= 0) {
        holeR = autoD / 2;
      }
    }
    // NOTE: thickness and gap for radial shapes are computed AFTER the pattern
    // branch runs — the linear branch may override holeR (based on projected
    // length / count) and we want the parallel-spoke formula to use the final R.
    if (equilateral && thickness === 0) {
      // Equilateral for non-radial complex shapes (plus/x/bowtie/diamond)
      const isRadial = shapeHint === 'radial' || shapeHint === 'spokes' || shapeHint === 'star';
      if (!isRadial) thickness = (holeR * 2) / Math.sqrt(3);
    }
    // Margin = rib/2 when user didn't set margin explicitly
    if (margin === 0) margin = ribArg / 2;
  }

  const Wu = Math.max(0, W - 2 * margin);
  const Hu = Math.max(0, Hh - 2 * margin);

  const pattern = patternName.toLowerCase();
  // Shape of each individual cutter (circle cylinder by default)
  const shapeName = getArgStr(stmt.args, 'cut_shape', -1, ctx, '')
    || getArgStr(stmt.args, 'shape', -1, ctx, 'circle');
  const shape = shapeName.toLowerCase();
  const VALID_SHAPES = new Set(['circle', 'hex', 'triangle', 'square', 'plus', 'cross', 'x', 'bowtie', 'diamond', 'rhombus', 'radial', 'spokes', 'star']);
  if (!VALID_SHAPES.has(shape)) {
    ctx.echoLog.push(`WARNING: Unknown cut_shape "${shapeName}". Expected one of: circle, hex, triangle, square, plus, cross, x, bowtie, diamond, rhombus, radial (spokes/star alias). Did you mean to set pattern="${shapeName}"?`);
  }
  // Isogrid pattern defaults to triangular cutter with alternating orientation
  const effectiveShape = (pattern === 'isogrid' && shape === 'circle') ? 'triangle' : shape;

  const centers: CutterCenter[] = [];

  if (pattern === 'grid') {
    const spArg = getArg(stmt.args, 'spacing', -1, ctx);
    let sx: number, sy: number;
    if (autoSpacingX !== null && autoSpacingY !== null) {
      sx = autoSpacingX;
      sy = autoSpacingY;
    } else if (Array.isArray(spArg)) {
      sx = (spArg[0] as number) ?? 10;
      sy = (spArg[1] as number) ?? ((spArg[0] as number) ?? 10);
    } else {
      sx = typeof spArg === 'number' ? spArg : 10;
      sy = typeof spArg === 'number' ? spArg : 10;
    }
    let rows = Math.max(0, getArgNum(stmt.args, 'rows', -1, ctx, 0) | 0);
    let cols = Math.max(0, getArgNum(stmt.args, 'cols', -1, ctx, 0) | 0);
    if (autoSpacingX !== null) {
      // Auto-fit mode: cells tile the full bbox; centers at spacing*(i+0.5) offset from edge
      if (rows === 0) rows = 1;
      if (cols === 0) cols = 1;
      const ox = -W / 2 + sx / 2;
      const oy = -Hh / 2 + sy / 2;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) centers.push({ x: ox + c * sx, y: oy + r * sy });
      }
    } else {
      if ((rows === 0 || cols === 0) && bbox) {
        if (cols === 0) cols = Math.max(1, Math.floor(Wu / sx) + 1);
        if (rows === 0) rows = Math.max(1, Math.floor(Hu / sy) + 1);
      }
      if (rows === 0) rows = 3;
      if (cols === 0) cols = 3;
      const ox = -((cols - 1) * sx) / 2;
      const oy = -((rows - 1) * sy) / 2;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) centers.push({ x: ox + c * sx, y: oy + r * sy });
      }
    }
  } else if (pattern === 'hex') {
    const spArg = getArg(stmt.args, 'spacing', -1, ctx);
    const sx = autoSpacingX !== null
      ? autoSpacingX
      : (Array.isArray(spArg) ? ((spArg[0] as number) ?? 10) : (typeof spArg === 'number' ? spArg : 10));
    const sy = autoSpacingX !== null ? (autoSpacingY as number) : sx * Math.sqrt(3) / 2;
    let rows = Math.max(0, getArgNum(stmt.args, 'rows', -1, ctx, 0) | 0);
    let cols = Math.max(0, getArgNum(stmt.args, 'cols', -1, ctx, 0) | 0);
    if ((rows === 0 || cols === 0) && bbox) {
      if (cols === 0) cols = Math.max(1, Math.floor(Wu / sx) + 1);
      if (rows === 0) rows = Math.max(1, Math.floor(Hu / sy) + 1);
    }
    if (rows === 0) rows = 3;
    if (cols === 0) cols = 3;
    const ox = -((cols - 1) * sx) / 2;
    const oy = -((rows - 1) * sy) / 2;
    const halfW = Wu / 2 + holeR;
    for (let r = 0; r < rows; r++) {
      const shift = (r % 2) * (sx / 2);
      for (let c = 0; c < cols; c++) {
        const x = ox + c * sx + shift;
        if (bbox && Math.abs(x) > halfW) continue;
        centers.push({ x, y: oy + r * sy });
      }
    }
  } else if (pattern === 'isogrid') {
    // Proper equilateral triangle tessellation. Pitch `spacing` = triangle edge length.
    // Each row alternates up-triangle / down-triangle, sharing edges; rows stack directly
    // (no horizontal offset between rows — pattern repeats every `spacing` in X and
    // every triangle-height in Y).
    const spArg = getArg(stmt.args, 'spacing', -1, ctx);
    const s = Array.isArray(spArg) ? ((spArg[0] as number) ?? 10) : (typeof spArg === 'number' ? spArg : 10);
    const hTri = s * Math.sqrt(3) / 2; // triangle height
    let rows = Math.max(0, getArgNum(stmt.args, 'rows', -1, ctx, 0) | 0);
    let cols = Math.max(0, getArgNum(stmt.args, 'cols', -1, ctx, 0) | 0);
    if ((rows === 0 || cols === 0) && bbox) {
      if (cols === 0) cols = Math.max(1, Math.floor(Wu / s));
      if (rows === 0) rows = Math.max(1, Math.floor(Hu / hTri));
    }
    if (rows === 0) rows = 2;
    if (cols === 0) cols = 3;
    // Each row holds 2*cols triangles (alternating up/down at s/2 pitch).
    const totalW = cols * s;
    const totalH = rows * hTri;
    const ox = -totalW / 2;
    const oy = -totalH / 2;
    for (let r = 0; r < rows; r++) {
      const baseY = oy + r * hTri;
      for (let i = 0; i < 2 * cols; i++) {
        const x = ox + (i + 1) * s / 2;
        const isUp = i % 2 === 0;
        const y = baseY + (isUp ? hTri / 3 : 2 * hTri / 3);
        centers.push({ x, y, rotDeg: isUp ? 0 : 180 });
      }
    }
  } else if (pattern === 'ring') {
    const count = Math.max(1, getArgNum(stmt.args, `${prefix}count`, -1, ctx, 8) | 0);
    const radius = getArgNum(stmt.args, `${prefix}radius`, -1, ctx, 20);
    const startDeg = getArgNum(stmt.args, 'start_angle', -1, ctx, 0);
    const start = startDeg * Math.PI / 180;
    // Auto-fit: derive hole diameter from arc length between adjacent cuts
    // minus the rib. Only override when user didn't set d/r explicitly.
    if (ribArg > 0 && dArg <= 0 && rArg <= 0) {
      const arc = 2 * Math.PI * radius / count;
      holeR = Math.max(0.1, (arc - ribArg) / 2);
    }
    // orient: "fixed" (default, all cutters same orientation) or "radial"
    // (each cutter rotated so its +Y direction points toward ring center).
    // "radial" is useful for triangle/bowtie/diamond shapes so apex points inward.
    const orientArg = getArgStr(stmt.args, 'orient', -1, ctx, 'fixed').toLowerCase();
    const isRadialOrient = orientArg === 'radial' || orientArg === 'inward' || orientArg === 'center';
    for (let i = 0; i < count; i++) {
      const a = start + i * 2 * Math.PI / count;
      // For radial orient: rotate so shape's +Y (e.g. triangle apex) points
      // toward origin. Inward direction is at angle a+π; +Y is at π/2.
      // Delta = (a + π) − π/2 = a + π/2.
      const rotDeg = isRadialOrient ? (a * 180 / Math.PI + 90) : 0;
      centers.push({ x: radius * Math.cos(a), y: radius * Math.sin(a), rotDeg });
    }
  } else if (pattern === 'linear') {
    // 1D repeat along a configurable axis. angle in degrees; 0 = +X, 90 = +Y.
    const angleDeg = getArgNum(stmt.args, 'angle', -1, ctx, 0);
    const theta = angleDeg * Math.PI / 180;
    const dx = Math.cos(theta), dy = Math.sin(theta);
    let count = Math.max(0, getArgNum(stmt.args, `${prefix}count`, -1, ctx, 0) | 0);
    let s: number;
    if (ribArg > 0 && bbox) {
      // Auto-fit: project the bbox onto the angle direction.
      const projLen = Math.abs(W * dx) + Math.abs(Hh * dy);
      if (count === 0) {
        // Derive count from projected length and cell size (cell = thickness + rib).
        // If thickness isn't set, fall back to hole diameter as cell size.
        const cell = thickness > 0
          ? thickness + ribArg
          : (dArg > 0 ? dArg : (rArg > 0 ? rArg * 2 : 5)) + ribArg;
        count = Math.max(1, Math.floor((projLen + ribArg) / cell));
      }
      s = projLen / count;
      // Recompute hole diameter from auto-cell size and override holeR
      const autoDLinear = Math.max(0.1, s - ribArg);
      if (dArg <= 0 && rArg <= 0) holeR = autoDLinear / 2;
      if (equilateral && thickness === 0) thickness = autoDLinear / Math.sqrt(3);
    } else {
      const spArg = getArg(stmt.args, 'spacing', -1, ctx);
      s = Array.isArray(spArg) ? ((spArg[0] as number) ?? 10) : (typeof spArg === 'number' ? spArg : 10);
      if (count === 0 && bbox) {
        const projLen = Math.abs(Wu * dx) + Math.abs(Hu * dy);
        count = Math.max(1, Math.floor(projLen / s) + 1);
      }
      if (count === 0) count = 3;
    }
    const half = (count - 1) * s / 2;
    for (let i = 0; i < count; i++) {
      const t = -half + i * s;
      centers.push({ x: t * dx, y: t * dy });
    }
  } else {
    evalError(ctx, `Unknown hole pattern "${pattern}". Use "grid", "hex", "ring", "isogrid", or "linear".`, stmt.from, stmt.to);
    return null;
  }

  if (centers.length === 0) return null;

  // For RADIAL shapes with rib auto-fit: now that holeR is final (the linear
  // branch may have overridden it), compute gap and thickness using the
  // "parallel-sided spoke" relationship so material between spokes is uniform.
  //   gap       = rib / sin(π/N)
  //   thickness = (2R - gap) * tan(π/N)
  if (ribArg > 0) {
    const isRadialShape = shape === 'radial' || shape === 'spokes' || shape === 'star';
    if (isRadialShape && spokes >= 3) {
      const sinHalf = Math.sin(Math.PI / spokes);
      const tanHalf = Math.tan(Math.PI / spokes);
      if (!gapWasSet && sinHalf > 0) {
        gap = ribArg / sinHalf;
      }
      if (thickness === 0) {
        thickness = Math.max(0.5, (2 * holeR - gap) * tanHalf);
      }
    }
  }

  // cutterSize reflects the effective diameter (respects auto-fit overrides).
  const cutterSize = dArg > 0 ? dArg : (rArg > 0 ? rArg * 2 : (holeR > 0 ? holeR * 2 : 5));

  // Precompute orientation + target offset in world space.
  // Cutters are built Z-aligned centered at origin, then rotated to match
  // `anchor`, then each translated to its world-space position.
  const anchorNorm = anchorNormal(anchor);
  const rotMat = rotationFromTo([0, 0, 1] as Vec3, anchorNorm);
  const rotMat4 = mat3ToMat4Transform(rotMat);
  const bboxCenter: Vec3 = bbox ? [
    (bbox.min[0] + bbox.max[0]) / 2,
    (bbox.min[1] + bbox.max[1]) / 2,
    (bbox.min[2] + bbox.max[2]) / 2,
  ] : [0, 0, 0];
  const doClip = clip && parent !== null;

  // Place a shape-in-local-frame at its world position (rotate Z→anchor, translate)
  const placeInWorld = (localCutter: Manifold, ctr: CutterCenter): Manifold => {
    let c = localCutter;
    if (ctr.rotDeg) {
      const rot = c.rotate([0, 0, ctr.rotDeg]);
      trackIntermediate(ctx, rot);
      c = rot;
    }
    const localPos = c.translate([ctr.x + centerOffset[0], ctr.y + centerOffset[1], 0]);
    trackIntermediate(ctx, localPos);
    const oriented = localPos.transform(rotMat4);
    trackIntermediate(ctx, oriented);
    const placed = oriented.translate(bboxCenter);
    trackIntermediate(ctx, placed);
    return placed;
  };

  // Clip tolerance: allow a small fraction of the cutter to poke outside parent
  // before rejecting it. Useful for gears with scalloped tooth boundary —
  // a triangle can dip into a between-tooth gap without being discarded.
  const clipTol = getArgNum(stmt.args, `${prefix}clip_tol`, -1, ctx, 0.3);
  // Thin probe height — used for a planar silhouette test so non-uniform
  // Z parts (gear+hub, shell+lid) don't falsely reject cutters that fit
  // the main drilling face but miss the hub/lid above.
  const thinProbeH = Math.min(probeH, 0.5);
  let acc: Manifold | null = null;
  for (const ctr of centers) {
    // Clip test: thin probe near parent's anchor-opposite face; measure
    // fraction of volume outside parent and skip if above clipTol.
    if (doClip && parent) {
      const probeLocal = makeCutter(effectiveShape, thinProbeH, cutterSize, segs, ctx,
        { chamfer: 0, fillet: 0 }, { chamfer: 0, fillet: 0 }, thickness, cornerR, gap, spokes);
      // Offset probe to parent's anchor-opposite face (bottom for anchor=TOP)
      // so it sits in the "main" material, not dangling above a hub/lid.
      const zOffset = -(probeH - thinProbeH) / 2;
      const shifted = probeLocal.translate([0, 0, zOffset]);
      trackIntermediate(ctx, shifted);
      const probePlaced = placeInWorld(shifted, ctr);
      const outside = probePlaced.subtract(parent);
      trackIntermediate(ctx, outside);
      if (clipTol <= 0) {
        if (!outside.isEmpty()) continue;
      } else {
        const probeVol = probePlaced.volume();
        const outsideVol = outside.volume();
        if (probeVol <= 0 || outsideVol / probeVol > clipTol) continue;
      }
    }
    // Build the real cutter (with overshoot + any chamfer/fillet) and add to
    // union. Chamfer/fillet regions are positioned inside the parent material
    // (not at the cutter's outer faces), so the wide end of each chamfer
    // coincides with the corresponding parent face.
    let cutterLocal = makeCutter(effectiveShape, h, cutterSize, segs, ctx,
      topEdge, botEdge, thickness, cornerR, gap, spokes,
      overshootTop, overshootBot);
    if (localZShift !== 0) {
      const shiftedCut = cutterLocal.translate([0, 0, localZShift]);
      trackIntermediate(ctx, shiftedCut);
      cutterLocal = shiftedCut;
    }
    const placed = placeInWorld(cutterLocal, ctr);
    if (acc === null) {
      acc = placed;
    } else {
      const next: Manifold = acc.add(placed);
      trackIntermediate(ctx, next);
      acc = next;
    }
  }
  return acc;
}

/**
 * Check for a `cut_pattern` arg on any primitive module and, if present,
 * subtract the generated pattern from `parent`. Pattern args are read from
 * the same stmt (spacing, d, rows, cols, anchor, margin, etc.).
 */
function tryApplyCutPattern(
  parent: Manifold,
  stmt: ModuleCallStatement,
  ctx: EvalContext,
): Manifold {
  const patternName = getArgStr(stmt.args, 'cut_pattern', -1, ctx, '');
  if (!patternName) return parent;
  const anchorArg = (getArg(stmt.args, 'anchor', -1, ctx) as number[] | undefined) ?? [0, 0, 1];
  const anchor: Vec3 = [anchorArg[0] ?? 0, anchorArg[1] ?? 0, anchorArg[2] ?? 1];
  const bbox = parent.boundingBox();
  // Use "hole_" prefix so pattern args (hole_d, hole_h, hole_count, hole_radius,
  // hole_r, hole_center) don't collide with the primitive's own d/h/r/center.
  const holes = buildHolePatternUnion(patternName, anchor, bbox, stmt, ctx, 'hole_', parent);
  if (!holes) return parent;
  const result = parent.subtract(holes);
  trackIntermediate(ctx, result);
  return result;
}

function handleHolePattern(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const pattern = getArgStr(stmt.args, 'pattern', 0, ctx, 'grid');
  const anchorArg = (getArg(stmt.args, 'anchor', -1, ctx) as number[] | undefined) ?? [0, 0, 1];
  const anchor: Vec3 = [anchorArg[0] ?? 0, anchorArg[1] ?? 0, anchorArg[2] ?? 1];

  const hasChildren = stmt.children.length > 0;
  let parent: Manifold | null = null;
  let bbox: { min: Vec3; max: Vec3 } | null = null;
  if (hasChildren) {
    parent = evalChildren(stmt.children, ctx);
    if (!parent) return null;
    bbox = parent.boundingBox();
  }

  const holes = buildHolePatternUnion(pattern, anchor, bbox, stmt, ctx, '', parent);
  if (!holes) return parent;

  if (!hasChildren || !parent) return holes;

  const result = parent.subtract(holes);
  trackIntermediate(ctx, result);
  return result;
}


function handleSpring(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const { CrossSection } = ctx.wasm;
  const r = getArgNum(stmt.args, 'r', 0, ctx, 5);
  const rWire = getArgNum(stmt.args, 'r_wire', 1, ctx, 0.5);
  const pitch = getArgNum(stmt.args, 'pitch', -1, ctx, 3);
  const turns = getArgNum(stmt.args, 'turns', -1, ctx, 5);
  const center = getArgBool(stmt.args, 'center', -1, ctx, false);
  const fn = getArgNum(stmt.args, '$fn', -1, ctx, ctx.fn);

  const totalHeight = turns * pitch;
  const totalTwist = turns * 360;
  const slices = Math.max(Math.ceil(Math.abs(totalTwist) / 6), 4);

  // Circle cross-section at offset (r, 0)
  const circSteps = Math.max(Math.round(Math.max(fn, 16) / 2), 8);
  const profile: Vec2[] = [];
  for (let i = 0; i < circSteps; i++) {
    const a = (i / circSteps) * 2 * Math.PI;
    profile.push([r + rWire * Math.cos(a), rWire * Math.sin(a)]);
  }

  const cs = CrossSection.ofPolygons([profile]);
  let m: Manifold;
  try {
    m = cs.extrude(totalHeight, slices, totalTwist, [1, 1], center);
  } finally {
    cs.delete();
  }
  trackIntermediate(ctx, m);
  return m;
}

// ====================================================================
// Gear Modules — involute profile generation and 6 gear types
// ====================================================================

function generateInvoluteProfile(
  teeth: number, mod: number, pressureAngleDeg: number,
  backlash: number, segments: number,
): Vec2[] {
  const pitchR = (mod * teeth) / 2;
  const pa = pressureAngleDeg * Math.PI / 180;
  const baseR = pitchR * Math.cos(pa);
  const outerR = pitchR + mod;
  const rootR = Math.max(pitchR - 1.25 * mod, 0.1);
  const angularPitch = 2 * Math.PI / teeth;

  const tAt = (r: number): number => r <= baseR ? 0 : Math.sqrt((r / baseR) ** 2 - 1);
  const invFn = (t: number): number => t - Math.atan(t);

  const tPitch = tAt(pitchR);
  // Tooth thickness at pitch circle = circular_pitch / 2 = π·mod / 2
  // Half-thickness angle = (π·mod/2 - backlash/2) / pitchR = (π·mod - backlash) / (4·pitchR)
  const halfThickAngle = (Math.PI * mod - backlash) / (4 * pitchR);
  // baseAngleOff = tooth half-width at base circle (widest point)
  // The involute curls TOWARD the tooth center as it goes outward, narrowing the tooth.
  // Right flank angle = tc + baseAngleOff - invFn(t)  (decreases outward)
  // Left flank angle  = tc - baseAngleOff + invFn(t)  (increases outward)
  const baseAngleOff = halfThickAngle + invFn(tPitch);

  const stepsPerFlank = Math.max(Math.ceil(segments / teeth / 2), 3);
  const points: Vec2[] = [];
  const flankStart = Math.max(rootR, baseR);

  for (let i = 0; i < teeth; i++) {
    const tc = i * angularPitch;

    // Left flank: root → tip (angle increases going outward — CCW)
    if (rootR < baseR) {
      const a = tc - baseAngleOff;
      points.push([rootR * Math.cos(a), rootR * Math.sin(a)]);
    }
    for (let s = 0; s <= stepsPerFlank; s++) {
      const r = flankStart + (s / stepsPerFlank) * (outerR - flankStart);
      const t = tAt(r);
      const a = tc - baseAngleOff + invFn(t);
      points.push([r * Math.cos(a), r * Math.sin(a)]);
    }

    // Right flank: tip → root (angle increases going inward — CCW)
    for (let s = 0; s <= stepsPerFlank; s++) {
      const r = outerR - (s / stepsPerFlank) * (outerR - flankStart);
      const t = tAt(r);
      const a = tc + baseAngleOff - invFn(t);
      points.push([r * Math.cos(a), r * Math.sin(a)]);
    }
    if (rootR < baseR) {
      const a = tc + baseAngleOff;
      points.push([rootR * Math.cos(a), rootR * Math.sin(a)]);
    }

    // Root arc in the gap to next tooth's left root
    const rightEnd = rootR < baseR
      ? tc + baseAngleOff
      : tc + baseAngleOff - invFn(tAt(flankStart));
    const nextLeft = rootR < baseR
      ? tc + angularPitch - baseAngleOff
      : tc + angularPitch - baseAngleOff + invFn(tAt(flankStart));
    const arcSpan = nextLeft - rightEnd;
    if (arcSpan > 0.01) {
      for (let s = 1; s < 3; s++) {
        const a = rightEnd + (s / 3) * arcSpan;
        points.push([rootR * Math.cos(a), rootR * Math.sin(a)]);
      }
    }
  }

  return points;
}

function generateRackProfile(
  teeth: number, mod: number, pressureAngleDeg: number,
  backlash: number, barHeight: number,
): Vec2[] {
  const pa = pressureAngleDeg * Math.PI / 180;
  const pitch = Math.PI * mod;
  const addendum = mod;
  const dedendum = 1.25 * mod;
  const toothThick = pitch / 2 - backlash;
  const tanPA = Math.tan(pa);
  const totalWidth = teeth * pitch;

  const points: Vec2[] = [];
  // CCW winding: bottom-left → bottom-right → up right → teeth right-to-left → down left
  points.push([0, -(dedendum + barHeight)]);
  points.push([totalWidth, -(dedendum + barHeight)]);
  points.push([totalWidth, -dedendum]);

  for (let i = teeth - 1; i >= 0; i--) {
    const cx = (i + 0.5) * pitch;
    const tipHalfW = Math.max(toothThick / 2 - addendum * tanPA, 0);
    const rootHalfW = toothThick / 2 + dedendum * tanPA;
    points.push([cx + rootHalfW, -dedendum]);
    points.push([cx + tipHalfW, addendum]);
    points.push([cx - tipHalfW, addendum]);
    points.push([cx - rootHalfW, -dedendum]);
  }

  points.push([0, -dedendum]);
  return points;
}

function handleSpurGear(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const { Manifold, CrossSection } = ctx.wasm;
  const teeth = Math.max(Math.round(getArgNum(stmt.args, 'teeth', 0, ctx, 12)), 3);
  const mod = getArgNum(stmt.args, 'mod', 1, ctx, 1);
  const pa = getArgNum(stmt.args, 'pressure_angle', -1, ctx, 20);
  const width = getArgNum(stmt.args, 'width', -1, ctx, 1);
  const bore = getArgNum(stmt.args, 'bore', -1, ctx, 0);
  const backlash = getArgNum(stmt.args, 'backlash', -1, ctx, 0);
  const center = getArgBool(stmt.args, 'center', -1, ctx, false);
  const fn = getArgNum(stmt.args, '$fn', -1, ctx, ctx.fn);
  // Hub (boss cylinder around bore, extends above the gear body)
  const hubD = getArgNum(stmt.args, 'hub_d', -1, ctx, 0);
  const hubH = getArgNum(stmt.args, 'hub_h', -1, ctx, 0);
  // Lightening holes (reduces weight; ring of holes between bore and teeth)
  const optimized = getArgBool(stmt.args, 'optimized', -1, ctx, false);
  // Helix angle in degrees — each tooth twists around the gear axis along width.
  // 0 = straight spur; positive = right-hand twist (CCW looking from +Z).
  const helixAngle = getArgNum(stmt.args, 'helix_angle', -1, ctx, 0);

  const profile = generateInvoluteProfile(teeth, mod, pa, backlash, Math.max(fn, 32));
  const cs = CrossSection.ofPolygons([profile]);
  // Compute twist angle over the width from the helix angle
  const pitchR = (mod * teeth) / 2;
  const twistDeg = helixAngle !== 0 && pitchR > 0
    ? (width * Math.tan(helixAngle * Math.PI / 180) / pitchR) * (180 / Math.PI)
    : 0;
  const slices = twistDeg !== 0 ? Math.max(Math.ceil(Math.abs(twistDeg) / 6), 1) : 0;
  let m: Manifold;
  try {
    m = cs.extrude(width, slices, twistDeg, [1, 1], center);
  } finally {
    cs.delete();
  }
  trackIntermediate(ctx, m);

  const segs = Math.max(fn, 16);
  const baseZ = center ? -width / 2 : 0;

  // Lightening holes (before bore so bore subtracts cleanly from the web)
  if (optimized && bore > 0) {
    const pitchR = (mod * teeth) / 2;
    const rootR = pitchR - mod * 1.25;  // approximate root-circle radius
    const rLoch = Math.max(0.5, (2 * rootR - bore) / 8);
    const rm = bore / 2 + 2 * rLoch;
    const zLoch = Math.max(3, Math.floor((2 * Math.PI * rm) / (3 * rLoch)));
    if (rm + rLoch < rootR * 0.95) {  // only if holes fit inside the gear body
      const holeH = width + 0.4;
      let holesAcc: Manifold | null = null;
      for (let i = 0; i < zLoch; i++) {
        const a = (i * 2 * Math.PI) / zLoch;
        const x = rm * Math.cos(a);
        const y = rm * Math.sin(a);
        const holeCyl = Manifold.cylinder(holeH, rLoch, rLoch, segs, false);
        trackIntermediate(ctx, holeCyl);
        const holeP = holeCyl.translate([x, y, baseZ - 0.2]);
        trackIntermediate(ctx, holeP);
        if (holesAcc === null) {
          holesAcc = holeP;
        } else {
          const u: Manifold = holesAcc.add(holeP);
          trackIntermediate(ctx, u);
          holesAcc = u;
        }
      }
      if (holesAcc) {
        const withHoles: Manifold = m.subtract(holesAcc);
        trackIntermediate(ctx, withHoles);
        m = withHoles;
      }
    }
  }

  if (bore > 0) {
    const boreH = width + (hubH > 0 ? hubH + 0.2 : 0.2);
    const boreCyl = Manifold.cylinder(boreH, bore / 2, bore / 2, segs, false);
    trackIntermediate(ctx, boreCyl);
    const boreShifted = boreCyl.translate([0, 0, baseZ - 0.1]);
    trackIntermediate(ctx, boreShifted);
    const next = m.subtract(boreShifted);
    trackIntermediate(ctx, next);
    m = next;
  }

  // Hub boss on top of the gear body (around the bore)
  if (hubD > 0 && hubH > 0 && hubD / 2 > bore / 2) {
    const hubCyl = Manifold.cylinder(hubH, hubD / 2, hubD / 2, segs, false);
    trackIntermediate(ctx, hubCyl);
    // Subtract bore through the hub
    let hubShape: Manifold = hubCyl;
    if (bore > 0) {
      const hubBore = Manifold.cylinder(hubH + 0.4, bore / 2, bore / 2, segs, false);
      trackIntermediate(ctx, hubBore);
      const hubBoreShift = hubBore.translate([0, 0, -0.2]);
      trackIntermediate(ctx, hubBoreShift);
      const hubMinusBore: Manifold = hubCyl.subtract(hubBoreShift);
      trackIntermediate(ctx, hubMinusBore);
      hubShape = hubMinusBore;
    }
    const hubPlaced = hubShape.translate([0, 0, baseZ + width]);
    trackIntermediate(ctx, hubPlaced);
    const gearPlusHub: Manifold = m.add(hubPlaced);
    trackIntermediate(ctx, gearPlusHub);
    m = gearPlusHub;
  }

  return m;
}

function handleHelicalGear(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const { Manifold, CrossSection } = ctx.wasm;
  const teeth = Math.max(Math.round(getArgNum(stmt.args, 'teeth', 0, ctx, 12)), 3);
  const mod = getArgNum(stmt.args, 'mod', 1, ctx, 1);
  const pa = getArgNum(stmt.args, 'pressure_angle', -1, ctx, 20);
  const width = getArgNum(stmt.args, 'width', -1, ctx, 1);
  const helixAngle = getArgNum(stmt.args, 'helix_angle', -1, ctx, 20);
  const bore = getArgNum(stmt.args, 'bore', -1, ctx, 0);
  const backlash = getArgNum(stmt.args, 'backlash', -1, ctx, 0);
  const center = getArgBool(stmt.args, 'center', -1, ctx, false);
  const fn = getArgNum(stmt.args, '$fn', -1, ctx, ctx.fn);

  const pitchR = (mod * teeth) / 2;
  const helixRad = helixAngle * Math.PI / 180;
  const twistDeg = (width * Math.tan(helixRad) / pitchR) * (180 / Math.PI);
  const slices = Math.max(Math.ceil(Math.abs(twistDeg) / 6), 1);

  const profile = generateInvoluteProfile(teeth, mod, pa, backlash, Math.max(fn, 32));
  const cs = CrossSection.ofPolygons([profile]);
  let m: Manifold;
  try {
    m = cs.extrude(width, slices, twistDeg, [1, 1], center);
  } finally {
    cs.delete();
  }
  trackIntermediate(ctx, m);

  if (bore > 0) {
    const boreH = width + 0.2;
    const boreCyl = Manifold.cylinder(boreH, bore / 2, bore / 2, Math.max(fn, 16), false);
    trackIntermediate(ctx, boreCyl);
    const boreShifted = boreCyl.translate([0, 0, center ? -boreH / 2 : -0.1]);
    trackIntermediate(ctx, boreShifted);
    const next = m.subtract(boreShifted);
    trackIntermediate(ctx, next);
    m = next;
  }

  return m;
}

function handleHerringboneGear(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const { Manifold, CrossSection } = ctx.wasm;
  const teeth = Math.max(Math.round(getArgNum(stmt.args, 'teeth', 0, ctx, 12)), 3);
  const mod = getArgNum(stmt.args, 'mod', 1, ctx, 1);
  const pa = getArgNum(stmt.args, 'pressure_angle', -1, ctx, 20);
  const width = getArgNum(stmt.args, 'width', -1, ctx, 1);
  const helixAngle = getArgNum(stmt.args, 'helix_angle', -1, ctx, 30);
  const bore = getArgNum(stmt.args, 'bore', -1, ctx, 0);
  const backlash = getArgNum(stmt.args, 'backlash', -1, ctx, 0);
  const center = getArgBool(stmt.args, 'center', -1, ctx, false);
  const fn = getArgNum(stmt.args, '$fn', -1, ctx, ctx.fn);

  const pitchR = (mod * teeth) / 2;
  const helixRad = helixAngle * Math.PI / 180;
  const halfWidth = width / 2;
  const twistDeg = (halfWidth * Math.tan(helixRad) / pitchR) * (180 / Math.PI);
  const slices = Math.max(Math.ceil(Math.abs(twistDeg) / 6), 1);

  const profile = generateInvoluteProfile(teeth, mod, pa, backlash, Math.max(fn, 32));
  const cs = CrossSection.ofPolygons([profile]);
  let bottom: Manifold;
  let top: Manifold;
  try {
    bottom = cs.extrude(halfWidth, slices, twistDeg, [1, 1], false);
    trackIntermediate(ctx, bottom);
    top = cs.extrude(halfWidth, slices, -twistDeg, [1, 1], false);
    trackIntermediate(ctx, top);
  } finally {
    cs.delete();
  }

  const topMoved = top.translate([0, 0, halfWidth]);
  trackIntermediate(ctx, topMoved);

  let m = bottom.add(topMoved);
  trackIntermediate(ctx, m);

  if (center) {
    const centered = m.translate([0, 0, -halfWidth]);
    trackIntermediate(ctx, centered);
    m = centered;
  }

  if (bore > 0) {
    const boreH = width + 0.2;
    const boreCyl = Manifold.cylinder(boreH, bore / 2, bore / 2, Math.max(fn, 16), false);
    trackIntermediate(ctx, boreCyl);
    const boreShifted = boreCyl.translate([0, 0, center ? -boreH / 2 : -0.1]);
    trackIntermediate(ctx, boreShifted);
    const next = m.subtract(boreShifted);
    trackIntermediate(ctx, next);
    m = next;
  }

  return m;
}

function handleRack(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const { CrossSection } = ctx.wasm;
  const teeth = Math.max(Math.round(getArgNum(stmt.args, 'teeth', 0, ctx, 10)), 1);
  const mod = getArgNum(stmt.args, 'mod', 1, ctx, 1);
  const pa = getArgNum(stmt.args, 'pressure_angle', -1, ctx, 20);
  const width = getArgNum(stmt.args, 'width', -1, ctx, 1);
  const heightArg = getArgNum(stmt.args, 'height', -1, ctx, -1);
  const barHeight = heightArg > 0 ? heightArg : 2.25 * mod;
  const backlash = getArgNum(stmt.args, 'backlash', -1, ctx, 0);
  const center = getArgBool(stmt.args, 'center', -1, ctx, false);

  const profile = generateRackProfile(teeth, mod, pa, backlash, barHeight);
  const cs = CrossSection.ofPolygons([profile]);
  let m: Manifold;
  try {
    m = cs.extrude(width, 0, 0, [1, 1], false);
  } finally {
    cs.delete();
  }
  trackIntermediate(ctx, m);

  if (center) {
    const totalLength = teeth * Math.PI * mod;
    const shifted = m.translate([-totalLength / 2, 0, -width / 2]);
    trackIntermediate(ctx, shifted);
    m = shifted;
  }

  return m;
}

function handleBevelGear(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const { Manifold, CrossSection } = ctx.wasm;
  const teeth = Math.max(Math.round(getArgNum(stmt.args, 'teeth', 0, ctx, 12)), 3);
  const mod = getArgNum(stmt.args, 'mod', 1, ctx, 1);
  const pa = getArgNum(stmt.args, 'pressure_angle', -1, ctx, 20);
  const width = getArgNum(stmt.args, 'width', -1, ctx, 1);
  const coneAngle = getArgNum(stmt.args, 'cone_angle', -1, ctx, 45);
  const bore = getArgNum(stmt.args, 'bore', -1, ctx, 0);
  const backlash = getArgNum(stmt.args, 'backlash', -1, ctx, 0);
  const center = getArgBool(stmt.args, 'center', -1, ctx, false);
  const fn = getArgNum(stmt.args, '$fn', -1, ctx, ctx.fn);

  const pitchR = (mod * teeth) / 2;
  const coneRad = coneAngle * Math.PI / 180;
  const scaleTop = Math.max((pitchR - width * Math.sin(coneRad)) / pitchR, 0.1);
  const height = width * Math.cos(coneRad);
  const slices = Math.max(Math.ceil(width / mod), 4);

  const profile = generateInvoluteProfile(teeth, mod, pa, backlash, Math.max(fn, 32));
  const cs = CrossSection.ofPolygons([profile]);
  let m: Manifold;
  try {
    m = cs.extrude(height, slices, 0, [scaleTop, scaleTop], center);
  } finally {
    cs.delete();
  }
  trackIntermediate(ctx, m);

  if (bore > 0) {
    const boreH = height + 0.2;
    const boreCyl = Manifold.cylinder(boreH, bore / 2, bore / 2, Math.max(fn, 16), false);
    trackIntermediate(ctx, boreCyl);
    const boreShifted = boreCyl.translate([0, 0, center ? -boreH / 2 : -0.1]);
    trackIntermediate(ctx, boreShifted);
    const next = m.subtract(boreShifted);
    trackIntermediate(ctx, next);
    m = next;
  }

  return m;
}

function handleWorm(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const { Manifold, CrossSection } = ctx.wasm;
  const starts = Math.min(Math.max(Math.round(getArgNum(stmt.args, 'starts', 0, ctx, 1)), 1), 8);
  const mod = getArgNum(stmt.args, 'mod', 1, ctx, 1);
  const pa = getArgNum(stmt.args, 'pressure_angle', -1, ctx, 20);
  const length = getArgNum(stmt.args, 'length', -1, ctx, 10);
  const d = getArgNum(stmt.args, 'd', -1, ctx, -1);
  const r = d >= 0 ? d / 2 : getArgNum(stmt.args, 'r', -1, ctx, 3);
  const bore = getArgNum(stmt.args, 'bore', -1, ctx, 0);
  const center = getArgBool(stmt.args, 'center', -1, ctx, false);
  const fn = getArgNum(stmt.args, '$fn', -1, ctx, ctx.fn);

  const addendum = mod;
  const dedendum = 1.25 * mod;
  const outerR = r + addendum;
  const rootR = Math.max(r - dedendum, 0.5);
  const pitch = Math.PI * mod;
  const totalTwist = (length / pitch) * 360;
  const slices = Math.max(Math.ceil(Math.abs(totalTwist) / 6), 4);

  // Star-shaped cross-section with `starts` lobes
  const paRad = pa * Math.PI / 180;
  const tanPA = Math.tan(paRad);
  const sectorAngle = 2 * Math.PI / starts;
  const toothDepth = outerR - rootR;
  const tipHalf = Math.max(sectorAngle * 0.15, 0.05);
  const rootHalf = Math.min(tipHalf + toothDepth * tanPA / ((outerR + rootR) / 2), sectorAngle * 0.4);

  const circSteps = Math.max(Math.ceil(Math.max(fn, 32) / starts / 2), 3);
  const points: Vec2[] = [];

  for (let i = 0; i < starts; i++) {
    const tc = i * sectorAngle;

    // Tooth: right root → right tip → left tip → left root
    points.push([rootR * Math.cos(tc - rootHalf), rootR * Math.sin(tc - rootHalf)]);
    points.push([outerR * Math.cos(tc - tipHalf), outerR * Math.sin(tc - tipHalf)]);
    points.push([outerR * Math.cos(tc + tipHalf), outerR * Math.sin(tc + tipHalf)]);
    points.push([rootR * Math.cos(tc + rootHalf), rootR * Math.sin(tc + rootHalf)]);

    // Root arc to next tooth
    const arcStart = tc + rootHalf;
    const arcEnd = tc + sectorAngle - rootHalf;
    for (let s = 1; s < circSteps; s++) {
      const a = arcStart + (s / circSteps) * (arcEnd - arcStart);
      points.push([rootR * Math.cos(a), rootR * Math.sin(a)]);
    }
  }

  const cs = CrossSection.ofPolygons([points]);
  let m: Manifold;
  try {
    m = cs.extrude(length, slices, totalTwist, [1, 1], center);
  } finally {
    cs.delete();
  }
  trackIntermediate(ctx, m);

  if (bore > 0 && bore / 2 < rootR) {
    const boreH = length + 0.2;
    const boreCyl = Manifold.cylinder(boreH, bore / 2, bore / 2, Math.max(fn, 16), false);
    trackIntermediate(ctx, boreCyl);
    const boreShifted = boreCyl.translate([0, 0, center ? -boreH / 2 : -0.1]);
    trackIntermediate(ctx, boreShifted);
    const next = m.subtract(boreShifted);
    trackIntermediate(ctx, next);
    m = next;
  }

  return m;
}

// ====================================================================
// Structural Profile Modules — L/U/O/T/I profiles with fillet/chamfer
// ====================================================================

function generateArcPoints(cx: number, cy: number, radius: number, startAngle: number, endAngle: number, segments: number): Vec2[] {
  const pts: Vec2[] = [];
  for (let i = 0; i <= segments; i++) {
    const a = startAngle + (i / segments) * (endAngle - startAngle);
    pts.push([cx + radius * Math.cos(a), cy + radius * Math.sin(a)]);
  }
  return pts;
}

function extrudeProfile(profile: Vec2[], l: number, w: number, h: number, center: boolean, ctx: EvalContext): Manifold {
  const { CrossSection } = ctx.wasm;
  const cs = CrossSection.ofPolygons([profile]);
  let m: Manifold;
  try {
    m = cs.extrude(l);
  } finally {
    cs.delete();
  }
  trackIntermediate(ctx, m);
  if (center) {
    const shifted = m.translate([-w / 2, -h / 2, -l / 2]);
    trackIntermediate(ctx, shifted);
    return shifted;
  }
  return m;
}

function handleLProfile(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const w = getArgNum(stmt.args, 'w', 0, ctx, 1);
  const h = getArgNum(stmt.args, 'h', 1, ctx, 1);
  let t = getArgNum(stmt.args, 't', 2, ctx, 0.1);
  const l = getArgNum(stmt.args, 'l', -1, ctx, 1);
  const filletInner = getArgNum(stmt.args, 'fillet', -1, ctx, 0);
  const chamferInner = getArgNum(stmt.args, 'chamfer', -1, ctx, 0);
  const filletOuter = getArgNum(stmt.args, 'fillet_outside', -1, ctx, 0);
  const chamferOuter = getArgNum(stmt.args, 'chamfer_outside', -1, ctx, 0);
  const center = getArgBool(stmt.args, 'center', -1, ctx, false);
  const fn = getArgNum(stmt.args, '$fn', -1, ctx, ctx.fn);
  const arcSegs = Math.max(Math.round(fn / 4), 4);

  // Fillet takes priority over chamfer
  let bevel = filletInner > 0 ? filletInner : chamferInner;
  const bevelType = filletInner > 0 ? 'fillet' : 'chamfer';
  let outside = filletOuter > 0 ? filletOuter : chamferOuter;
  const outsideType = filletOuter > 0 ? 'fillet' : 'chamfer';

  // Clamping
  t = Math.min(t, Math.min(w, h) * 0.99);
  bevel = Math.min(bevel, Math.min(w - t, h - t) * 0.99);
  outside = Math.min(outside, Math.min(w, h) * 0.99);

  // Profile CCW, starting at (w,0) so outside corner at (0,0) is mid-array
  const profile: Vec2[] = [];
  profile.push([w, 0]);
  profile.push([w, t]);

  // Inner corner
  if (bevel > 0) {
    if (bevelType === 'fillet') {
      const arcPts = generateArcPoints(t + bevel, t + bevel, bevel, Math.PI * 1.5, Math.PI, arcSegs);
      for (const p of arcPts) profile.push(p);
    } else {
      profile.push([t + bevel, t]);
      profile.push([t, t + bevel]);
    }
  } else {
    profile.push([t, t]);
  }

  profile.push([t, h]);
  profile.push([0, h]);

  // Outside corner at (0,0)
  if (outside > 0) {
    if (outsideType === 'fillet') {
      const arcPts = generateArcPoints(outside, outside, outside, Math.PI, Math.PI * 1.5, arcSegs);
      for (const p of arcPts) profile.push(p);
    } else {
      profile.push([0, outside]);
      profile.push([outside, 0]);
    }
  } else {
    profile.push([0, 0]);
  }

  return extrudeProfile(profile, l, w, h, center, ctx);
}

function handleUProfile(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const w = getArgNum(stmt.args, 'w', 0, ctx, 1);
  const h = getArgNum(stmt.args, 'h', 1, ctx, 1);
  let t = getArgNum(stmt.args, 't', 2, ctx, 0.1);
  const l = getArgNum(stmt.args, 'l', -1, ctx, 1);
  const filletInner = getArgNum(stmt.args, 'fillet', -1, ctx, 0);
  const chamferInner = getArgNum(stmt.args, 'chamfer', -1, ctx, 0);
  const filletOuter = getArgNum(stmt.args, 'fillet_outside', -1, ctx, 0);
  const chamferOuter = getArgNum(stmt.args, 'chamfer_outside', -1, ctx, 0);
  const center = getArgBool(stmt.args, 'center', -1, ctx, false);
  const fn = getArgNum(stmt.args, '$fn', -1, ctx, ctx.fn);
  const arcSegs = Math.max(Math.round(fn / 4), 4);

  // Fillet takes priority over chamfer
  let bevel = filletInner > 0 ? filletInner : chamferInner;
  const bevelType = filletInner > 0 ? 'fillet' : 'chamfer';
  let outside = filletOuter > 0 ? filletOuter : chamferOuter;
  const outsideType = filletOuter > 0 ? 'fillet' : 'chamfer';

  // Clamping
  t = Math.min(t, Math.min(w / 2, h) * 0.99);
  bevel = Math.min(bevel, Math.min(w / 2 - t, h - t) * 0.99);
  outside = Math.min(outside, Math.min(w / 2, h) * 0.99);

  // Profile CCW, starting at (0,h) so outside corners at (0,0) and (w,0) are mid-array
  const profile: Vec2[] = [];
  profile.push([0, h]);

  // Left outside corner at (0,0)
  if (outside > 0) {
    if (outsideType === 'fillet') {
      const arcPts = generateArcPoints(outside, outside, outside, Math.PI, Math.PI * 1.5, arcSegs);
      for (const p of arcPts) profile.push(p);
    } else {
      profile.push([0, outside]);
      profile.push([outside, 0]);
    }
  } else {
    profile.push([0, 0]);
  }

  // Right outside corner at (w,0)
  if (outside > 0) {
    if (outsideType === 'fillet') {
      const arcPts = generateArcPoints(w - outside, outside, outside, Math.PI * 1.5, Math.PI * 2, arcSegs);
      for (const p of arcPts) profile.push(p);
    } else {
      profile.push([w - outside, 0]);
      profile.push([w, outside]);
    }
  } else {
    profile.push([w, 0]);
  }

  profile.push([w, h]);
  profile.push([w - t, h]);

  // Right inner corner
  if (bevel > 0) {
    if (bevelType === 'fillet') {
      const rightArc = generateArcPoints(w - t - bevel, t + bevel, bevel, 0, -Math.PI / 2, arcSegs);
      for (const p of rightArc) profile.push(p);
    } else {
      profile.push([w - t, t + bevel]);
      profile.push([w - t - bevel, t]);
    }
  } else {
    profile.push([w - t, t]);
  }

  // Left inner corner
  if (bevel > 0) {
    if (bevelType === 'fillet') {
      const leftArc = generateArcPoints(t + bevel, t + bevel, bevel, -Math.PI / 2, -Math.PI, arcSegs);
      for (const p of leftArc) profile.push(p);
    } else {
      profile.push([t + bevel, t]);
      profile.push([t, t + bevel]);
    }
  } else {
    profile.push([t, t]);
  }

  profile.push([t, h]);

  return extrudeProfile(profile, l, w, h, center, ctx);
}

function handleOProfile(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const { CrossSection } = ctx.wasm;
  const w = getArgNum(stmt.args, 'w', 0, ctx, 1);
  const h = getArgNum(stmt.args, 'h', 1, ctx, 1);
  let t = getArgNum(stmt.args, 't', 2, ctx, 0.1);
  const l = getArgNum(stmt.args, 'l', -1, ctx, 1);
  const filletInner = getArgNum(stmt.args, 'fillet', -1, ctx, 0);
  const chamferInner = getArgNum(stmt.args, 'chamfer', -1, ctx, 0);
  const filletOuter = getArgNum(stmt.args, 'fillet_outside', -1, ctx, 0);
  const chamferOuter = getArgNum(stmt.args, 'chamfer_outside', -1, ctx, 0);
  const center = getArgBool(stmt.args, 'center', -1, ctx, false);
  const fn = getArgNum(stmt.args, '$fn', -1, ctx, ctx.fn);
  const arcSegs = Math.max(Math.round(fn / 4), 4);

  // Fillet takes priority over chamfer
  let bevel = filletInner > 0 ? filletInner : chamferInner;
  const bevelType = filletInner > 0 ? 'fillet' : 'chamfer';
  let outside = filletOuter > 0 ? filletOuter : chamferOuter;
  const outsideType = filletOuter > 0 ? 'fillet' : 'chamfer';

  // Clamping — wall can't exceed half the width or height
  t = Math.min(t, Math.min(w / 2, h / 2) * 0.99);
  bevel = Math.min(bevel, Math.min(w / 2 - t, h / 2 - t) * 0.99);
  outside = Math.min(outside, Math.min(w / 2, h / 2) * 0.99);

  // Outer contour CCW: BL → BR → TR → TL
  const outer: Vec2[] = [];

  // Bottom-left (0,0)
  if (outside > 0) {
    if (outsideType === 'fillet') {
      for (const p of generateArcPoints(outside, outside, outside, Math.PI, Math.PI * 1.5, arcSegs)) outer.push(p);
    } else {
      outer.push([0, outside]);
      outer.push([outside, 0]);
    }
  } else {
    outer.push([0, 0]);
  }

  // Bottom-right (w,0)
  if (outside > 0) {
    if (outsideType === 'fillet') {
      for (const p of generateArcPoints(w - outside, outside, outside, Math.PI * 1.5, Math.PI * 2, arcSegs)) outer.push(p);
    } else {
      outer.push([w - outside, 0]);
      outer.push([w, outside]);
    }
  } else {
    outer.push([w, 0]);
  }

  // Top-right (w,h)
  if (outside > 0) {
    if (outsideType === 'fillet') {
      for (const p of generateArcPoints(w - outside, h - outside, outside, 0, Math.PI / 2, arcSegs)) outer.push(p);
    } else {
      outer.push([w, h - outside]);
      outer.push([w - outside, h]);
    }
  } else {
    outer.push([w, h]);
  }

  // Top-left (0,h)
  if (outside > 0) {
    if (outsideType === 'fillet') {
      for (const p of generateArcPoints(outside, h - outside, outside, Math.PI / 2, Math.PI, arcSegs)) outer.push(p);
    } else {
      outer.push([outside, h]);
      outer.push([0, h - outside]);
    }
  } else {
    outer.push([0, h]);
  }

  // Inner contour — build CCW then reverse for CW (hole)
  const innerCCW: Vec2[] = [];

  // Bottom-left inner (t,t)
  if (bevel > 0) {
    if (bevelType === 'fillet') {
      for (const p of generateArcPoints(t + bevel, t + bevel, bevel, Math.PI, Math.PI * 1.5, arcSegs)) innerCCW.push(p);
    } else {
      innerCCW.push([t, t + bevel]);
      innerCCW.push([t + bevel, t]);
    }
  } else {
    innerCCW.push([t, t]);
  }

  // Bottom-right inner (w-t, t)
  if (bevel > 0) {
    if (bevelType === 'fillet') {
      for (const p of generateArcPoints(w - t - bevel, t + bevel, bevel, Math.PI * 1.5, Math.PI * 2, arcSegs)) innerCCW.push(p);
    } else {
      innerCCW.push([w - t - bevel, t]);
      innerCCW.push([w - t, t + bevel]);
    }
  } else {
    innerCCW.push([w - t, t]);
  }

  // Top-right inner (w-t, h-t)
  if (bevel > 0) {
    if (bevelType === 'fillet') {
      for (const p of generateArcPoints(w - t - bevel, h - t - bevel, bevel, 0, Math.PI / 2, arcSegs)) innerCCW.push(p);
    } else {
      innerCCW.push([w - t, h - t - bevel]);
      innerCCW.push([w - t - bevel, h - t]);
    }
  } else {
    innerCCW.push([w - t, h - t]);
  }

  // Top-left inner (t, h-t)
  if (bevel > 0) {
    if (bevelType === 'fillet') {
      for (const p of generateArcPoints(t + bevel, h - t - bevel, bevel, Math.PI / 2, Math.PI, arcSegs)) innerCCW.push(p);
    } else {
      innerCCW.push([t + bevel, h - t]);
      innerCCW.push([t, h - t - bevel]);
    }
  } else {
    innerCCW.push([t, h - t]);
  }

  // Reverse inner to CW for hole winding
  const inner: Vec2[] = innerCCW.reverse();

  // Create cross-section with hole and extrude
  const cs = CrossSection.ofPolygons([outer, inner]);
  let m: Manifold;
  try {
    m = cs.extrude(l);
  } finally {
    cs.delete();
  }
  trackIntermediate(ctx, m);
  if (center) {
    const shifted = m.translate([-w / 2, -h / 2, -l / 2]);
    trackIntermediate(ctx, shifted);
    return shifted;
  }
  return m;
}

function handleTProfile(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const w = getArgNum(stmt.args, 'w', 0, ctx, 6);
  const h = getArgNum(stmt.args, 'h', 1, ctx, 8);
  let t = getArgNum(stmt.args, 't', 2, ctx, 1);
  const twArg = getArgNum(stmt.args, 'tw', -1, ctx, -1);
  const tw = twArg >= 0 ? twArg : t;
  const l = getArgNum(stmt.args, 'l', -1, ctx, 10);
  const filletInner = getArgNum(stmt.args, 'fillet', -1, ctx, 0);
  const chamferInner = getArgNum(stmt.args, 'chamfer', -1, ctx, 0);
  const center = getArgBool(stmt.args, 'center', -1, ctx, false);
  const fn = getArgNum(stmt.args, '$fn', -1, ctx, ctx.fn);
  const arcSegs = Math.max(Math.round(fn / 4), 4);

  // Fillet takes priority over chamfer
  let bevel = filletInner > 0 ? filletInner : chamferInner;
  const bevelType = filletInner > 0 ? 'fillet' : 'chamfer';

  // Clamping
  t = Math.min(t, h * 0.5);
  const twClamped = Math.min(tw, w * 0.99);
  bevel = Math.min(bevel, Math.min((w - twClamped) / 2, h - t) * 0.99);

  // T-profile: web (bottom center) + flange (top full width)
  // CCW winding starting from bottom-left of web
  const webLeft = (w - twClamped) / 2;
  const webRight = (w + twClamped) / 2;
  const flangeBot = h - t;

  const profile: Vec2[] = [];
  // Bottom of web
  profile.push([webLeft, 0]);
  profile.push([webRight, 0]);

  // Right side of web up to flange
  // Right inner corner (web-flange junction)
  if (bevel > 0) {
    profile.push([webRight, flangeBot - bevel]);
    if (bevelType === 'fillet') {
      const arcPts = generateArcPoints(webRight + bevel, flangeBot - bevel, bevel, Math.PI, Math.PI / 2, arcSegs);
      for (const p of arcPts) profile.push(p);
    } else {
      profile.push([webRight + bevel, flangeBot]);
    }
  } else {
    profile.push([webRight, flangeBot]);
  }

  // Right side of flange
  profile.push([w, flangeBot]);
  profile.push([w, h]);

  // Top of flange left
  profile.push([0, h]);
  profile.push([0, flangeBot]);

  // Left inner corner (web-flange junction)
  if (bevel > 0) {
    if (bevelType === 'fillet') {
      const arcPts = generateArcPoints(webLeft - bevel, flangeBot - bevel, bevel, Math.PI / 2, 0, arcSegs);
      for (const p of arcPts) profile.push(p);
    } else {
      profile.push([webLeft - bevel, flangeBot]);
    }
    profile.push([webLeft, flangeBot - bevel]);
  } else {
    profile.push([webLeft, flangeBot]);
  }

  return extrudeProfile(profile, l, w, h, center, ctx);
}

function handleIProfile(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const w = getArgNum(stmt.args, 'w', 0, ctx, 6);
  const h = getArgNum(stmt.args, 'h', 1, ctx, 10);
  let t = getArgNum(stmt.args, 't', 2, ctx, 1);
  const twArg = getArgNum(stmt.args, 'tw', -1, ctx, -1);
  const tw = twArg >= 0 ? twArg : t;
  const l = getArgNum(stmt.args, 'l', -1, ctx, 10);
  const filletInner = getArgNum(stmt.args, 'fillet', -1, ctx, 0);
  const chamferInner = getArgNum(stmt.args, 'chamfer', -1, ctx, 0);
  const center = getArgBool(stmt.args, 'center', -1, ctx, false);
  const fn = getArgNum(stmt.args, '$fn', -1, ctx, ctx.fn);
  const arcSegs = Math.max(Math.round(fn / 4), 4);

  // Fillet takes priority over chamfer
  let bevel = filletInner > 0 ? filletInner : chamferInner;
  const bevelType = filletInner > 0 ? 'fillet' : 'chamfer';

  // Clamping
  t = Math.min(t, h * 0.25);
  const twClamped = Math.min(tw, w * 0.99);
  bevel = Math.min(bevel, Math.min((w - twClamped) / 2, (h - 2 * t) / 2) * 0.99);

  // I-profile: bottom flange + web + top flange
  const webLeft = (w - twClamped) / 2;
  const webRight = (w + twClamped) / 2;
  const botFlangeTop = t;
  const topFlangeBot = h - t;

  const profile: Vec2[] = [];

  // Bottom flange: BL → BR
  profile.push([0, 0]);
  profile.push([w, 0]);
  profile.push([w, botFlangeTop]);

  // Bottom-right inner corner
  if (bevel > 0) {
    if (bevelType === 'fillet') {
      const arcPts = generateArcPoints(webRight + bevel, botFlangeTop + bevel, bevel, -Math.PI / 2, -Math.PI, arcSegs);
      for (const p of arcPts) profile.push(p);
    } else {
      profile.push([webRight + bevel, botFlangeTop]);
      profile.push([webRight, botFlangeTop + bevel]);
    }
  } else {
    profile.push([webRight, botFlangeTop]);
  }

  // Web right side up
  // Top-right inner corner
  if (bevel > 0) {
    profile.push([webRight, topFlangeBot - bevel]);
    if (bevelType === 'fillet') {
      const arcPts = generateArcPoints(webRight + bevel, topFlangeBot - bevel, bevel, Math.PI, Math.PI / 2, arcSegs);
      for (const p of arcPts) profile.push(p);
    } else {
      profile.push([webRight + bevel, topFlangeBot]);
    }
  } else {
    profile.push([webRight, topFlangeBot]);
  }

  // Top flange: right → left
  profile.push([w, topFlangeBot]);
  profile.push([w, h]);
  profile.push([0, h]);
  profile.push([0, topFlangeBot]);

  // Top-left inner corner
  if (bevel > 0) {
    if (bevelType === 'fillet') {
      const arcPts = generateArcPoints(webLeft - bevel, topFlangeBot - bevel, bevel, Math.PI / 2, 0, arcSegs);
      for (const p of arcPts) profile.push(p);
    } else {
      profile.push([webLeft - bevel, topFlangeBot]);
    }
    profile.push([webLeft, topFlangeBot - bevel]);
  } else {
    profile.push([webLeft, topFlangeBot]);
  }

  // Web left side down
  // Bottom-left inner corner
  if (bevel > 0) {
    profile.push([webLeft, botFlangeTop + bevel]);
    if (bevelType === 'fillet') {
      const arcPts = generateArcPoints(webLeft - bevel, botFlangeTop + bevel, bevel, 0, -Math.PI / 2, arcSegs);
      for (const p of arcPts) profile.push(p);
    } else {
      profile.push([webLeft - bevel, botFlangeTop]);
    }
  } else {
    profile.push([webLeft, botFlangeTop]);
  }

  profile.push([0, botFlangeTop]);

  return extrudeProfile(profile, l, w, h, center, ctx);
}

function handleBoolean(op: 'union' | 'difference' | 'intersection'): ModuleHandler {
  return (stmt, ctx) => {
    const children: Manifold[] = [];
    const childParts: GeometryPart[] = [];
    for (const child of stmt.children) {
      const m = evalStatement(child, ctx);
      if (m) {
        children.push(m);
        const part: GeometryPart = { manifold: m, stmt: child, from: child.from, to: child.to };
        const childColor = ctx.manifoldColors.get(m);
        if (childColor) {
          part.color = childColor.color;
          part.opacity = childColor.opacity;
        }
        childParts.push(part);
      }
    }

    // Store child parts for later retrieval by the top-level loop
    if (childParts.length > 1) {
      ctx.booleanChildParts.set(stmt, registerGroup(ctx, { kind: 'bool', op, parts: childParts }));
    }
    const ownGroup = childParts.length > 1 ? ctx.booleanChildParts.get(stmt) as GhostGroup : undefined;

    if (children.length === 0) return null;
    if (children.length === 1) return children[0];

    let result = children[0];
    for (let i = 1; i < children.length; i++) {
      let next: Manifold;
      switch (op) {
        case 'union': next = result.add(children[i]); break;
        case 'difference': next = result.subtract(children[i]); break;
        case 'intersection': next = result.intersect(children[i]); break;
      }
      trackIntermediate(ctx, next);
      result = next;
    }
    inheritColor(ctx, result, op, children);
    if (op === 'union' && ownGroup) ctx.subGroupOf.set(result, ownGroup);
    return result;
  };
}

/** Position in the ghost-group registry before a transform evaluates its
 *  children — every group created after it is "inside" the transform
 *  (including module instances whose map key a later call overwrote). */
interface PartKeysSnapshot {
  groups: number;
}

function snapshotPartKeys(ctx: EvalContext): PartKeysSnapshot {
  return { groups: ctx.groups.length };
}

/**
 * Apply a transform to all ghost parts of groups created during evalChildren
 * (composes into each part's matrix — no new manifolds). Keeps selection
 * highlights aligned with the rendered geometry when outer transforms wrap
 * statements with attach, boolean/group or module-call children.
 */
function transformNewChildParts(ctx: EvalContext, keysBefore: PartKeysSnapshot, m4: Mat4): void {
  for (const g of groupsSince(ctx, keysBefore.groups)) transformGroupParts(g.parts, m4);
}

/** Pre-multiply each part's ghost matrix by `m4` (replaces part objects). */
function transformGroupParts(parts: GeometryPart[], m4: Mat4): void {
  for (let i = 0; i < parts.length; i++) {
    parts[i] = { ...parts[i], matrix: mat4Mul(m4, parts[i].matrix ?? MAT4_IDENTITY) };
  }
}

// --- Column-major 4×4 helpers for ghost matrices (SCAD coordinates) ---
type Mat4 = number[];
const MAT4_IDENTITY: Mat4 = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
function mat4Mul(a: Mat4, b: Mat4): Mat4 {
  const r = new Array(16).fill(0);
  for (let c = 0; c < 4; c++) for (let row = 0; row < 4; row++) {
    let v = 0;
    for (let k = 0; k < 4; k++) v += a[k * 4 + row] * b[c * 4 + k];
    r[c * 4 + row] = v;
  }
  return r;
}
const mat4Translate = (v: Vec3): Mat4 => [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, v[0], v[1], v[2], 1];
const mat4Scale = (v: Vec3): Mat4 => [v[0], 0, 0, 0, 0, v[1], 0, 0, 0, 0, v[2], 0, 0, 0, 0, 1];
/** Same convention as Manifold.rotate([x, y, z]): X first, then Y, then Z (degrees). */
function mat4EulerDeg(v: Vec3): Mat4 {
  const [x, y, z] = v.map(d => (d * Math.PI) / 180);
  const rx: Mat4 = [1, 0, 0, 0, 0, Math.cos(x), Math.sin(x), 0, 0, -Math.sin(x), Math.cos(x), 0, 0, 0, 0, 1];
  const ry: Mat4 = [Math.cos(y), 0, -Math.sin(y), 0, 0, 1, 0, 0, Math.sin(y), 0, Math.cos(y), 0, 0, 0, 0, 1];
  const rz: Mat4 = [Math.cos(z), Math.sin(z), 0, 0, -Math.sin(z), Math.cos(z), 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
  return mat4Mul(rz, mat4Mul(ry, rx));
}
function mat4Mirror(n: Vec3): Mat4 {
  const l2 = n[0] * n[0] + n[1] * n[1] + n[2] * n[2];
  if (l2 < 1e-12) return MAT4_IDENTITY;
  const m: Mat4 = [...MAT4_IDENTITY];
  for (let c = 0; c < 3; c++) for (let r = 0; r < 3; r++) m[c * 4 + r] -= (2 * n[r] * n[c]) / l2;
  return m;
}

/** A ghost part's mesh in world space (applies part.matrix to the vertices). */
export function partWorldMesh(part: GeometryPart): { vertProperties: Float32Array; triVerts: Uint32Array; numProp: number } {
  const mesh = part.manifold.getMesh();
  const m = part.matrix;
  if (!m) return mesh;
  const src = mesh.vertProperties;
  const np = mesh.numProp;
  const out = new Float32Array(src);
  for (let i = 0; i < src.length; i += np) {
    const x = src[i], y = src[i + 1], z = src[i + 2];
    out[i] = m[0] * x + m[4] * y + m[8] * z + m[12];
    out[i + 1] = m[1] * x + m[5] * y + m[9] * z + m[13];
    out[i + 2] = m[2] * x + m[6] * y + m[10] * z + m[14];
  }
  return { vertProperties: out, triVerts: mesh.triVerts, numProp: np };
}

/** World-space bounding box of a part (ghost matrix applied). */
export function partWorldBounds(part: GeometryPart): { min: Vec3; max: Vec3 } {
  if (!part.matrix) {
    const b = part.manifold.boundingBox();
    return { min: [b.min[0], b.min[1], b.min[2]], max: [b.max[0], b.max[1], b.max[2]] };
  }
  const { vertProperties: v, numProp: np } = partWorldMesh(part);
  const min: Vec3 = [Infinity, Infinity, Infinity], max: Vec3 = [-Infinity, -Infinity, -Infinity];
  for (let i = 0; i < v.length; i += np) for (let k = 0; k < 3; k++) {
    if (v[i + k] < min[k]) min[k] = v[i + k];
    if (v[i + k] > max[k]) max[k] = v[i + k];
  }
  return { min, max };
}

function translateNewFrameBoxes(ctx: EvalContext, startIndex: number, vec: Vec3): void {
  for (let i = startIndex; i < ctx.frameBoxes.length; i++) {
    const fb = ctx.frameBoxes[i];
    fb.min = [fb.min[0] + vec[0], fb.min[1] + vec[1], fb.min[2] + vec[2]];
    fb.max = [fb.max[0] + vec[0], fb.max[1] + vec[1], fb.max[2] + vec[2]];
  }
}

function scaleNewFrameBoxes(ctx: EvalContext, startIndex: number, vec: Vec3): void {
  for (let i = startIndex; i < ctx.frameBoxes.length; i++) {
    const fb = ctx.frameBoxes[i];
    const a: Vec3 = [fb.min[0] * vec[0], fb.min[1] * vec[1], fb.min[2] * vec[2]];
    const b: Vec3 = [fb.max[0] * vec[0], fb.max[1] * vec[1], fb.max[2] * vec[2]];
    fb.min = [Math.min(a[0], b[0]), Math.min(a[1], b[1]), Math.min(a[2], b[2])];
    fb.max = [Math.max(a[0], b[0]), Math.max(a[1], b[1]), Math.max(a[2], b[2])];
  }
}

function handleTranslate(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const vec = getArgVec3(stmt.args, 'v', 0, ctx, [0, 0, 0]);
  const keysBefore = snapshotPartKeys(ctx);
  const frameBoxesBefore = ctx.frameBoxes.length;
  const child = evalChildren(stmt.children, ctx);
  if (!child) return null;
  const m = child.translate(vec);
  trackIntermediate(ctx, m);
  // Forward color tag from child to the new manifold
  const childColor = ctx.manifoldColors.get(child);
  if (childColor) ctx.manifoldColors.set(m, childColor);
  forwardSubGroup(ctx, child, m);
  transformNewChildParts(ctx, keysBefore, mat4Translate(vec));
  translateNewFrameBoxes(ctx, frameBoxesBefore, vec);
  // Propagate phantomBBox through translate so outer attach uses correct frame bbox
  if (ctx.phantomBBox) {
    ctx.phantomBBox = {
      min: [ctx.phantomBBox.min[0] + vec[0], ctx.phantomBBox.min[1] + vec[1], ctx.phantomBBox.min[2] + vec[2]],
      max: [ctx.phantomBBox.max[0] + vec[0], ctx.phantomBBox.max[1] + vec[1], ctx.phantomBBox.max[2] + vec[2]],
    };
  }
  return m;
}

function handleRotate(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const aArg = getArg(stmt.args, 'a', 0, ctx);
  // Axis-angle form: rotate(a=<scalar>, v=[x,y,z])
  if (typeof aArg === 'number') {
    const axis = getArgVec3(stmt.args, 'v', 1, ctx, [0, 0, 1]);
    const len = Math.sqrt(axis[0] * axis[0] + axis[1] * axis[1] + axis[2] * axis[2]);
    const keysBefore = snapshotPartKeys(ctx);
    const child = evalChildren(stmt.children, ctx);
    if (!child) return null;
    if (len < 1e-12) return child; // degenerate axis → no rotation
    const normAxis: Vec3 = [axis[0] / len, axis[1] / len, axis[2] / len];
    const mat4 = mat3ToMat4Transform(mat3FromAxisAngle(normAxis, aArg * Math.PI / 180));
    const m = child.transform(mat4);
    trackIntermediate(ctx, m);
    const cc1 = ctx.manifoldColors.get(child);
    if (cc1) ctx.manifoldColors.set(m, cc1);
    forwardSubGroup(ctx, child, m);
    transformNewChildParts(ctx, keysBefore, mat4);
    return m;
  }
  // Euler form: rotate([x,y,z]) or rotate(a=[x,y,z])
  const vec = getArgVec3(stmt.args, 'a', 0, ctx, [0, 0, 0]);
  const keysBefore = snapshotPartKeys(ctx);
  const child = evalChildren(stmt.children, ctx);
  if (!child) return null;
  const m = child.rotate(vec);
  trackIntermediate(ctx, m);
  const cc2 = ctx.manifoldColors.get(child);
  if (cc2) ctx.manifoldColors.set(m, cc2);
  forwardSubGroup(ctx, child, m);
  transformNewChildParts(ctx, keysBefore, mat4EulerDeg(vec));
  return m;
}

/**
 * multmatrix(m) — arbitrary affine transform. `m` is a 4×4 (or 3×4) row-major
 * matrix as in OpenSCAD; the bottom row is ignored (no projective transforms).
 */
function handleMultmatrix(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const mArg = getArg(stmt.args, 'm', 0, ctx);
  const rows = Array.isArray(mArg) ? mArg.slice(0, 3) : [];
  const ok = rows.length === 3 && rows.every(r => Array.isArray(r) && r.length >= 3 && r.slice(0, 4).every(v => typeof v === 'number' && Number.isFinite(v)));
  const keysBefore = snapshotPartKeys(ctx);
  const child = evalChildren(stmt.children, ctx);
  if (!child) return null;
  if (!ok) {
    evalError(ctx, 'multmatrix() expects a 4×4 (or 3×4) numeric matrix', stmt.from, stmt.to);
    return child;
  }
  const r = rows as number[][];
  // Manifold.transform() takes a column-major 4×4 (bottom row ignored)
  const mat4: [number, number, number, number, number, number, number, number, number, number, number, number, number, number, number, number] = [
    r[0][0], r[1][0], r[2][0], 0,
    r[0][1], r[1][1], r[2][1], 0,
    r[0][2], r[1][2], r[2][2], 0,
    r[0][3] ?? 0, r[1][3] ?? 0, r[2][3] ?? 0, 1,
  ];
  const m = child.transform(mat4);
  trackIntermediate(ctx, m);
  const cc = ctx.manifoldColors.get(child);
  if (cc) ctx.manifoldColors.set(m, cc);
  forwardSubGroup(ctx, child, m);
  transformNewChildParts(ctx, keysBefore, mat4);
  return m;
}

function handleScale(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const vec = getArgVec3(stmt.args, 'v', 0, ctx, [1, 1, 1]);
  const keysBefore = snapshotPartKeys(ctx);
  const frameBoxesBefore = ctx.frameBoxes.length;
  const child = evalChildren(stmt.children, ctx);
  if (!child) return null;
  const m = child.scale(vec);
  trackIntermediate(ctx, m);
  const childColor = ctx.manifoldColors.get(child);
  if (childColor) ctx.manifoldColors.set(m, childColor);
  forwardSubGroup(ctx, child, m);
  transformNewChildParts(ctx, keysBefore, mat4Scale(vec));
  scaleNewFrameBoxes(ctx, frameBoxesBefore, vec);
  // Propagate phantomBBox through scale so outer attach uses correct frame bbox
  if (ctx.phantomBBox) {
    const pb = ctx.phantomBBox;
    const a: Vec3 = [pb.min[0] * vec[0], pb.min[1] * vec[1], pb.min[2] * vec[2]];
    const b: Vec3 = [pb.max[0] * vec[0], pb.max[1] * vec[1], pb.max[2] * vec[2]];
    ctx.phantomBBox = {
      min: [Math.min(a[0], b[0]), Math.min(a[1], b[1]), Math.min(a[2], b[2])],
      max: [Math.max(a[0], b[0]), Math.max(a[1], b[1]), Math.max(a[2], b[2])],
    };
  }
  return m;
}

function handleMirror(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const normal = getArgVec3(stmt.args, 'v', 0, ctx, [1, 0, 0]);
  const keysBefore = snapshotPartKeys(ctx);
  const frameBoxesBefore = ctx.frameBoxes.length;
  const child = evalChildren(stmt.children, ctx);
  if (!child) return null;
  const m = child.mirror(normal);
  trackIntermediate(ctx, m);
  const childColor = ctx.manifoldColors.get(child);
  if (childColor) ctx.manifoldColors.set(m, childColor);
  forwardSubGroup(ctx, child, m);
  transformNewChildParts(ctx, keysBefore, mat4Mirror(normal));
  // Mirror is equivalent to negating one axis for frame boxes
  // Compute a scale vector that negates the mirrored axis
  const len = Math.sqrt(normal[0] * normal[0] + normal[1] * normal[1] + normal[2] * normal[2]);
  if (len > 0) {
    const nx = normal[0] / len, ny = normal[1] / len, nz = normal[2] / len;
    // Reflection matrix diagonal: 1 - 2*n_i^2 (off-diag terms handled by min/max swap)
    const sx = 1 - 2 * nx * nx, sy = 1 - 2 * ny * ny, sz = 1 - 2 * nz * nz;
    // For axis-aligned mirrors, scale works perfectly. For arbitrary normals,
    // use the bounding box min/max swap approach.
    for (let i = frameBoxesBefore; i < ctx.frameBoxes.length; i++) {
      const fb = ctx.frameBoxes[i];
      const a: Vec3 = [fb.min[0] * sx, fb.min[1] * sy, fb.min[2] * sz];
      const b: Vec3 = [fb.max[0] * sx, fb.max[1] * sy, fb.max[2] * sz];
      fb.min = [Math.min(a[0], b[0]), Math.min(a[1], b[1]), Math.min(a[2], b[2])];
      fb.max = [Math.max(a[0], b[0]), Math.max(a[1], b[1]), Math.max(a[2], b[2])];
    }
  }
  if (ctx.phantomBBox) {
    const pb = ctx.phantomBBox;
    const nl = Math.sqrt(normal[0] * normal[0] + normal[1] * normal[1] + normal[2] * normal[2]);
    if (nl > 0) {
      const nx = normal[0] / nl, ny = normal[1] / nl, nz = normal[2] / nl;
      const sx = 1 - 2 * nx * nx, sy = 1 - 2 * ny * ny, sz = 1 - 2 * nz * nz;
      const a: Vec3 = [pb.min[0] * sx, pb.min[1] * sy, pb.min[2] * sz];
      const b: Vec3 = [pb.max[0] * sx, pb.max[1] * sy, pb.max[2] * sz];
      ctx.phantomBBox = {
        min: [Math.min(a[0], b[0]), Math.min(a[1], b[1]), Math.min(a[2], b[2])],
        max: [Math.max(a[0], b[0]), Math.max(a[1], b[1]), Math.max(a[2], b[2])],
      };
    }
  }
  return m;
}

function handleResize(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const newSize = getArgVec3(stmt.args, 'newsize', 0, ctx, [0, 0, 0]);
  const autoArg = getArg(stmt.args, 'auto', 1, ctx);

  const keysBefore = snapshotPartKeys(ctx);
  const frameBoxesBefore = ctx.frameBoxes.length;
  const child = evalChildren(stmt.children, ctx);
  if (!child) return null;

  const bbox = child.boundingBox();
  const curSize: Vec3 = [
    bbox.max[0] - bbox.min[0],
    bbox.max[1] - bbox.min[1],
    bbox.max[2] - bbox.min[2],
  ];

  // Parse auto parameter: true, false, or [bool, bool, bool]
  let autoFlags: [boolean, boolean, boolean] = [false, false, false];
  if (autoArg !== undefined) {
    if (Array.isArray(autoArg)) {
      autoFlags = [!!autoArg[0], !!autoArg[1], !!autoArg[2]];
    } else if (autoArg) {
      autoFlags = [true, true, true];
    }
  }

  // Compute scale for explicitly specified dimensions
  const scaleVec: Vec3 = [1, 1, 1];
  let firstSpecifiedScale = 1;
  let foundFirst = false;
  for (let i = 0; i < 3; i++) {
    if (newSize[i] !== 0 && curSize[i] > 1e-10) {
      const s = newSize[i] / curSize[i];
      scaleVec[i] = s;
      if (!foundFirst) { firstSpecifiedScale = s; foundFirst = true; }
    }
  }

  // Auto: fill unspecified dimensions with the first specified scale
  if (foundFirst) {
    for (let i = 0; i < 3; i++) {
      if (newSize[i] === 0 && autoFlags[i]) {
        scaleVec[i] = firstSpecifiedScale;
      }
    }
  }

  const m = child.scale(scaleVec);
  trackIntermediate(ctx, m);
  transformNewChildParts(ctx, keysBefore, mat4Scale(scaleVec));
  scaleNewFrameBoxes(ctx, frameBoxesBefore, scaleVec);
  if (ctx.phantomBBox) {
    const pb = ctx.phantomBBox;
    const a: Vec3 = [pb.min[0] * scaleVec[0], pb.min[1] * scaleVec[1], pb.min[2] * scaleVec[2]];
    const b: Vec3 = [pb.max[0] * scaleVec[0], pb.max[1] * scaleVec[1], pb.max[2] * scaleVec[2]];
    ctx.phantomBBox = {
      min: [Math.min(a[0], b[0]), Math.min(a[1], b[1]), Math.min(a[2], b[2])],
      max: [Math.max(a[0], b[0]), Math.max(a[1], b[1]), Math.max(a[2], b[2])],
    };
  }
  return m;
}

// Named colors map (CSS color names to RGB 0-1)
const NAMED_COLORS: Record<string, [number, number, number]> = {
  // Basic colors
  red: [1, 0, 0], green: [0, 0.5, 0], blue: [0, 0, 1], yellow: [1, 1, 0],
  cyan: [0, 1, 1], magenta: [1, 0, 1], white: [1, 1, 1], black: [0, 0, 0],
  orange: [1, 0.65, 0], pink: [1, 0.75, 0.8], purple: [0.5, 0, 0.5],
  gray: [0.5, 0.5, 0.5], grey: [0.5, 0.5, 0.5], brown: [0.65, 0.16, 0.16],
  // Reds / Pinks
  indianred: [0.804, 0.361, 0.361], lightcoral: [0.941, 0.502, 0.502],
  salmon: [0.980, 0.502, 0.447], darksalmon: [0.914, 0.588, 0.478],
  lightsalmon: [1, 0.627, 0.478], crimson: [0.863, 0.078, 0.235],
  darkred: [0.545, 0, 0], firebrick: [0.698, 0.133, 0.133],
  hotpink: [1, 0.412, 0.706], deeppink: [1, 0.078, 0.576],
  mediumvioletred: [0.780, 0.082, 0.522], palevioletred: [0.859, 0.439, 0.576],
  lightpink: [1, 0.714, 0.757], mistyrose: [1, 0.894, 0.882],
  // Oranges
  coral: [1, 0.498, 0.314], tomato: [1, 0.388, 0.278],
  orangered: [1, 0.271, 0], darkorange: [1, 0.549, 0],
  gold: [1, 0.843, 0], peachpuff: [1, 0.855, 0.725],
  // Yellows
  lightyellow: [1, 1, 0.878], lemonchiffon: [1, 0.980, 0.804],
  papayawhip: [1, 0.937, 0.835], moccasin: [1, 0.894, 0.710],
  khaki: [0.941, 0.902, 0.549], darkkhaki: [0.741, 0.718, 0.420],
  // Greens
  lawngreen: [0.486, 0.988, 0], chartreuse: [0.498, 1, 0],
  limegreen: [0.196, 0.804, 0.196], lime: [0, 1, 0],
  forestgreen: [0.133, 0.545, 0.133], darkgreen: [0, 0.392, 0],
  greenyellow: [0.678, 1, 0.184], yellowgreen: [0.604, 0.804, 0.196],
  springgreen: [0, 1, 0.498], mediumspringgreen: [0, 0.980, 0.604],
  lightgreen: [0.565, 0.933, 0.565], palegreen: [0.596, 0.984, 0.596],
  darkseagreen: [0.561, 0.737, 0.561], mediumseagreen: [0.235, 0.702, 0.443],
  seagreen: [0.180, 0.545, 0.341], olive: [0.502, 0.502, 0],
  darkolivegreen: [0.333, 0.420, 0.184], olivedrab: [0.420, 0.557, 0.137],
  // Cyans / Teals
  lightcyan: [0.878, 1, 1], aqua: [0, 1, 1], aquamarine: [0.498, 1, 0.831],
  mediumaquamarine: [0.400, 0.804, 0.667], paleturquoise: [0.686, 0.933, 0.933],
  turquoise: [0.251, 0.878, 0.816], mediumturquoise: [0.282, 0.820, 0.800],
  darkturquoise: [0, 0.808, 0.820], lightseagreen: [0.125, 0.698, 0.667],
  cadetblue: [0.373, 0.620, 0.627], darkcyan: [0, 0.545, 0.545],
  teal: [0, 0.502, 0.502],
  // Blues
  powderblue: [0.690, 0.878, 0.902], lightblue: [0.678, 0.847, 0.902],
  lightskyblue: [0.529, 0.808, 0.980], skyblue: [0.529, 0.808, 0.922],
  deepskyblue: [0, 0.749, 1], lightsteelblue: [0.690, 0.769, 0.871],
  dodgerblue: [0.118, 0.565, 1], cornflowerblue: [0.392, 0.584, 0.929],
  steelblue: [0.275, 0.510, 0.706], royalblue: [0.255, 0.412, 0.882],
  mediumblue: [0, 0, 0.804], darkblue: [0, 0, 0.545],
  navy: [0, 0, 0.502], midnightblue: [0.098, 0.098, 0.439],
  // Purples
  lavender: [0.902, 0.902, 0.980], thistle: [0.847, 0.749, 0.847],
  plum: [0.867, 0.627, 0.867], violet: [0.933, 0.510, 0.933],
  orchid: [0.855, 0.439, 0.839], fuchsia: [1, 0, 1],
  mediumorchid: [0.729, 0.333, 0.827], mediumpurple: [0.576, 0.439, 0.859],
  rebeccapurple: [0.400, 0.200, 0.600], blueviolet: [0.541, 0.169, 0.886],
  darkviolet: [0.580, 0, 0.827], darkorchid: [0.600, 0.196, 0.800],
  darkmagenta: [0.545, 0, 0.545], indigo: [0.294, 0, 0.510],
  slateblue: [0.416, 0.353, 0.804], darkslateblue: [0.282, 0.239, 0.545],
  mediumslateblue: [0.482, 0.408, 0.933],
  // Grays
  gainsboro: [0.863, 0.863, 0.863], lightgray: [0.827, 0.827, 0.827],
  lightgrey: [0.827, 0.827, 0.827], silver: [0.753, 0.753, 0.753],
  darkgray: [0.663, 0.663, 0.663], darkgrey: [0.663, 0.663, 0.663],
  dimgray: [0.412, 0.412, 0.412], dimgrey: [0.412, 0.412, 0.412],
  lightslategray: [0.467, 0.533, 0.600], lightslategrey: [0.467, 0.533, 0.600],
  slategray: [0.439, 0.502, 0.565], slategrey: [0.439, 0.502, 0.565],
  darkslategray: [0.184, 0.310, 0.310], darkslategrey: [0.184, 0.310, 0.310],
  // Whites / Beiges
  snow: [1, 0.980, 0.980], honeydew: [0.941, 1, 0.941],
  mintcream: [0.961, 1, 0.980], azure: [0.941, 1, 1],
  aliceblue: [0.941, 0.973, 1], ghostwhite: [0.973, 0.973, 1],
  whitesmoke: [0.961, 0.961, 0.961], seashell: [1, 0.961, 0.933],
  beige: [0.961, 0.961, 0.863], oldlace: [0.992, 0.961, 0.902],
  floralwhite: [1, 0.980, 0.941], ivory: [1, 1, 0.941],
  antiquewhite: [0.980, 0.922, 0.843], linen: [0.980, 0.941, 0.902],
  lavenderblush: [1, 0.941, 0.961], cornsilk: [1, 0.973, 0.863],
  // Browns
  maroon: [0.502, 0, 0], saddlebrown: [0.545, 0.271, 0.075],
  sienna: [0.627, 0.322, 0.176], chocolate: [0.824, 0.412, 0.118],
  darkgoldenrod: [0.722, 0.525, 0.043], goldenrod: [0.855, 0.647, 0.125],
  peru: [0.804, 0.522, 0.247], rosybrown: [0.737, 0.561, 0.561],
  sandybrown: [0.957, 0.643, 0.376], burlywood: [0.871, 0.722, 0.529],
  tan: [0.824, 0.706, 0.549], wheat: [0.961, 0.871, 0.702],
  navajowhite: [1, 0.871, 0.678], bisque: [1, 0.894, 0.769],
  blanchedalmond: [1, 0.922, 0.804],
};

function parseColor(arg: unknown): [number, number, number] | null {
  if (typeof arg === 'string') {
    // Named color
    const lc = arg.toLowerCase();
    if (NAMED_COLORS[lc]) return NAMED_COLORS[lc];
    // Hex color #RRGGBB or #RGB
    if (arg.startsWith('#')) {
      let hex = arg.slice(1);
      if (hex.length === 3) hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
      if (hex.length === 6) {
        const r = parseInt(hex.slice(0, 2), 16) / 255;
        const g = parseInt(hex.slice(2, 4), 16) / 255;
        const b = parseInt(hex.slice(4, 6), 16) / 255;
        return [r, g, b];
      }
    }
    return null;
  }
  if (Array.isArray(arg) && arg.length >= 3) {
    // RGB array - values can be 0-1 or 0-255
    let [r, g, b] = arg as number[];
    // If any value > 1, assume 0-255 range
    if (r > 1 || g > 1 || b > 1) {
      r = r / 255; g = g / 255; b = b / 255;
    }
    return [Math.min(1, Math.max(0, r)), Math.min(1, Math.max(0, g)), Math.min(1, Math.max(0, b))];
  }
  return null;
}

function handleColor(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const colorArg = getArg(stmt.args, 'c', 0, ctx);
  const alphaArg = getArg(stmt.args, 'alpha', 1, ctx) as number | undefined;

  const parsedColor = parseColor(colorArg);
  const opacity = typeof alphaArg === 'number' ? Math.min(1, Math.max(0, alphaArg)) : undefined;

  // Save previous color/opacity
  const prevColor = ctx.currentColor;
  const prevOpacity = ctx.currentOpacity;

  // Set new color/opacity for children
  if (parsedColor) ctx.currentColor = parsedColor;
  if (opacity !== undefined) ctx.currentOpacity = opacity;

  const child = evalChildren(stmt.children, ctx);

  // Restore previous color/opacity
  ctx.currentColor = prevColor;
  ctx.currentOpacity = prevOpacity;

  return child;
}

function handleHull(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const children: Manifold[] = [];
  for (const child of stmt.children) {
    const m = evalStatement(child, ctx);
    if (m) children.push(m);
  }
  if (children.length === 0) return null;
  if (children.length === 1) {
    const result = children[0].hull();
    trackIntermediate(ctx, result);
    return result;
  }
  // Manifold.hull() accepts an array of Manifold | Vec3
  const result = ctx.wasm.Manifold.hull(children);
  trackIntermediate(ctx, result);
  return result;
}

function handleMinkowski(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const children: Manifold[] = [];
  for (const child of stmt.children) {
    const m = evalStatement(child, ctx);
    if (m) children.push(m);
  }
  if (children.length < 2) {
    if (children.length === 1) return children[0];
    return null;
  }

  // Approximate Minkowski sum via pairwise vertex sums → convex hull.
  // Exact when at least one operand is convex (the common case: rounding with a sphere).
  const meshA = children[0].getMesh();
  const meshB = children[1].getMesh();

  const vpA = meshA.vertProperties;
  const npA = meshA.numProp;
  const numVertA = vpA.length / npA;

  const vpB = meshB.vertProperties;
  const npB = meshB.numProp;
  const numVertB = vpB.length / npB;

  const points: Vec3[] = [];
  for (let i = 0; i < numVertA; i++) {
    const ax = vpA[i * npA];
    const ay = vpA[i * npA + 1];
    const az = vpA[i * npA + 2];
    for (let j = 0; j < numVertB; j++) {
      points.push([
        ax + vpB[j * npB],
        ay + vpB[j * npB + 1],
        az + vpB[j * npB + 2],
      ]);
    }
  }

  const result = ctx.wasm.Manifold.hull(points);
  trackIntermediate(ctx, result);
  return result;
}

function handleChildrenModule(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  if (ctx.callerChildren.length === 0) return null;
  return evalChildren(ctx.callerChildren, ctx);
}

// ====================================================================
// 2D Evaluation (for linear_extrude children)
// ====================================================================

type Module2DHandler = (stmt: ModuleCallStatement, ctx: EvalContext, cleanup: CrossSection[]) => CrossSection | null;

function evalChildren2D(children: Statement[], ctx: EvalContext, cleanup: CrossSection[]): CrossSection | null {
  hoistDefinitions(children, ctx);
  const results: CrossSection[] = [];
  for (const child of children) {
    const cs = evalStatement2D(child, ctx, cleanup);
    if (cs) results.push(cs);
  }
  if (results.length === 0) return null;
  if (results.length === 1) return results[0];
  // Implicit union
  let combined = results[0];
  for (let i = 1; i < results.length; i++) {
    const next = combined.add(results[i]);
    cleanup.push(next);
    combined = next;
  }
  return combined;
}

function evalForStatement2D(stmt: ForStatement, ctx: EvalContext, cleanup: CrossSection[]): CrossSection | null {
  const rangeVal = evalExpr(stmt.range, ctx);
  if (!Array.isArray(rangeVal)) {
    evalError(ctx, 'for() range must be an array or range expression', stmt.from, stmt.to);
    return null;
  }
  if (rangeVal.length > MAX_LOOP_ITERATIONS) {
    evalError(ctx, `for() loop exceeds maximum iterations (${MAX_LOOP_ITERATIONS})`, stmt.from, stmt.to);
    console.error(`SCAD Error: for() loop exceeds maximum iterations (${MAX_LOOP_ITERATIONS})`);
    return null;
  }
  const results: CrossSection[] = [];
  const prevVars = ctx.vars;
  for (const item of rangeVal) {
    const childVars = new Map(prevVars);
    childVars.set(stmt.variable, item);
    ctx.vars = childVars;
    for (const child of stmt.body) {
      const cs = evalStatement2D(child, ctx, cleanup);
      if (cs) results.push(cs);
    }
  }
  ctx.vars = prevVars;
  if (results.length === 0) return null;
  if (results.length === 1) return results[0];
  let combined = results[0];
  for (let i = 1; i < results.length; i++) {
    const next = combined.add(results[i]);
    cleanup.push(next);
    combined = next;
  }
  return combined;
}

function evalStatement2D(stmt: Statement, ctx: EvalContext, cleanup: CrossSection[]): CrossSection | null {
  checkTimeout(ctx);
  if (stmt.type === 'AssignmentStatement') {
    const val = evalExpr(stmt.value, ctx);
    ctx.vars.set(stmt.name, val);
    return null;
  }

  if (stmt.type === 'ModuleDefinitionStatement') {
    ctx.modules.set(stmt.name, { params: stmt.params, body: stmt.body });
    return null;
  }

  if (stmt.type === 'FunctionDefinitionStatement') {
    ctx.functions.set(stmt.name, { params: stmt.params, body: stmt.body });
    return null;
  }

  if (stmt.type === 'ForStatement') {
    return evalForStatement2D(stmt, ctx, cleanup);
  }

  if (stmt.type === 'IfStatement') {
    const cond = evalExpr(stmt.condition, ctx);
    const body = cond ? stmt.thenBody : stmt.elseBody;
    return evalChildren2D(body, ctx, cleanup);
  }

  if (stmt.type === 'ModuleCallStatement') {
    // User-defined modules in 2D context
    const userMod = ctx.modules.get(stmt.name);
    if (userMod) {
      return callUserModule2D(userMod, stmt, ctx, cleanup);
    }

    const handler = MODULE_HANDLERS_2D[stmt.name];
    if (handler) return handler(stmt, ctx, cleanup);

    unknownNameError(ctx, 'Unknown module', stmt.name,
      [...Object.keys(MODULE_HANDLERS_2D), ...Object.keys(MODULE_HANDLERS), ...ctx.modules.keys()],
      ...callNameRange(stmt), 'warning');
    return null;
  }

  return null;
}

function callUserModule2D(
  mod: UserModule,
  call: ModuleCallStatement,
  ctx: EvalContext,
  cleanup: CrossSection[],
): CrossSection | null {
  // Check recursion depth
  if (ctx.recursionDepth >= MAX_RECURSION_DEPTH) {
    evalError(ctx, `Maximum recursion depth (${MAX_RECURSION_DEPTH}) exceeded`, call.from, call.to);
    console.error(`SCAD Error: Maximum recursion depth (${MAX_RECURSION_DEPTH}) exceeded at module '${call.name}'`);
    return null;
  }
  ctx.recursionDepth++;

  const prevVars = ctx.vars;
  const prevCallerChildren = ctx.callerChildren;
  const newVars = new Map(prevVars);

  for (let i = 0; i < mod.params.length; i++) {
    const param = mod.params[i];
    const arg = getArg(call.args, param.name, i, ctx);
    if (arg !== undefined) {
      newVars.set(param.name, arg);
    } else if (param.defaultValue) {
      newVars.set(param.name, evalExpr(param.defaultValue, ctx));
    }
  }

  ctx.vars = newVars;
  ctx.callerChildren = call.children;
  const result = evalChildren2D(mod.body, ctx, cleanup);
  ctx.vars = prevVars;
  ctx.callerChildren = prevCallerChildren;
  ctx.recursionDepth--;
  return result;
}

// --- 2D Primitives ---

function handleCircle2D(stmt: ModuleCallStatement, ctx: EvalContext, cleanup: CrossSection[]): CrossSection | null {
  const { CrossSection } = ctx.wasm;
  const d = getArgNum(stmt.args, 'd', -1, ctx, -1);
  const r = d >= 0 ? d / 2 : getArgNum(stmt.args, 'r', 0, ctx, 1);
  const fn = getArgNum(stmt.args, '$fn', -1, ctx, ctx.fn);
  const cs = CrossSection.circle(r, Math.max(fn, 8));
  cleanup.push(cs);
  return cs;
}

function handleSquare2D(stmt: ModuleCallStatement, ctx: EvalContext, cleanup: CrossSection[]): CrossSection | null {
  const { CrossSection } = ctx.wasm;
  const sizeArg = getArg(stmt.args, 'size', 0, ctx);
  const center = getArgBool(stmt.args, 'center', 1, ctx, false);

  let size: Vec2;
  if (Array.isArray(sizeArg)) {
    size = [(sizeArg[0] as number) || 1, (sizeArg[1] as number) || 1];
  } else {
    const s = (sizeArg as number) || 1;
    size = [s, s];
  }

  const cs = CrossSection.square(size, center);
  cleanup.push(cs);
  return cs;
}

function handlePolygon2D(stmt: ModuleCallStatement, ctx: EvalContext, cleanup: CrossSection[]): CrossSection | null {
  const { CrossSection } = ctx.wasm;
  const pointsArg = getArg(stmt.args, 'points', 0, ctx);
  if (!Array.isArray(pointsArg)) {
    evalError(ctx, 'polygon() requires a points array', stmt.from, stmt.to);
    return null;
  }

  // Sketch-value branch: ["sketch", plane, origin, entities]
  if (isSketchValue(pointsArg)) {
    const [, plane, originRaw, entitiesRaw] = pointsArg;
    const entities = (Array.isArray(entitiesRaw) ? entitiesRaw : []) as SketchEntity[];
    const paths = walkEntities(entities, ctx.fn);
    const usable = paths.filter(p => p.length >= 3);
    // Empty / incomplete sketch → return null silently instead of emitting
    // an evalError. This lets users scaffold a new `sketchN = sketch(...,
    // entities=[])` assignment before drawing anything without the pipeline
    // flagging an error every cycle.
    if (usable.length === 0) return null;
    const origin: Vec3 = Array.isArray(originRaw)
      ? [(originRaw[0] as number) || 0, (originRaw[1] as number) || 0, (originRaw[2] as number) || 0]
      : [0, 0, 0];
    ctx.pendingSketchPlane = { plane: plane as (string | Vec3), origin };
    const cs = CrossSection.ofPolygons(usable, 'EvenOdd');
    cleanup.push(cs);
    return cs;
  }

  const allPoints: Vec2[] = (pointsArg as unknown[][]).map(p => [(p[0] as number) || 0, (p[1] as number) || 0]);
  const pathsArg = getArg(stmt.args, 'paths', 1, ctx);

  let polygons: Vec2[][];
  if (Array.isArray(pathsArg) && pathsArg.length > 0) {
    // Each path is an array of indices into allPoints
    polygons = (pathsArg as unknown[][]).map(path =>
      (path as number[]).map(idx => allPoints[idx] ?? [0, 0]),
    );
  } else {
    polygons = [allPoints];
  }

  // Use EvenOdd fill rule to handle both CW and CCW winding (OpenSCAD is winding-agnostic)
  const cs = CrossSection.ofPolygons(polygons, 'EvenOdd');
  cleanup.push(cs);
  return cs;
}

// --- 2D Transforms ---

function handleTranslate2D(stmt: ModuleCallStatement, ctx: EvalContext, cleanup: CrossSection[]): CrossSection | null {
  const vec = getArgVec3(stmt.args, 'v', 0, ctx, [0, 0, 0]);
  const child = evalChildren2D(stmt.children, ctx, cleanup);
  if (!child) return null;
  const cs = child.translate([vec[0], vec[1]]);
  cleanup.push(cs);
  return cs;
}

function handleRotate2D(stmt: ModuleCallStatement, ctx: EvalContext, cleanup: CrossSection[]): CrossSection | null {
  const arg = getArg(stmt.args, 'a', 0, ctx);
  let angle: number;
  if (Array.isArray(arg)) {
    angle = (arg[2] as number) || 0; // Z rotation only in 2D
  } else {
    angle = (arg as number) || 0;
  }
  const child = evalChildren2D(stmt.children, ctx, cleanup);
  if (!child) return null;
  const cs = child.rotate(angle);
  cleanup.push(cs);
  return cs;
}

function handleScale2D(stmt: ModuleCallStatement, ctx: EvalContext, cleanup: CrossSection[]): CrossSection | null {
  const vec = getArgVec3(stmt.args, 'v', 0, ctx, [1, 1, 1]);
  const child = evalChildren2D(stmt.children, ctx, cleanup);
  if (!child) return null;
  const cs = child.scale([vec[0], vec[1]]);
  cleanup.push(cs);
  return cs;
}

function handleMirror2D(stmt: ModuleCallStatement, ctx: EvalContext, cleanup: CrossSection[]): CrossSection | null {
  const vec = getArgVec3(stmt.args, 'v', 0, ctx, [1, 0, 0]);
  const child = evalChildren2D(stmt.children, ctx, cleanup);
  if (!child) return null;
  const cs = child.mirror([vec[0], vec[1]]);
  cleanup.push(cs);
  return cs;
}

function handleOffset2D(stmt: ModuleCallStatement, ctx: EvalContext, cleanup: CrossSection[]): CrossSection | null {
  const rArg = getArg(stmt.args, 'r', 0, ctx);
  const deltaArg = getArg(stmt.args, 'delta', -1, ctx);
  const chamfer = getArgBool(stmt.args, 'chamfer', -1, ctx, false);
  const fn = getArgNum(stmt.args, '$fn', -1, ctx, ctx.fn);

  const child = evalChildren2D(stmt.children, ctx, cleanup);
  if (!child) return null;

  let delta: number;
  let joinType: 'Square' | 'Round' | 'Miter';

  if (rArg !== undefined && typeof rArg === 'number') {
    // r parameter: use Round join
    delta = rArg;
    joinType = 'Round';
  } else if (deltaArg !== undefined && typeof deltaArg === 'number') {
    // delta parameter: use Miter or Square
    delta = deltaArg;
    joinType = chamfer ? 'Square' : 'Miter';
  } else {
    // Default: r=1 with Round
    delta = typeof rArg === 'number' ? rArg : 1;
    joinType = 'Round';
  }

  let cs: CrossSection;
  if (joinType === 'Round') {
    cs = child.offset(delta, joinType, 2, Math.max(fn, 8));
  } else {
    cs = child.offset(delta, joinType);
  }
  const simplified = cs.simplify();
  cleanup.push(cs);
  cleanup.push(simplified);
  return simplified;
}

// --- 2D Booleans ---

function handleBoolean2D(op: 'union' | 'difference' | 'intersection'): Module2DHandler {
  return (stmt, ctx, cleanup) => {
    const children: CrossSection[] = [];
    for (const child of stmt.children) {
      const cs = evalStatement2D(child, ctx, cleanup);
      if (cs) children.push(cs);
    }
    if (children.length === 0) return null;
    if (children.length === 1) return children[0];

    let result = children[0];
    for (let i = 1; i < children.length; i++) {
      let next: CrossSection;
      switch (op) {
        case 'union': next = result.add(children[i]); break;
        case 'difference': next = result.subtract(children[i]); break;
        case 'intersection': next = result.intersect(children[i]); break;
      }
      cleanup.push(next);
      result = next;
    }
    return result;
  };
}

function handleChildren2D(stmt: ModuleCallStatement, ctx: EvalContext, cleanup: CrossSection[]): CrossSection | null {
  if (ctx.callerChildren.length === 0) return null;
  return evalChildren2D(ctx.callerChildren, ctx, cleanup);
}

// --- Additional 2D Primitives ---

function handleRegularPolygon2D(stmt: ModuleCallStatement, ctx: EvalContext, cleanup: CrossSection[]): CrossSection | null {
  const { CrossSection } = ctx.wasm;
  const r = getArgNum(stmt.args, 'r', 0, ctx, 1);
  const n = Math.max(Math.round(getArgNum(stmt.args, 'n', 1, ctx, 6)), 3);

  const points: Vec2[] = [];
  for (let i = 0; i < n; i++) {
    const a = (i / n) * 2 * Math.PI;
    points.push([r * Math.cos(a), r * Math.sin(a)]);
  }
  const cs = CrossSection.ofPolygons([points]);
  cleanup.push(cs);
  return cs;
}

function handleHexagon2D(stmt: ModuleCallStatement, ctx: EvalContext, cleanup: CrossSection[]): CrossSection | null {
  const { CrossSection } = ctx.wasm;
  const r = getArgNum(stmt.args, 'r', 0, ctx, 1);

  const points: Vec2[] = [];
  for (let i = 0; i < 6; i++) {
    const a = (i / 6) * 2 * Math.PI;
    points.push([r * Math.cos(a), r * Math.sin(a)]);
  }
  const cs = CrossSection.ofPolygons([points]);
  cleanup.push(cs);
  return cs;
}

function handleSlot2D(stmt: ModuleCallStatement, ctx: EvalContext, cleanup: CrossSection[]): CrossSection | null {
  const { CrossSection } = ctx.wasm;
  const w = getArgNum(stmt.args, 'w', 0, ctx, 4);
  const h = getArgNum(stmt.args, 'h', 1, ctx, 2);
  const centerFlag = getArgBool(stmt.args, 'center', -1, ctx, false);
  const fn = getArgNum(stmt.args, '$fn', -1, ctx, ctx.fn);
  const arcSteps = Math.max(Math.round(Math.max(fn, 16) / 2), 4);

  const points: Vec2[] = [];
  if (w >= h) {
    // Horizontal: semicircle caps on left and right, radius = h/2
    const capR = h / 2;
    const straight = w - h;
    // Bottom edge: left to right
    points.push([capR, 0]);
    points.push([capR + straight, 0]);
    // Right semicircle
    for (let i = 1; i < arcSteps; i++) {
      const a = -Math.PI / 2 + (i / arcSteps) * Math.PI;
      points.push([capR + straight + capR * Math.cos(a), capR + capR * Math.sin(a)]);
    }
    // Top edge: right to left
    points.push([capR + straight, h]);
    points.push([capR, h]);
    // Left semicircle
    for (let i = 1; i < arcSteps; i++) {
      const a = Math.PI / 2 + (i / arcSteps) * Math.PI;
      points.push([capR + capR * Math.cos(a), capR + capR * Math.sin(a)]);
    }
  } else {
    // Vertical: semicircle caps on top and bottom, radius = w/2
    const capR = w / 2;
    const straight = h - w;
    // Right edge: bottom to top
    points.push([w, capR]);
    points.push([w, capR + straight]);
    // Top semicircle
    for (let i = 1; i < arcSteps; i++) {
      const a = (i / arcSteps) * Math.PI;
      points.push([capR + capR * Math.cos(a), capR + straight + capR * Math.sin(a)]);
    }
    // Left edge: top to bottom
    points.push([0, capR + straight]);
    points.push([0, capR]);
    // Bottom semicircle
    for (let i = 1; i < arcSteps; i++) {
      const a = Math.PI + (i / arcSteps) * Math.PI;
      points.push([capR + capR * Math.cos(a), capR + capR * Math.sin(a)]);
    }
  }

  let cs = CrossSection.ofPolygons([points]);
  cleanup.push(cs);
  if (centerFlag) {
    const centered = cs.translate([-w / 2, -h / 2]);
    cleanup.push(centered);
    cs = centered;
  }
  return cs;
}

function handlePlus2D(stmt: ModuleCallStatement, ctx: EvalContext, cleanup: CrossSection[]): CrossSection | null {
  const size = getArgNum(stmt.args, 'size', 0, ctx, 10);
  const thickness = getArgNum(stmt.args, 'thickness', 1, ctx, size / 3);
  const cornerR = getArgNum(stmt.args, 'r', -1, ctx, 0);
  const cs = plusCrossSection(size, thickness, cornerR, ctx);
  cleanup.push(cs);
  return cs;
}

function handleX2D(stmt: ModuleCallStatement, ctx: EvalContext, cleanup: CrossSection[]): CrossSection | null {
  const size = getArgNum(stmt.args, 'size', 0, ctx, 10);
  const thickness = getArgNum(stmt.args, 'thickness', 1, ctx, size / 4);
  const cornerR = getArgNum(stmt.args, 'r', -1, ctx, 0);
  const cs = xCrossSection(size, thickness, cornerR, ctx);
  cleanup.push(cs);
  return cs;
}

function handleBowtie2D(stmt: ModuleCallStatement, ctx: EvalContext, cleanup: CrossSection[]): CrossSection | null {
  const size = getArgNum(stmt.args, 'size', 0, ctx, 10);
  const thickness = getArgNum(stmt.args, 'thickness', 1, ctx, size / 2);
  const cornerR = getArgNum(stmt.args, 'r', -1, ctx, 0);
  const cs = bowtieCrossSection(size, thickness, cornerR, ctx);
  cleanup.push(cs);
  return cs;
}

function handleDiamond2D(stmt: ModuleCallStatement, ctx: EvalContext, cleanup: CrossSection[]): CrossSection | null {
  const size = getArgNum(stmt.args, 'size', 0, ctx, 10);
  const thickness = getArgNum(stmt.args, 'thickness', 1, ctx, size / 2);
  const gap = getArgNum(stmt.args, 'gap', 2, ctx, 0);
  const cornerR = getArgNum(stmt.args, 'r', -1, ctx, 0);
  const cs = diamondCrossSection(size, thickness, gap, cornerR, ctx);
  cleanup.push(cs);
  return cs;
}

function handleRadial2D(stmt: ModuleCallStatement, ctx: EvalContext, cleanup: CrossSection[]): CrossSection | null {
  const spokes = Math.max(1, Math.floor(getArgNum(stmt.args, 'spokes', 0, ctx, 4)));
  const size = getArgNum(stmt.args, 'size', 1, ctx, 10);
  const thickness = getArgNum(stmt.args, 'thickness', 2, ctx, size / 4);
  const gap = getArgNum(stmt.args, 'gap', 3, ctx, 0);
  const cornerR = getArgNum(stmt.args, 'r', -1, ctx, 0);
  const cs = radialCrossSection(size, thickness, gap, spokes, cornerR, ctx);
  cleanup.push(cs);
  return cs;
}

function handleStar2D(stmt: ModuleCallStatement, ctx: EvalContext, cleanup: CrossSection[]): CrossSection | null {
  const { CrossSection } = ctx.wasm;
  const numPoints = Math.max(Math.round(getArgNum(stmt.args, 'points', 0, ctx, 5)), 2);
  const rOuter = getArgNum(stmt.args, 'r_outer', 1, ctx, 2);
  const rInner = getArgNum(stmt.args, 'r_inner', 2, ctx, rOuter * 0.4);

  const verts: Vec2[] = [];
  const total = numPoints * 2;
  for (let i = 0; i < total; i++) {
    const a = (i / total) * 2 * Math.PI - Math.PI / 2;
    const rad = i % 2 === 0 ? rOuter : rInner;
    verts.push([rad * Math.cos(a), rad * Math.sin(a)]);
  }
  const cs = CrossSection.ofPolygons([verts]);
  cleanup.push(cs);
  return cs;
}

// --- text() 2D handler ---

function handleText2D(stmt: ModuleCallStatement, ctx: EvalContext, cleanup: CrossSection[]): CrossSection | null {
  const { CrossSection } = ctx.wasm;

  const textStr = getArgStr(stmt.args, 'text', 0, ctx, '');
  if (!textStr) {
    evalError(ctx, 'text() requires a non-empty string', stmt.from, stmt.to);
    return null;
  }

  const size = getArgNum(stmt.args, 'size', 1, ctx, 10);
  const halign = getArgStr(stmt.args, 'halign', -1, ctx, 'left');
  const valign = getArgStr(stmt.args, 'valign', -1, ctx, 'baseline');
  const spacing = getArgNum(stmt.args, 'spacing', -1, ctx, 1);
  const fn = getArgNum(stmt.args, '$fn', -1, ctx, Math.min(ctx.fn, 16));

  let font: import('opentype.js').Font;
  try {
    font = getFont();
  } catch {
    evalError(ctx, 'Font not loaded yet', stmt.from, stmt.to);
    return null;
  }

  // Get path with spacing applied via letterSpacing option
  // opentype.js letterSpacing is in em units, so we convert
  const path = font.getPath(textStr, 0, 0, size, {
    letterSpacing: (spacing - 1) * size / font.unitsPerEm,
  });

  // Convert path commands to Vec2 contours with bezier tessellation
  const contours: Vec2[][] = [];
  let current: Vec2[] = [];
  let cx = 0, cy = 0; // current point for bezier start

  for (const cmd of path.commands) {
    switch (cmd.type) {
      case 'M':
        if (current.length > 0) contours.push(current);
        current = [[cmd.x, -cmd.y]]; // Flip Y (font Y is down, SCAD Y is up)
        cx = cmd.x; cy = cmd.y;
        break;
      case 'L':
        current.push([cmd.x, -cmd.y]);
        cx = cmd.x; cy = cmd.y;
        break;
      case 'Q': {
        // Quadratic bezier: B(t) = (1-t)²P0 + 2(1-t)tP1 + t²P2
        const steps = Math.max(fn, 4);
        for (let i = 1; i <= steps; i++) {
          const t = i / steps;
          const t1 = 1 - t;
          const x = t1 * t1 * cx + 2 * t1 * t * cmd.x1 + t * t * cmd.x;
          const y = t1 * t1 * cy + 2 * t1 * t * cmd.y1 + t * t * cmd.y;
          current.push([x, -y]);
        }
        cx = cmd.x; cy = cmd.y;
        break;
      }
      case 'C': {
        // Cubic bezier: B(t) = (1-t)³P0 + 3(1-t)²tP1 + 3(1-t)t²P2 + t³P3
        const steps = Math.max(fn, 4);
        for (let i = 1; i <= steps; i++) {
          const t = i / steps;
          const t1 = 1 - t;
          const x = t1 * t1 * t1 * cx + 3 * t1 * t1 * t * cmd.x1 + 3 * t1 * t * t * cmd.x2 + t * t * t * cmd.x;
          const y = t1 * t1 * t1 * cy + 3 * t1 * t1 * t * cmd.y1 + 3 * t1 * t * t * cmd.y2 + t * t * t * cmd.y;
          current.push([x, -y]);
        }
        cx = cmd.x; cy = cmd.y;
        break;
      }
      case 'Z':
        if (current.length > 2) {
          // Remove duplicate close point if present
          const first = current[0];
          const last = current[current.length - 1];
          if (Math.abs(first[0] - last[0]) < 1e-6 && Math.abs(first[1] - last[1]) < 1e-6) {
            current.pop();
          }
          if (current.length > 2) contours.push(current);
        }
        current = [];
        break;
    }
  }
  if (current.length > 2) contours.push(current);

  if (contours.length === 0) return null;

  // Compute alignment offsets
  let dx = 0, dy = 0;
  const totalWidth = font.getAdvanceWidth(textStr, size, {
    letterSpacing: (spacing - 1) * size / font.unitsPerEm,
  });
  const scale = size / font.unitsPerEm;

  if (halign === 'center') dx = -totalWidth / 2;
  else if (halign === 'right') dx = -totalWidth;

  if (valign === 'top') dy = -font.ascender * scale;
  else if (valign === 'center') dy = -(font.ascender + font.descender) * scale / 2;
  else if (valign === 'bottom') dy = -font.descender * scale;
  // 'baseline' => dy = 0

  // Apply alignment offsets
  if (dx !== 0 || dy !== 0) {
    for (const contour of contours) {
      for (const pt of contour) {
        pt[0] += dx;
        pt[1] += dy;
      }
    }
  }

  try {
    const cs = CrossSection.ofPolygons(contours, 'EvenOdd');
    cleanup.push(cs);
    return cs;
  } catch (err) {
    evalError(ctx, `text() error: ${(err as Error).message}`, stmt.from, stmt.to);
    return null;
  }
}

const MODULE_HANDLERS_2D: Record<string, Module2DHandler> = {
  circle: handleCircle2D,
  square: handleSquare2D,
  polygon: handlePolygon2D,
  translate: handleTranslate2D,
  rotate: handleRotate2D,
  scale: handleScale2D,
  union: handleBoolean2D('union'),
  difference: handleBoolean2D('difference'),
  intersection: handleBoolean2D('intersection'),
  children: handleChildren2D,
  regular_polygon: handleRegularPolygon2D,
  hexagon: handleHexagon2D,
  slot: handleSlot2D,
  star: handleStar2D,
  plus: handlePlus2D,
  cross: handlePlus2D,
  x: handleX2D,
  bowtie: handleBowtie2D,
  diamond: handleDiamond2D,
  rhombus: handleDiamond2D,
  radial: handleRadial2D,
  spokes: handleRadial2D,
  text: handleText2D,
  mirror: handleMirror2D,
  offset: handleOffset2D,
};

// ====================================================================
// linear_extrude (3D handler that evaluates children in 2D)
// ====================================================================

function handleLinearExtrude(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const height = getArgNum(stmt.args, 'height', 0, ctx, 1);
  const twist = getArgNum(stmt.args, 'twist', -1, ctx, 0);
  const slices = getArgNum(stmt.args, 'slices', -1, ctx, 0);
  const scaleArg = getArg(stmt.args, 'scale', -1, ctx);
  const center = getArgBool(stmt.args, 'center', -1, ctx, false);

  const cleanup: CrossSection[] = [];
  try {
    const cs = evalChildren2D(stmt.children, ctx, cleanup);
    if (!cs) return null;

    let scaleTop: Vec2 = [1, 1];
    if (scaleArg !== undefined) {
      if (Array.isArray(scaleArg)) {
        scaleTop = [(scaleArg[0] as number) ?? 1, (scaleArg[1] as number) ?? 1];
      } else {
        const s = scaleArg as number;
        scaleTop = [s, s];
      }
    }

    const needsDivisions = twist !== 0 || scaleTop[0] !== 1 || scaleTop[1] !== 1;
    const nDivisions = slices > 0
      ? Math.round(slices)
      : (needsDivisions ? Math.max(1, Math.ceil(Math.abs(twist || 0) / 10) + (twist === 0 ? 1 : 0)) : 0);

    let m = cs.extrude(height, nDivisions, twist, scaleTop, center);
    trackIntermediate(ctx, m);

    // If the 2D child came from a sketch value, apply the stored plane
    // rotation + origin translation to the extruded result. This is how the
    // sketch's plane="XZ" / "YZ" / normal=[x,y,z] actually shows up in 3D.
    if (ctx.pendingSketchPlane) {
      const { plane, origin } = ctx.pendingSketchPlane;
      ctx.pendingSketchPlane = undefined;
      const rot = rotationForPlane(plane, rotationFromTo);
      const tx = mat3ToMat4WithTranslation(rot, origin);
      const rotated = m.transform(tx);
      trackIntermediate(ctx, rotated);
      m = rotated;
    }

    return m;
  } finally {
    for (const c of cleanup) {
      try { c.delete(); } catch { /* ok */ }
    }
  }
}

// ====================================================================
// tapered_extrude — variable-height linear extrusion
// ====================================================================
//
// `tapered_extrude(h_start, h_end, length, axis="x") { profile_2d(); }`
//
// Extrudes a 2D profile at the maximum of (h_start, h_end), then carves
// off a wedge so the height varies linearly from h_start at one end to
// h_end at the other along the chosen axis. Equivalent to:
//
//   intersection() {
//     linear_extrude(max(h_start, h_end)) profile();
//     hull() {
//       translate([0, …, 0])      cube([eps, big, h_start]);
//       translate([length, …, 0]) cube([eps, big, h_end]);
//     }
//   }
//
// Args:
//   h_start  — height at the lower end of the chosen axis
//   h_end    — height at the upper end of the chosen axis
//   length   — taper length in mm. Default = profile bbox extent along axis.
//   axis     — "x" (default) or "y". Direction the height tapers along.

function handleTaperedExtrude(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const { Manifold } = ctx.wasm;
  const hStart = getArgNum(stmt.args, 'h_start', 0, ctx, 1);
  const hEnd = getArgNum(stmt.args, 'h_end', 1, ctx, 1);
  const lengthArg = getArgNum(stmt.args, 'length', 2, ctx, 0);
  const axisStr = getArgStr(stmt.args, 'axis', 3, ctx, 'x').toLowerCase();
  const axis: 0 | 1 = axisStr === 'y' ? 1 : 0;
  // Optional edge treatments. `chamfer_top` and `fillet_top` follow the
  // wedge's slanted top (so the bevel survives the entire taper, not just
  // the high end where the wedge happens to be tall enough). `chamfer_bot`
  // and `fillet_bot` are standard flat-top edge treatments at z=0.
  const fn = getArgNum(stmt.args, '$fn', -1, ctx, ctx.fn);
  const chamferAll = getArgNum(stmt.args, 'chamfer', -1, ctx, 0);
  const chamferTop = getArgNum(stmt.args, 'chamfer_top', -1, ctx, chamferAll);
  const chamferBot = getArgNum(stmt.args, 'chamfer_bot', -1, ctx, chamferAll);
  const filletAll = getArgNum(stmt.args, 'fillet', -1, ctx, 0);
  const filletTop = getArgNum(stmt.args, 'fillet_top', -1, ctx, filletAll);
  const filletBot = getArgNum(stmt.args, 'fillet_bot', -1, ctx, filletAll);

  if (hStart <= 0 && hEnd <= 0) return null;

  const cleanup: CrossSection[] = [];
  try {
    const cs = evalChildren2D(stmt.children, ctx, cleanup);
    if (!cs) return null;

    // Profile bbox + axis bounds drive both the default length and the
    // perpendicular-axis padding for the wedge cutter.
    const probeExt = cs.extrude(Math.max(hStart, hEnd, 0.01));
    trackIntermediate(ctx, probeExt);
    const bbox = probeExt.boundingBox();
    const otherAxis: 0 | 1 = axis === 0 ? 1 : 0;
    const profileExtent = bbox.max[axis] - bbox.min[axis];
    const length = lengthArg > 0 ? lengthArg : profileExtent;
    const start = bbox.min[axis];
    const end = start + length;
    const padBounds = {
      min: bbox.min[otherAxis],
      max: bbox.max[otherAxis],
      extent: Math.max(profileExtent, Math.max(hStart, hEnd)),
    };

    // Build a tapered wedge for an arbitrary silhouette + heights.
    const buildTapered = (silhouette: CrossSection, h1: number, h2: number): Manifold => {
      const hM = Math.max(h1, h2);
      const ext = silhouette.extrude(hM);
      trackIntermediate(ctx, ext);
      const SLAB_THICK = 0.01;
      const PAD = padBounds.extent * 4 + 100;
      const otherMin = padBounds.min - PAD;
      const otherSpan = (padBounds.max - padBounds.min) + PAD * 2;
      const buildSlab = (xPos: number, h: number): Manifold => {
        const dims: [number, number, number] = [0, 0, h];
        dims[axis] = SLAB_THICK;
        dims[otherAxis] = otherSpan;
        const cube = Manifold.cube(dims as Vec3, false);
        trackIntermediate(ctx, cube);
        const pos: [number, number, number] = [0, 0, 0];
        pos[axis] = xPos;
        pos[otherAxis] = otherMin;
        const placed = cube.translate(pos);
        trackIntermediate(ctx, placed);
        return placed;
      };
      const slab1 = buildSlab(start, h1);
      const slab2 = buildSlab(end - SLAB_THICK, h2);
      const wedge = Manifold.hull([slab1, slab2]);
      trackIntermediate(ctx, wedge);
      const out = ext.intersect(wedge);
      trackIntermediate(ctx, out);
      return out;
    };

    let result: Manifold;

    // Convexity test on the silhouette — `Manifold.hull` of layered
    // wedges only matches the desired chamfer when the silhouette is
    // convex. For non-convex profiles (e.g., a slot with an indented arc
    // end), the hull bulges into concave regions and the apparent shape
    // grows beyond the original silhouette. Compare silhouette area to
    // its 2D convex-hull area: ratio < 0.99 → non-convex.
    let silhouetteIsConvex = true;
    try {
      const csArea = Math.abs(cs.area());
      if (csArea > 1e-9) {
        const hullCS = cs.hull();
        try {
          const hullArea = Math.abs(hullCS.area());
          if (hullArea > 1e-9) silhouetteIsConvex = csArea / hullArea > 0.99;
        } finally { hullCS.delete(); }
      }
    } catch { /* default convex */ }

    // Top edge: chamfer or fillet that tracks the wedge slope.
    // Convex silhouettes → hull-of-layered-wedges (chamfer follows the
    //   entire slanted top, exact for convex profiles).
    // Non-convex silhouettes → bake the chamfer into the flat extrusion
    //   before the wedge cut. The chamfer only survives where the wedge
    //   is tall enough for the chamfer zone (the high end), but at least
    //   the silhouette is preserved and there's no apparent extension.
    const ftEff = filletTop > 0 ? filletTop : 0;
    const ctEff = ftEff > 0 ? 0 : (chamferTop > 0 ? chamferTop : 0);
    const topSize = Math.max(ftEff, ctEff);

    if (topSize > 0 && hStart - topSize > 0 && hEnd - topSize > 0 && silhouetteIsConvex) {
      // Number of layers for the fillet arc; chamfer needs only 2 layers
      // (outer-low + inner-high) since the connecting hull face is
      // already a clean 45° plane.
      const steps = ftEff > 0 ? Math.max(6, Math.round(fn / 6)) : 1;
      const layers: Manifold[] = [];
      for (let i = 0; i <= steps; i++) {
        const t = i / steps;                       // 0 = outer, 1 = inner
        const inset = ftEff > 0
          ? topSize * Math.sin(t * Math.PI / 2)
          : topSize * t;
        const drop = ftEff > 0
          ? topSize * (1 - Math.cos(t * Math.PI / 2))
          : topSize * (1 - t);
        let layerCS: CrossSection;
        try {
          layerCS = inset > 1e-9
            ? cs.offset(-inset, 'Round')
            : cs.offset(0, 'Miter', 10);
        } catch { continue; }
        if (layerCS.isEmpty()) { layerCS.delete(); continue; }
        cleanup.push(layerCS);
        const wedgeAtLayer = buildTapered(layerCS, hStart - drop, hEnd - drop);
        layers.push(wedgeAtLayer);
      }
      if (layers.length >= 2) {
        result = Manifold.hull(layers);
        trackIntermediate(ctx, result);
      } else {
        result = buildTapered(cs, hStart, hEnd);
      }
    } else if (topSize > 0 && !silhouetteIsConvex) {
      // Non-convex fallback: chamfer the flat extrusion first, then cut
      // with the wedge. The flat-prism chamfer is correct for any
      // silhouette (no over-fill); the wedge cut may remove some of the
      // chamfer at the low end, but the silhouette stays as-drawn.
      const hMax = Math.max(hStart, hEnd);
      const flatExt = cs.extrude(hMax);
      trackIntermediate(ctx, flatExt);
      let chamfered: Manifold = flatExt;
      const treated = applyEdgeOpToManifold(
        flatExt, ctx, topSize, true, false, fn, ftEff > 0 ? 'fillet' : 'chamfer',
      );
      if (treated) chamfered = treated;
      // Now cut with the wedge — same logic as buildTapered but reusing
      // the already-chamfered shape instead of re-extruding.
      const SLAB_THICK = 0.01;
      const PAD = padBounds.extent * 4 + 100;
      const otherMin = padBounds.min - PAD;
      const otherSpan = (padBounds.max - padBounds.min) + PAD * 2;
      const buildSlab = (xPos: number, h: number): Manifold => {
        const dims: [number, number, number] = [0, 0, h];
        dims[axis] = SLAB_THICK;
        dims[otherAxis] = otherSpan;
        const cube = Manifold.cube(dims as Vec3, false);
        trackIntermediate(ctx, cube);
        const pos: [number, number, number] = [0, 0, 0];
        pos[axis] = xPos;
        pos[otherAxis] = otherMin;
        const placed = cube.translate(pos);
        trackIntermediate(ctx, placed);
        return placed;
      };
      const wedge = Manifold.hull([buildSlab(start, hStart), buildSlab(end - SLAB_THICK, hEnd)]);
      trackIntermediate(ctx, wedge);
      result = chamfered.intersect(wedge);
      trackIntermediate(ctx, result);
    } else {
      result = buildTapered(cs, hStart, hEnd);
    }

    // Bottom edge: standard flat-bottom edge treatment, since the wedge
    // bottom is at z=0 regardless of slope. Reuse the chamfer/fillet
    // pipeline that already handles flat tops/bottoms correctly.
    const fbEff = filletBot > 0 ? filletBot : 0;
    const cbEff = fbEff > 0 ? 0 : (chamferBot > 0 ? chamferBot : 0);
    if (fbEff > 0) {
      const treated = applyEdgeOpToManifold(result, ctx, fbEff, false, true, fn, 'fillet');
      if (treated) result = treated;
    } else if (cbEff > 0) {
      const treated = applyEdgeOpToManifold(result, ctx, cbEff, false, true, fn, 'chamfer');
      if (treated) result = treated;
    }

    if (ctx.pendingSketchPlane) {
      const { plane, origin } = ctx.pendingSketchPlane;
      ctx.pendingSketchPlane = undefined;
      const rot = rotationForPlane(plane, rotationFromTo);
      const tx = mat3ToMat4WithTranslation(rot, origin);
      const rotated = result.transform(tx);
      trackIntermediate(ctx, rotated);
      result = rotated;
    }
    return result;
  } finally {
    for (const c of cleanup) {
      try { c.delete(); } catch { /* ok */ }
    }
  }
}

// ====================================================================
// chamfer / fillet — generalized top/bottom edge treatment on any child
// ====================================================================
//
// Both operate by slicing the child's cross-section near the edge zone and
// rebuilding the zone as a stack of thin, progressively-offset slabs:
//
//   chamfer(c) { shape; }       linear taper  (offset = -t · c)
//   fillet(r)  { shape; }       quarter-arc   (offset = -(r - √(r² - (t·r)²)))
//
// The child can be any 3D manifold. The technique assumes the shape's
// silhouette is approximately prismatic within the edge zone (top `c` or
// bottom `c` for chamfer, top `r` / bottom `r` for fillet) — the slice at
// the inner boundary of the zone is taken as the reference profile, and the
// real geometry above / below it is replaced by the new taper. For purely
// prismatic shapes (cubes, linear_extrude profiles, primitives) this is
// exact; for shapes whose silhouette changes within the zone this is an
// approximation.

function evaluateChildUnion(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  // Cheap wrapper that mirrors handleGroup / handleUnion's implicit union.
  const results: Manifold[] = [];
  for (const child of stmt.children) {
    const m = evalStatement(child, ctx);
    if (m) results.push(m);
  }
  if (results.length === 0) return null;
  if (results.length === 1) return results[0];
  let combined = results[0];
  for (let i = 1; i < results.length; i++) {
    const next = combined.add(results[i]);
    trackIntermediate(ctx, next);
    combined = next;
  }
  return combined;
}

function handleChamfer(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const sizeArg = getArgNum(stmt.args, 'c', 0, ctx, Number.NaN);
  const size = Number.isNaN(sizeArg) ? getArgNum(stmt.args, 'size', -1, ctx, 1) : sizeArg;
  const top = getArgBool(stmt.args, 'top', -1, ctx, true);
  const bottom = getArgBool(stmt.args, 'bottom', -1, ctx, true);
  const fn = getArgNum(stmt.args, '$fn', -1, ctx, ctx.fn);
  return applyEdgeOp(stmt, ctx, size, top, bottom, fn, 'chamfer');
}

function handleFillet(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const rArg = getArgNum(stmt.args, 'r', 0, ctx, Number.NaN);
  const r = Number.isNaN(rArg) ? getArgNum(stmt.args, 'size', -1, ctx, 1) : rArg;
  const top = getArgBool(stmt.args, 'top', -1, ctx, true);
  const bottom = getArgBool(stmt.args, 'bottom', -1, ctx, true);
  const fn = getArgNum(stmt.args, '$fn', -1, ctx, ctx.fn);
  return applyEdgeOp(stmt, ctx, r, top, bottom, fn, 'fillet');
}

function applyEdgeOp(
  stmt: ModuleCallStatement,
  ctx: EvalContext,
  size: number,
  doTop: boolean,
  doBottom: boolean,
  fn: number,
  kind: 'chamfer' | 'fillet',
): Manifold | null {
  const child = evaluateChildUnion(stmt, ctx);
  if (!child) return null;
  return applyEdgeOpToManifold(child, ctx, size, doTop, doBottom, fn, kind);
}

// Core implementation — works on a pre-built Manifold so other operators
// (e.g. tapered_extrude) can reuse it without going through the chamfer /
// fillet stmt-handler API.
function applyEdgeOpToManifold(
  child: Manifold,
  ctx: EvalContext,
  size: number,
  doTop: boolean,
  doBottom: boolean,
  fn: number,
  kind: 'chamfer' | 'fillet',
): Manifold | null {
  if (size <= 0 || (!doTop && !doBottom)) return child;

  const bbox = child.boundingBox();
  const zMin = bbox.min[2];
  const zMax = bbox.max[2];
  const h = zMax - zMin;
  if (h <= 0) return child;

  const sizeBudget = (doTop && doBottom) ? h / 2 : h;
  const s = Math.min(size, sizeBudget);
  if (s <= 1e-6) return child;

  const pieces: Manifold[] = [];

  const midBotZ = doBottom ? zMin + s : zMin;
  const midTopZ = doTop ? zMax - s : zMax;
  if (midTopZ - midBotZ > 1e-6) {
    let mid: Manifold = child;
    if (doTop) {
      const trimmed = mid.trimByPlane([0, 0, -1], -midTopZ);
      trackIntermediate(ctx, trimmed);
      mid = trimmed;
    }
    if (doBottom) {
      const trimmed = mid.trimByPlane([0, 0, 1], midBotZ);
      trackIntermediate(ctx, trimmed);
      mid = trimmed;
    }
    pieces.push(mid);
  }

  if (doTop) {
    const part = buildEdgeZone(ctx, child, midTopZ, zMax, s, fn, kind, 'top');
    if (!part) return child;
    pieces.push(part);
  }
  if (doBottom) {
    const part = buildEdgeZone(ctx, child, zMin, midBotZ, s, fn, kind, 'bottom');
    if (!part) return child;
    pieces.push(part);
  }

  if (pieces.length === 0) return null;
  if (pieces.length === 1) return pieces[0];
  let result = pieces[0];
  for (let i = 1; i < pieces.length; i++) {
    const u = result.add(pieces[i]);
    trackIntermediate(ctx, u);
    result = u;
  }
  return result;
}

function buildEdgeZone(
  ctx: EvalContext,
  child: Manifold,
  zLow: number,
  zHigh: number,
  size: number,
  fn: number,
  kind: 'chamfer' | 'fillet',
  side: 'top' | 'bottom',
): Manifold | null {
  // Profile at the inner boundary of the edge zone — the reference silhouette
  // the taper should start from. Small epsilon keeps us strictly inside the
  // shape (slicing exactly at the bbox face returns empty).
  const innerZ = side === 'top' ? zLow + 1e-3 : zHigh - 1e-3;
  const base = child.slice(innerZ);
  if (!base || base.isEmpty()) {
    if (base) base.delete();
    return null;
  }

  // Convexity check — compare the full silhouette's area with its convex
  // hull. Ratio ≈ 1 means convex (cube / cylinder / circle / simple
  // extrusion) and we can use the fast hull-based per-segment taper. Ratio
  // < 0.99 means the silhouette has real concavity or holes (text, L-shape,
  // ring etc.) so fall back to the correctness-first slab-stack. In both
  // cases we process the entire CrossSection in ONE pass — CrossSection.
  // offset(-t) naturally handles each disconnected island (e.g. each
  // letter of a text string) on its own, so there's no need to decompose.
  let isConvex = true;
  try {
    const pieceArea = Math.abs(base.area());
    if (pieceArea > 1e-9) {
      const hullCS = base.hull();
      try {
        const hullArea = Math.abs(hullCS.area());
        if (hullArea > 1e-9) isConvex = pieceArea / hullArea > 0.99;
      } finally { hullCS.delete(); }
    }
  } catch { /* default convex */ }

  const insetAt = (t: number): number => {
    if (kind === 'chamfer') return t * size;
    const d = t * size;
    return size - Math.sqrt(Math.max(0, size * size - d * d));
  };
  const zAt = (t: number): number => side === 'top' ? zLow + t * size : zHigh - t * size;

  // Step count depends on strategy:
  //   hull  — few steps is enough; the hull pass smooths each segment.
  //   slabs — need fine layering to hide the staircase, but each slab is a
  //           separate mesh to union, so step count is the main cost driver.
  //           Scale with `size` so small chamfers on complex silhouettes
  //           (text) stay fast while larger ones still look smooth.
  const hullSteps = kind === 'chamfer' ? 2 : Math.max(6, Math.round(fn / 8));
  const slabSteps = kind === 'chamfer'
    ? Math.max(6, Math.min(24, Math.round(size * 4 + fn / 12)))
    : Math.max(12, Math.min(32, Math.round(size * 6 + fn / 8)));

  // For the slab path, pre-simplify the silhouette once — text contours have
  // many vertices (each curve sample). A light simplify cuts vertex count by
  // 2-4x, making each slab's extrude + union far cheaper, with no visible
  // quality loss at the edge-zone scale.
  let workBase = base;
  if (!isConvex) {
    try {
      const eps = Math.max(size / 40, 0.01);
      const simp = base.simplify(eps);
      base.delete();
      workBase = simp;
    } catch { /* keep original */ }
  }

  try {
    return isConvex
      ? buildEdgeZoneHull(ctx, workBase, size, hullSteps, insetAt, zAt, side)
      : buildEdgeZoneSlabs(ctx, workBase, size, slabSteps, insetAt, zAt, side);
  } finally {
    workBase.delete();
  }
}

// Smooth hull strategy — one thin wafer per layer, hull adjacent pairs.
// Only used when the silhouette is convex, so per-segment hulls don't merge
// disconnected islands or fill concave regions.
function buildEdgeZoneHull(
  ctx: EvalContext,
  piece: CrossSection,
  size: number,
  steps: number,
  insetAt: (t: number) => number,
  zAt: (t: number) => number,
  side: 'top' | 'bottom',
): Manifold | null {
  const { Manifold } = ctx.wasm;
  const SLAB_THICK = 1e-3;
  const wafers: Manifold[] = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    const inset = insetAt(t);
    let shrunk;
    try { shrunk = piece.offset(-inset, 'Round'); } catch { continue; }
    if (!shrunk || shrunk.isEmpty()) { if (shrunk) shrunk.delete(); continue; }
    let slab: Manifold;
    try { slab = shrunk.extrude(SLAB_THICK); } finally { shrunk.delete(); }
    const zc = zAt(t);
    const zBase = i === 0
      ? (side === 'top' ? zc : zc - SLAB_THICK)
      : i === steps
        ? (side === 'top' ? zc - SLAB_THICK : zc)
        : zc - SLAB_THICK / 2;
    const placed = slab.translate([0, 0, zBase]);
    slab.delete();
    wafers.push(placed);
    trackIntermediate(ctx, placed);
  }
  if (wafers.length < 2) return wafers[0] ?? null;
  const segments: Manifold[] = [];
  for (let i = 0; i < wafers.length - 1; i++) {
    const seg = Manifold.hull([wafers[i], wafers[i + 1]]);
    trackIntermediate(ctx, seg);
    segments.push(seg);
  }
  const merged = Manifold.union(segments);
  trackIntermediate(ctx, merged);
  return merged;
}

// Top-level slab builder. Tries the polyhedron path (single mesh per
// letter, no CSG, no staircase). If that fails — typically because the
// silhouette is so complex that Miter offset can't preserve topology
// (text, complex curves) — return null. Caller treats null as "skip this
// chamfer entirely" and passes the child through unchanged, which looks
// much better than a stair-stepped approximation.
function buildEdgeZoneSlabs(
  ctx: EvalContext,
  piece: CrossSection,
  _size: number,
  steps: number,
  insetAt: (t: number) => number,
  zAt: (t: number) => number,
  side: 'top' | 'bottom',
): Manifold | null {
  return buildEdgeZonePoly(ctx, piece, steps, insetAt, zAt, side);
}

// Fast path — construct each letter/island as a single closed polyhedron
// via earcut-triangulated caps + direct side triangles. Zero CSG booleans.
function buildEdgeZonePoly(
  ctx: EvalContext,
  piece: CrossSection,
  steps: number,
  insetAt: (t: number) => number,
  zAt: (t: number) => number,
  side: 'top' | 'bottom',
): Manifold | null {
  const { Manifold } = ctx.wasm;
  let pieces: CrossSection[];
  try { pieces = piece.decompose(); } catch { return null; }
  if (!pieces || pieces.length === 0) return null;
  const results: Manifold[] = [];
  let allOK = true;
  try {
    for (const sub of pieces) {
      const built = buildTaperPieceMesh(ctx, sub, steps, insetAt, zAt, side);
      if (!built) { allOK = false; break; }
      results.push(built);
    }
    // If any piece can't be built cleanly, bail the whole fast path — the
    // caller will retry with the robust slab-stack fallback so we don't
    // end up with missing letters.
    if (!allOK || results.length === 0) {
      for (const r of results) { try { r.delete(); } catch { /* ok */ } }
      return null;
    }
    if (results.length === 1) return results[0];
    const merged = Manifold.compose(results);
    trackIntermediate(ctx, merged);
    return merged;
  } finally {
    for (const p of pieces) { try { p.delete(); } catch { /* ok */ } }
  }
}

// Build one closed taper Manifold for a single decomposed silhouette piece
// (one outer contour + zero or more holes). Constructs vertices + triangles
// directly and hands the raw mesh to Manifold — no Boolean ops.
function buildTaperPieceMesh(
  ctx: EvalContext,
  sub: CrossSection,
  steps: number,
  insetAt: (t: number) => number,
  zAt: (t: number) => number,
  side: 'top' | 'bottom',
): Manifold | null {
  const { Manifold, Mesh } = ctx.wasm;

  // Sample the piece at every layer. Use Miter offset with a generous miter
  // limit so the vertex count stays constant across layers (so layer L[v]
  // corresponds directly to layer L+1[v] for side-wall triangles). If any
  // layer changes topology (offset collapsed a feature), bail out — caller
  // will see `null` and keep the original shape unchanged for this piece.
  type LayerContour = { outer: Vec2[]; holes: Vec2[][] };
  const layers: LayerContour[] = [];

  // Clipper returns an empty result when offset delta is exactly 0. Use a
  // tiny floor so every layer — including layer 0 — goes through the same
  // offset pipeline and ends up with a consistent vertex count.
  const OFFSET_FLOOR = 1e-6;
  for (let l = 0; l <= steps; l++) {
    const t = l / steps;
    const inset = Math.max(insetAt(t), OFFSET_FLOOR);
    let cs: CrossSection;
    try { cs = sub.offset(-inset, 'Miter', 10); } catch { return null; }
    if (cs.isEmpty()) { cs.delete(); return null; }
    const polys = cs.toPolygons() as Vec2[][];
    cs.delete();
    if (polys.length === 0) return null;
    // First polygon with positive signed area is the outer; the rest are holes.
    let outerIdx = -1;
    let maxArea = -Infinity;
    for (let i = 0; i < polys.length; i++) {
      const a = signedPolyArea(polys[i]);
      if (a > maxArea) { maxArea = a; outerIdx = i; }
    }
    if (outerIdx < 0) return null;
    const outer = polys[outerIdx];
    const holes = polys.filter((_, i) => i !== outerIdx);
    layers.push({ outer, holes });
  }

  // Vertex counts must match across all layers for direct side-wall linkage.
  const outerN = layers[0].outer.length;
  const holesN = layers[0].holes.map(h => h.length);
  for (let l = 1; l <= steps; l++) {
    if (layers[l].outer.length !== outerN) return null;
    if (layers[l].holes.length !== holesN.length) return null;
    for (let h = 0; h < holesN.length; h++) {
      if (layers[l].holes[h].length !== holesN[h]) return null;
    }
  }

  // Assemble flat vertex buffer + index maps per (layer, contour, vertex).
  const vp: number[] = [];
  const outerIdx: number[][] = []; // [layer][v] -> global vert index
  const holeIdx: number[][][] = []; // [layer][h][v] -> global vert index
  for (let l = 0; l <= steps; l++) {
    const z = zAt(l / steps);
    outerIdx.push([]);
    for (const [x, y] of layers[l].outer) {
      outerIdx[l].push(vp.length / 3);
      vp.push(x, y, z);
    }
    const lh: number[][] = [];
    for (const hole of layers[l].holes) {
      const arr: number[] = [];
      for (const [x, y] of hole) {
        arr.push(vp.length / 3);
        vp.push(x, y, z);
      }
      lh.push(arr);
    }
    holeIdx.push(lh);
  }

  const tris: number[] = [];
  // Outer contours: CCW (positive area) when viewed from +Z — so their
  // outward-facing normal is the out-of-shape direction.
  // Holes: CW (negative area); their outward normal points into the hole.
  // For top side the z=outer layer is the higher z (cap facing +Z); for
  // bottom side it's the lower z (cap facing -Z).
  emitSideWallTriangles(tris, outerIdx, true /*isOuter*/, side);
  for (let h = 0; h < holesN.length; h++) {
    emitSideWallTriangles(
      tris,
      holeIdx.map(layerHoles => layerHoles[h]),
      false /*isOuter*/,
      side,
    );
  }

  // Top cap (at layer=steps, z = zAt(1) = outer face) and bottom cap (layer=0,
  // z = zAt(0) = inner boundary where this piece meets the mid section).
  // Normals: top face normal is +Z for side='top', -Z for side='bottom';
  // bottom face has the opposite.
  emitCap(tris, vp, layers[steps].outer, layers[steps].holes, outerIdx[steps], holeIdx[steps], side === 'top' ? +1 : -1);
  emitCap(tris, vp, layers[0].outer,    layers[0].holes,     outerIdx[0],     holeIdx[0],     side === 'top' ? -1 : +1);

  try {
    const mesh = new Mesh({
      numProp: 3,
      vertProperties: new Float32Array(vp),
      triVerts: new Uint32Array(tris),
    });
    const m = new Manifold(mesh);
    // Sanity-check the mesh — an inverted or degenerate polyhedron will
    // report zero or non-finite volume. Better to fall through to the slab
    // fallback than render an empty / inside-out shape.
    let vol = 0;
    try { vol = m.volume(); } catch { vol = 0; }
    if (!Number.isFinite(vol) || vol <= 0 || m.isEmpty()) {
      try { m.delete(); } catch { /* ok */ }
      return null;
    }
    trackIntermediate(ctx, m);
    return m;
  } catch {
    return null;
  }
}

// Signed area of a simple polygon. Positive = CCW when viewed from +Z.
function signedPolyArea(poly: Vec2[]): number {
  let a = 0;
  for (let i = 0; i < poly.length; i++) {
    const [x1, y1] = poly[i];
    const [x2, y2] = poly[(i + 1) % poly.length];
    a += x1 * y2 - x2 * y1;
  }
  return a / 2;
}

// Emit side-wall triangles for one contour stacked across all layers.
// `contourVerts[layer][v]` gives the global vertex index.
// Winding rule:
//   outer CCW + top side  → quad (A,B,C,D) clockwise from outside, CCW
//   outer CCW + bot side  → reversed
//   hole CW              → opposite of outer
function emitSideWallTriangles(
  tris: number[],
  contourVerts: number[][],
  isOuter: boolean,
  side: 'top' | 'bottom',
): void {
  const steps = contourVerts.length - 1;
  const n = contourVerts[0].length;
  const flip = (!isOuter) !== (side === 'bottom'); // XOR
  for (let l = 0; l < steps; l++) {
    for (let v = 0; v < n; v++) {
      const vN = (v + 1) % n;
      const A = contourVerts[l][v];
      const B = contourVerts[l][vN];
      const C = contourVerts[l + 1][vN];
      const D = contourVerts[l + 1][v];
      if (flip) {
        tris.push(A, C, B, A, D, C);
      } else {
        tris.push(A, B, C, A, C, D);
      }
    }
  }
}

// Emit triangle indices for one flat cap (top OR bottom face of a piece).
// Uses earcut on the 2D contour + holes, then remaps indices + flips winding
// if the cap's normal points -Z.
function emitCap(
  tris: number[],
  _vp: number[],
  outer: Vec2[],
  holes: Vec2[][],
  outerIdx: number[],
  holeIdx: number[][],
  normalSign: 1 | -1,
): void {
  // Build earcut input: flat [x0,y0, x1,y1, ...] of outer then all holes;
  // `holeStartIndices` marks the first vertex index (in polygon-vertex space,
  // not flat-coord space) of each hole.
  const flat: number[] = [];
  for (const [x, y] of outer) flat.push(x, y);
  const holeStartIndices: number[] = [];
  let vCount = outer.length;
  for (const h of holes) {
    holeStartIndices.push(vCount);
    for (const [x, y] of h) flat.push(x, y);
    vCount += h.length;
  }
  const ec = earcut(flat, holeStartIndices.length ? holeStartIndices : undefined);

  // Map earcut polygon-vertex index → global mesh vertex index.
  const globalFor = (i: number): number => {
    if (i < outer.length) return outerIdx[i];
    let rem = i - outer.length;
    for (let h = 0; h < holes.length; h++) {
      if (rem < holes[h].length) return holeIdx[h][rem];
      rem -= holes[h].length;
    }
    return -1;
  };

  // Earcut produces CCW triangles relative to the polygon's natural orientation
  // (outer CCW, holes CW). For +Z normal the winding is already correct;
  // for -Z flip each triangle.
  for (let i = 0; i < ec.length; i += 3) {
    const a = globalFor(ec[i]);
    const b = globalFor(ec[i + 1]);
    const c = globalFor(ec[i + 2]);
    if (a < 0 || b < 0 || c < 0) continue;
    if (normalSign > 0) tris.push(a, b, c);
    else tris.push(a, c, b);
  }
}

// ====================================================================
// rotate_extrude (3D handler that evaluates children in 2D)
// ====================================================================

function handleRotateExtrude(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const angle = getArgNum(stmt.args, 'angle', -1, ctx, 360);
  const fn = getArgNum(stmt.args, '$fn', -1, ctx, ctx.fn);

  const cleanup: CrossSection[] = [];
  try {
    const cs = evalChildren2D(stmt.children, ctx, cleanup);
    if (!cs) return null;

    const m = cs.revolve(Math.max(fn, 8), angle);
    trackIntermediate(ctx, m);
    return m;
  } finally {
    for (const c of cleanup) {
      try { c.delete(); } catch { /* ok */ }
    }
  }
}

// ====================================================================
// polyhedron (3D from raw vertices + faces)
// ====================================================================

function handlePolyhedron(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const { Manifold, Mesh } = ctx.wasm;
  const pointsArg = getArg(stmt.args, 'points', 0, ctx);
  const facesArg = getArg(stmt.args, 'faces', 1, ctx) ?? getArg(stmt.args, 'triangles', -1, ctx);

  if (!Array.isArray(pointsArg)) {
    evalError(ctx, 'polyhedron() requires a points array', stmt.from, stmt.to);
    return null;
  }
  if (!Array.isArray(facesArg)) {
    evalError(ctx, 'polyhedron() requires a faces array', stmt.from, stmt.to);
    return null;
  }

  // Build vertex properties (flat xyz)
  const vertProperties = new Float32Array(pointsArg.length * 3);
  for (let i = 0; i < pointsArg.length; i++) {
    const p = pointsArg[i] as number[];
    vertProperties[i * 3] = p[0] ?? 0;
    vertProperties[i * 3 + 1] = p[1] ?? 0;
    vertProperties[i * 3 + 2] = p[2] ?? 0;
  }

  // Triangulate faces (fan triangulation for faces with > 3 vertices)
  const triIndices: number[] = [];
  for (const face of facesArg as number[][]) {
    if (face.length < 3) continue;
    for (let i = 1; i < face.length - 1; i++) {
      triIndices.push(face[0], face[i], face[i + 1]);
    }
  }

  try {
    const mesh = new Mesh({
      numProp: 3,
      vertProperties,
      triVerts: new Uint32Array(triIndices),
    });
    const m = new Manifold(mesh);
    trackIntermediate(ctx, m);
    return m;
  } catch (err) {
    evalError(ctx, `polyhedron error: ${(err as Error).message}`, stmt.from, stmt.to);
    return null;
  }
}

// ====================================================================
// import (loads pre-imported STEP/mesh files from context)
// ====================================================================

function handleImport(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const fileArg = getArg(stmt.args, 'file', 0, ctx);
  const filename = typeof fileArg === 'string' ? fileArg : '';
  if (!filename) {
    evalError(ctx, 'import() requires a filename string', stmt.from, stmt.to);
    return null;
  }

  const imported = ctx.importedManifolds.get(filename);
  if (!imported) {
    evalError(ctx, `File not loaded: "${filename}". Use Import Mesh to load files.`, stmt.from, stmt.to);
    return null;
  }

  // Return a copy so each import() call gets its own Manifold for transforms
  const copy = imported.asOriginal();
  trackIntermediate(ctx, copy);
  return copy;
}

// --- 2D-to-3D wrappers (thin extrusion for top-level 2D primitives) ---

function wrap2DTo3D(handler2D: Module2DHandler): ModuleHandler {
  return (stmt, ctx) => {
    const cleanup: CrossSection[] = [];
    try {
      const cs = handler2D(stmt, ctx, cleanup);
      if (!cs) return null;
      const m = cs.extrude(0.01);
      trackIntermediate(ctx, m);
      return m;
    } finally {
      for (const c of cleanup) {
        try { c.delete(); } catch { /* ok */ }
      }
    }
  };
}

// --- Vector Math Utilities ---

function vec3Length(v: Vec3): number {
  return Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]);
}

function vec3Normalize(v: Vec3): Vec3 {
  const len = vec3Length(v);
  if (len < 1e-12) return [0, 0, 0];
  return [v[0] / len, v[1] / len, v[2] / len];
}

function vec3Dot(a: Vec3, b: Vec3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
}

function vec3Cross(a: Vec3, b: Vec3): Vec3 {
  return [
    a[1] * b[2] - a[2] * b[1],
    a[2] * b[0] - a[0] * b[2],
    a[0] * b[1] - a[1] * b[0],
  ];
}

function vec3Negate(v: Vec3): Vec3 {
  return [-v[0], -v[1], -v[2]];
}

/** Rodrigues' rotation formula: 3x3 matrix from axis + angle (radians) */
function mat3FromAxisAngle(axis: Vec3, angle: number): number[] {
  const c = Math.cos(angle);
  const s = Math.sin(angle);
  const t = 1 - c;
  const [x, y, z] = axis;
  return [
    t * x * x + c,     t * x * y - s * z, t * x * z + s * y,
    t * x * y + s * z, t * y * y + c,     t * y * z - s * x,
    t * x * z - s * y, t * y * z + s * x, t * z * z + c,
  ];
}

function mat3MulVec(m: number[], v: Vec3): Vec3 {
  return [
    m[0] * v[0] + m[1] * v[1] + m[2] * v[2],
    m[3] * v[0] + m[4] * v[1] + m[5] * v[2],
    m[6] * v[0] + m[7] * v[1] + m[8] * v[2],
  ];
}

function mat3Mul(a: number[], b: number[]): number[] {
  const r = new Array(9);
  for (let row = 0; row < 3; row++) {
    for (let col = 0; col < 3; col++) {
      r[row * 3 + col] =
        a[row * 3 + 0] * b[0 * 3 + col] +
        a[row * 3 + 1] * b[1 * 3 + col] +
        a[row * 3 + 2] * b[2 * 3 + col];
    }
  }
  return r;
}

/** Convert row-major 3x3 rotation matrix to column-major 4x4 for Manifold .transform() */
function mat3ToMat4Transform(m: number[]): [number,number,number,number, number,number,number,number, number,number,number,number, number,number,number,number] {
  // Manifold .transform() takes column-major Mat4 (last row ignored)
  return [
    m[0], m[3], m[6], 0,  // column 0
    m[1], m[4], m[7], 0,  // column 1
    m[2], m[5], m[8], 0,  // column 2
    0,    0,    0,    1,   // column 3 (translation = 0)
  ];
}

function mat3ToMat4WithTranslation(m: number[], t: Vec3): [number,number,number,number, number,number,number,number, number,number,number,number, number,number,number,number] {
  return [
    m[0], m[3], m[6], 0,
    m[1], m[4], m[7], 0,
    m[2], m[5], m[8], 0,
    t[0], t[1], t[2], 1,
  ];
}

/** Anchor normal: normalized anchor vector, CENTER defaults to [0,0,1] */
function anchorNormal(anchor: number[]): Vec3 {
  const v: Vec3 = [anchor[0] ?? 0, anchor[1] ?? 0, anchor[2] ?? 0];
  const len = vec3Length(v);
  if (len < 1e-12) return [0, 0, 1]; // CENTER → UP
  return [v[0] / len, v[1] / len, v[2] / len];
}

/**
 * Compute a rotation matrix that rotates vector `from` to vector `to`.
 * Both must be unit vectors.
 */
function rotationFromTo(from: Vec3, to: Vec3): number[] {
  const d = vec3Dot(from, to);
  if (d > 1 - 1e-8) {
    // Already aligned — identity
    return [1, 0, 0, 0, 1, 0, 0, 0, 1];
  }
  if (d < -1 + 1e-8) {
    // Anti-parallel: rotate 180° around any perpendicular axis
    let perp: Vec3;
    if (Math.abs(from[0]) < 0.9) {
      perp = vec3Normalize(vec3Cross(from, [1, 0, 0]));
    } else {
      perp = vec3Normalize(vec3Cross(from, [0, 1, 0]));
    }
    return mat3FromAxisAngle(perp, Math.PI);
  }
  const axis = vec3Normalize(vec3Cross(from, to));
  const angle = Math.acos(Math.max(-1, Math.min(1, d)));
  return mat3FromAxisAngle(axis, angle);
}

// --- Anchor / Attach Helpers ---

function computeAnchorPoint(
  bbox: { min: Vec3; max: Vec3 },
  anchor: number[],
  pad: number | number[] = 0,
): Vec3 {
  const px = typeof pad === 'number' ? pad : (pad[0] ?? 0);
  const py = typeof pad === 'number' ? pad : (pad[1] ?? 0);
  const pz = typeof pad === 'number' ? pad : (pad[2] ?? 0);
  const mn: Vec3 = [bbox.min[0] - px, bbox.min[1] - py, bbox.min[2] - pz];
  const mx: Vec3 = [bbox.max[0] + px, bbox.max[1] + py, bbox.max[2] + pz];
  return [
    mn[0] + ((anchor[0] ?? 0) + 1) / 2 * (mx[0] - mn[0]),
    mn[1] + ((anchor[1] ?? 0) + 1) / 2 * (mx[1] - mn[1]),
    mn[2] + ((anchor[2] ?? 0) + 1) / 2 * (mx[2] - mn[2]),
  ];
}

/**
 * Check if a statement is an attach call, possibly wrapped in color().
 * Returns the inner attach statement if found, null otherwise.
 */
const LAYOUT_MODULE_NAMES = new Set(['attach', 'position', 'align']);
// Modules where `anchor=` is a FACE / DIRECTION selector, not a
// positioning instruction. The generic self-anchor wrapper must skip
// these or it will translate the parent shape by a bogus amount.
const SELF_ANCHOR_SKIP = new Set(['hole_pattern', 'bolt_hole', 'nut_hole']);

/**
 * The named `anchor=` of a layout wrapper's single child shape (looking
 * through color() wrappers), or null when it has none.
 */
function explicitChildAnchor(children: Statement[], ctx: EvalContext): number[] | null {
  let s: Statement | undefined = children.length === 1 ? children[0] : undefined;
  while (s && s.type === 'ModuleCallStatement' && s.name === 'color' && s.children.length === 1) s = s.children[0];
  if (!s || s.type !== 'ModuleCallStatement') return null;
  if (!s.args.some(a => a.name === 'anchor')) return null;
  const v = getArg(s.args, 'anchor', -1, ctx);
  return Array.isArray(v) && v.length >= 3 && v.every(n => typeof n === 'number') ? (v as number[]) : null;
}

function unwrapLayoutChild(stmt: Statement): { type: 'attach' | 'position' | 'align'; stmt: ModuleCallStatement } | null {
  if (stmt.type !== 'ModuleCallStatement') return null;
  if (LAYOUT_MODULE_NAMES.has(stmt.name)) {
    return { type: stmt.name as 'attach' | 'position' | 'align', stmt };
  }
  // Walk through color() wrappers to find layout module
  if (stmt.name === 'color' && stmt.children.length === 1) {
    return unwrapLayoutChild(stmt.children[0]);
  }
  return null;
}

/** Modules that are NOT layout parents (their children should not be extracted as position/align) */
const NON_LAYOUT_MODULES = new Set([
  'union', 'difference', 'intersection', 'translate', 'rotate', 'scale',
  'mirror', 'resize',
  'hull', 'minkowski', 'color', 'linear_extrude', 'rotate_extrude',
  'tapered_extrude', 'chamfer', 'fillet',
]);

/**
 * Check if a statement is a layout child (attach/position/align) or a color-wrapped one.
 * When isLayoutParent is false, only attach() is extracted (position/align stay as regular children).
 */
function isAttachLike(stmt: Statement, isLayoutParent = true): boolean {
  const info = unwrapLayoutChild(stmt);
  if (!info) return false;
  if (info.type === 'attach') return true; // attach always extracted
  return isLayoutParent; // position/align only from layout parents
}

function processAttachChildren(
  parentResult: Manifold,
  parentStmt: Statement,
  attachStmts: ModuleCallStatement[],
  ctx: EvalContext,
  phantomParent = false,
): Manifold | null {
  let combined: Manifold | null = phantomParent ? null : parentResult;
  const parentBBox = parentResult.boundingBox();
  const childParts: GeometryPart[] = [];

  // Add parent body as a ghost so it's individually selectable
  // (without this, clicking the parent area selects the entire combined mesh)
  // Skip for phantom parents — they produce no geometry
  if (!phantomParent) {
    const bodyPart: GeometryPart = {
      manifold: parentResult,
      stmt: parentStmt,
      from: parentStmt.from,
      to: parentStmt.to,
      isParentBody: true,
    };
    // Inherit parent's current color context so separate rendering can color the body
    if (ctx.currentColor) {
      bodyPart.color = ctx.currentColor;
      bodyPart.opacity = ctx.currentOpacity;
    }
    childParts.push(bodyPart);
  }

  for (const rawStmt of attachStmts) {
    // Unwrap color() wrappers to get the actual layout statement
    const layoutInfo = unwrapLayoutChild(rawStmt)!;
    const attachStmt = layoutInfo.stmt;
    const layoutType = layoutInfo.type;
    // If the statement is wrapped (e.g. color(...) attach(...)), apply
    // the wrapper's color/opacity context while evaluating the attach children.
    const isWrapped = rawStmt !== attachStmt;
    let wrapperColor: [number, number, number] | undefined;
    let wrapperOpacity: number | undefined;
    let prevColor: [number, number, number] | undefined;
    let prevOpacity: number | undefined;

    if (isWrapped && rawStmt.type === 'ModuleCallStatement' && rawStmt.name === 'color') {
      const colorArg = getArg(rawStmt.args, 'c', 0, ctx);
      const alphaArg = getArg(rawStmt.args, 'alpha', 1, ctx) as number | undefined;
      const parsed = parseColor(colorArg);
      if (parsed) wrapperColor = parsed;
      if (typeof alphaArg === 'number') wrapperOpacity = Math.min(1, Math.max(0, alphaArg));
      // Save and apply color context
      prevColor = ctx.currentColor;
      prevOpacity = ctx.currentOpacity;
      if (wrapperColor) ctx.currentColor = wrapperColor;
      if (wrapperOpacity !== undefined) ctx.currentOpacity = wrapperOpacity;
    }

    // Common params shared by all layout types
    const mode = getArg(attachStmt.args, 'mode', -1, ctx) as string ?? 'add';
    const extend = getArg(attachStmt.args, 'extend', -1, ctx) as number[] ?? [0, 0, 0];
    const padArg = getArg(attachStmt.args, 'pad', -1, ctx) ?? 0;
    const pad = typeof padArg === 'number' ? padArg : (Array.isArray(padArg) ? padArg as number[] : 0);
    const axis = getArg(attachStmt.args, 'axis', -1, ctx) as number[] ?? [1, 1, 1];

    // Snapshot attachChildParts keys to detect nested attach children added during eval
    const keysBefore = new Set(ctx.attachChildParts.keys());
    const groupsBefore = ctx.groups.length;

    // Track debug manifolds and frame boxes added by children so we can re-translate them
    const debugBefore = ctx.debugManifolds.length;
    const frameBoxesBefore = ctx.frameBoxes.length;
    ctx.phantomBBox = null;
    const child = evalChildren(attachStmt.children, ctx);

    // Restore color context after evaluating children
    if (isWrapped) {
      ctx.currentColor = prevColor;
      ctx.currentOpacity = prevOpacity;
    }

    if (!child) continue;

    // Use the frame's phantom bbox (if a frame was evaluated) so that
    // the offset is computed from the virtual box, not the children's bbox.
    const childBBox = ctx.phantomBBox ?? child.boundingBox();
    ctx.phantomBBox = null;

    // --- Dispatch on layout type to compute offset and optional rotation ---
    let offset: Vec3;
    let positioned: Manifold;
    // For attach(), store rotation matrix to apply to nested ghosts/debug manifolds
    let rotationMat: number[] | null = null;
    // For frame box repositioning, we need the 'from' anchor used for child alignment
    let fromAnchorForFrameBoxes: number[];

    if (layoutType === 'position') {
      // position(anchor): place the child's anchor point (its own named
      // `anchor=` argument, default CENTER) on the parent's anchor point —
      // e.g. position(TOP) cube(4, anchor = BOTTOM) stands on the top face.
      const anchor = getArg(attachStmt.args, 'anchor', 0, ctx) as number[] ?? [0, 0, 0];
      const toPoint = computeAnchorPoint(parentBBox, anchor, pad);
      const childAnchor = explicitChildAnchor(attachStmt.children, ctx) ?? [0, 0, 0];
      const childCenter = computeAnchorPoint(childBBox, childAnchor);

      const rawOffset: Vec3 = [
        toPoint[0] - childCenter[0] + (extend[0] ?? 0),
        toPoint[1] - childCenter[1] + (extend[1] ?? 0),
        toPoint[2] - childCenter[2] + (extend[2] ?? 0),
      ];
      offset = [
        (axis[0] ?? 1) ? rawOffset[0] : 0,
        (axis[1] ?? 1) ? rawOffset[1] : 0,
        (axis[2] ?? 1) ? rawOffset[2] : 0,
      ];
      positioned = child.translate(offset);
      trackIntermediate(ctx, positioned);
      fromAnchorForFrameBoxes = [0, 0, 0];

    } else if (layoutType === 'align') {
      // align(face, [alignment], inset=): flush child face onto parent face
      const faceAnchor = getArg(attachStmt.args, 'anchor', 0, ctx) as number[] ?? [0, 0, 0];
      const alignmentArg = getArg(attachStmt.args, 'alignment', 1, ctx) as number[] | undefined;
      const insetArg = getArg(attachStmt.args, 'inset', -1, ctx) as number | undefined;

      // Build combined parent anchor: face + alignment
      const combinedAnchor: number[] = [
        faceAnchor[0] ?? 0,
        faceAnchor[1] ?? 0,
        faceAnchor[2] ?? 0,
      ];
      if (alignmentArg) {
        for (let i = 0; i < 3; i++) {
          if ((combinedAnchor[i] ?? 0) === 0) {
            combinedAnchor[i] = alignmentArg[i] ?? 0;
          }
        }
      }

      const parentPoint = computeAnchorPoint(parentBBox, combinedAnchor, pad);

      // Auto-anchor: negate face components, keep alignment components
      const childAnchor: number[] = [0, 0, 0];
      for (let i = 0; i < 3; i++) {
        const fv = faceAnchor[i] ?? 0;
        const av = alignmentArg ? (alignmentArg[i] ?? 0) : 0;
        if (fv !== 0) {
          childAnchor[i] = (mode === 'cut' || mode === 'intersect') ? fv : -fv;
        } else {
          childAnchor[i] = av;  // Same alignment
        }
      }

      const childPoint = computeAnchorPoint(childBBox, childAnchor);
      const rawOffset: Vec3 = [
        parentPoint[0] - childPoint[0] + (extend[0] ?? 0),
        parentPoint[1] - childPoint[1] + (extend[1] ?? 0),
        parentPoint[2] - childPoint[2] + (extend[2] ?? 0),
      ];

      // Apply inset: shift toward center along alignment directions
      if (insetArg && insetArg !== 0 && alignmentArg) {
        for (let i = 0; i < 3; i++) {
          const av = alignmentArg[i] ?? 0;
          if (av !== 0 && (faceAnchor[i] ?? 0) === 0) {
            rawOffset[i] -= av * insetArg;
          }
        }
      }

      // Shift child slightly along face normal to avoid coincident-face CSG artifacts
      if (mode === 'cut') {
        const cutExtend = 0.01;
        const fn = anchorNormal(faceAnchor);
        rawOffset[0] += fn[0] * cutExtend;
        rawOffset[1] += fn[1] * cutExtend;
        rawOffset[2] += fn[2] * cutExtend;
      }

      offset = [
        (axis[0] ?? 1) ? rawOffset[0] : 0,
        (axis[1] ?? 1) ? rawOffset[1] : 0,
        (axis[2] ?? 1) ? rawOffset[2] : 0,
      ];
      positioned = child.translate(offset);
      trackIntermediate(ctx, positioned);
      fromAnchorForFrameBoxes = childAnchor;

    } else {
      // attach(parent_anchor, child_anchor, overlap=, spin=):
      // Reorient child so its face normal opposes parent's face normal
      const parentAnchor = getArg(attachStmt.args, 'anchor', 0, ctx) as number[] ?? [0, 0, 0];
      const childAnchorArg = getArg(attachStmt.args, 'from', 1, ctx) as number[] | undefined;
      const overlapArg = getArg(attachStmt.args, 'overlap', -1, ctx) as number ?? 0;
      const spinArg = getArg(attachStmt.args, 'spin', -1, ctx) as number ?? 0;
      const alignArg = getArg(attachStmt.args, 'align', -1, ctx) as number[] | undefined;

      // Default child_anchor: if none given, use CENTER (normal=[0,0,1])
      const childAnchorVec = childAnchorArg ?? [0, 0, 0];

      // Compute parent face normal
      const pn = anchorNormal(parentAnchor);

      // Step 1: Rotate child so its UP axis aligns with parent face normal.
      // The child_anchor only determines the attachment POINT, not orientation.
      const mainRot = rotationFromTo([0, 0, 1] as Vec3, pn);

      // Step 2: Spin rules — fix the remaining DOF around the attachment axis
      // After main rotation, find where child's reference direction ended up
      // and where we want it to be, then correct.
      let spinCorrection = 0;
      const attachAxis = pn; // The axis we're attaching along

      // Choose reference direction based on parent anchor:
      // TOP/BOTTOM (attachment axis ~= Z): reference = FRONT [0,-1,0]
      // Side faces: reference = UP [0,0,1]
      const isVertical = Math.abs(pn[2]) > 0.9;
      const desiredRef: Vec3 = isVertical ? [0, -1, 0] : [0, 0, 1];

      // Project desired reference onto plane perpendicular to attachment axis
      const desiredProj = vec3Normalize([
        desiredRef[0] - vec3Dot(desiredRef, attachAxis) * attachAxis[0],
        desiredRef[1] - vec3Dot(desiredRef, attachAxis) * attachAxis[1],
        desiredRef[2] - vec3Dot(desiredRef, attachAxis) * attachAxis[2],
      ]);

      // What the child's reference direction becomes after main rotation
      // For vertical: FRONT stays meaningful; for sides: use Y axis (won't degenerate)
      const childRef: Vec3 = isVertical ? [0, -1, 0] : [0, 1, 0];
      const rotatedChildRef = mat3MulVec(mainRot, childRef);
      const currentProj = vec3Normalize([
        rotatedChildRef[0] - vec3Dot(rotatedChildRef, attachAxis) * attachAxis[0],
        rotatedChildRef[1] - vec3Dot(rotatedChildRef, attachAxis) * attachAxis[1],
        rotatedChildRef[2] - vec3Dot(rotatedChildRef, attachAxis) * attachAxis[2],
      ]);

      // Compute angle between current and desired projections
      if (vec3Length(desiredProj) > 1e-6 && vec3Length(currentProj) > 1e-6) {
        const dot = Math.max(-1, Math.min(1, vec3Dot(currentProj, desiredProj)));
        spinCorrection = Math.acos(dot);
        // Determine sign using cross product
        const cross = vec3Cross(currentProj, desiredProj);
        if (vec3Dot(cross, attachAxis) < 0) spinCorrection = -spinCorrection;
      }

      // Combine: main rotation + spin correction + user spin
      const totalSpin = spinCorrection + (spinArg * Math.PI / 180);
      const spinRot = mat3FromAxisAngle(attachAxis, totalSpin);
      const finalRot = mat3Mul(spinRot, mainRot);
      rotationMat = finalRot;

      // Step 3: Rotate child using direct matrix transform (avoids Euler gimbal lock)
      const rotatedChild = child.transform(mat3ToMat4Transform(finalRot));
      trackIntermediate(ctx, rotatedChild);

      // Step 4: Compute offset after rotation
      const rotatedBBox = rotatedChild.boundingBox();
      // Combine face anchor with align for position (face components take priority)
      let positionAnchor = parentAnchor;
      if (alignArg) {
        positionAnchor = [
          (parentAnchor[0] ?? 0) !== 0 ? (parentAnchor[0] ?? 0) : (alignArg[0] ?? 0),
          (parentAnchor[1] ?? 0) !== 0 ? (parentAnchor[1] ?? 0) : (alignArg[1] ?? 0),
          (parentAnchor[2] ?? 0) !== 0 ? (parentAnchor[2] ?? 0) : (alignArg[2] ?? 0),
        ];
      }
      const parentPoint = computeAnchorPoint(parentBBox, positionAnchor, pad);
      // The child anchor vector must be rotated through the same rotation as the child,
      // so BOTTOM (which was [0,0,-1]) maps to its new direction in the rotated bbox.
      const rotatedChildAnchor = mat3MulVec(finalRot, childAnchorVec as Vec3);
      const rotatedChildAnchorPt = computeAnchorPoint(rotatedBBox, rotatedChildAnchor);

      const rawOffset: Vec3 = [
        parentPoint[0] - rotatedChildAnchorPt[0] + (extend[0] ?? 0),
        parentPoint[1] - rotatedChildAnchorPt[1] + (extend[1] ?? 0),
        parentPoint[2] - rotatedChildAnchorPt[2] + (extend[2] ?? 0),
      ];

      // Apply overlap: shift child toward parent along parent normal
      if (overlapArg !== 0) {
        rawOffset[0] -= pn[0] * overlapArg;
        rawOffset[1] -= pn[1] * overlapArg;
        rawOffset[2] -= pn[2] * overlapArg;
      }

      // For cut mode, extend cutter slightly past the parent surface
      // to avoid coincident faces which cause CSG rendering artifacts
      if (mode === 'cut') {
        const cutExtend = 0.01;
        rawOffset[0] += pn[0] * cutExtend;
        rawOffset[1] += pn[1] * cutExtend;
        rawOffset[2] += pn[2] * cutExtend;
      }

      offset = [
        (axis[0] ?? 1) ? rawOffset[0] : 0,
        (axis[1] ?? 1) ? rawOffset[1] : 0,
        (axis[2] ?? 1) ? rawOffset[2] : 0,
      ];
      positioned = rotatedChild.translate(offset);
      trackIntermediate(ctx, positioned);
      fromAnchorForFrameBoxes = childAnchorVec;
    }

    // Position frame boxes using their own bounds so the phantom cube
    // stays anchored to the parent face regardless of child positions
    for (let i = frameBoxesBefore; i < ctx.frameBoxes.length; i++) {
      const fb = ctx.frameBoxes[i];
      const fbFrom = computeAnchorPoint(fb, fromAnchorForFrameBoxes);
      const anchor0 = getArg(attachStmt.args, 'anchor', 0, ctx) as number[] ?? [0, 0, 0];
      const toPoint0 = computeAnchorPoint(parentBBox, anchor0, pad);
      const rawFbOff: Vec3 = [
        toPoint0[0] - fbFrom[0] + (extend[0] ?? 0),
        toPoint0[1] - fbFrom[1] + (extend[1] ?? 0),
        toPoint0[2] - fbFrom[2] + (extend[2] ?? 0),
      ];
      const fbOff: Vec3 = [
        (axis[0] ?? 1) ? rawFbOff[0] : 0,
        (axis[1] ?? 1) ? rawFbOff[1] : 0,
        (axis[2] ?? 1) ? rawFbOff[2] : 0,
      ];
      fb.min = [fb.min[0] + fbOff[0], fb.min[1] + fbOff[1], fb.min[2] + fbOff[2]];
      fb.max = [fb.max[0] + fbOff[0], fb.max[1] + fbOff[1], fb.max[2] + fbOff[2]];
    }

    // Boolean/group and module-instance ghosts created by the child: move them
    // with the child (nested attach groups are re-collected below instead).
    for (const g of groupsSince(ctx, groupsBefore)) {
      if (g.kind === 'attach') continue;
      transformGroupParts(g.parts, rotationMat ? mat3ToMat4WithTranslation(rotationMat, offset) : mat4Translate(offset));
    }

    // Save positioned child for ghost mesh selection.
    // Use body-only manifold (before nested attach children were merged) so the
    // ghost represents only this level's geometry, not all descendants.
    let ghostManifold = positioned;
    if (attachStmt.children.length === 1) {
      const bodyOnly = ctx.bodyOnlyResults.get(attachStmt.children[0]);
      if (bodyOnly) {
        if (rotationMat) {
          const rotBody = bodyOnly.transform(mat3ToMat4Transform(rotationMat));
          trackIntermediate(ctx, rotBody);
          ghostManifold = rotBody.translate(offset);
        } else {
          ghostManifold = bodyOnly.translate(offset);
        }
        trackIntermediate(ctx, ghostManifold);
      }
    }
    // Use the outer wrapper statement for source range (so color(...) attach(...) is fully covered)
    const attachColor = wrapperColor
      ? { color: wrapperColor, opacity: wrapperOpacity }
      : ctx.manifoldColors.get(child!);
    const part: GeometryPart = {
      manifold: ghostManifold,
      fullManifold: ghostManifold !== positioned ? positioned : undefined,
      stmt: rawStmt,
      from: rawStmt.from,
      to: rawStmt.to,
    };
    if (mode === 'cut' || mode === 'intersect') {
      part.isCut = true;
    }
    if (attachColor) {
      part.color = attachColor.color;
      part.opacity = attachColor.opacity;
    }

    // Collect nested attach children added during evalChildren (deeper attach chains).
    // Re-translate them by our offset so they're in the parent's coordinate space,
    // then remove consumed entries so higher levels don't double-translate.
    const newKeys: Statement[] = [];
    for (const key of ctx.attachChildParts.keys()) {
      if (!keysBefore.has(key)) newKeys.push(key);
    }
    const newlyCollected: GeometryPart[] = [];
    for (const key of newKeys) {
      const nestedParts = ctx.attachChildParts.get(key)!;
      for (const nested of nestedParts) {
        let repositioned: Manifold;
        if (rotationMat) {
          const rotated = nested.manifold.transform(mat3ToMat4Transform(rotationMat));
          trackIntermediate(ctx, rotated);
          repositioned = rotated.translate(offset);
        } else {
          repositioned = nested.manifold.translate(offset);
        }
        trackIntermediate(ctx, repositioned);

        let fullRepositioned: Manifold | undefined;
        if (nested.fullManifold) {
          if (rotationMat) {
            const rotFull = nested.fullManifold.transform(mat3ToMat4Transform(rotationMat));
            trackIntermediate(ctx, rotFull);
            fullRepositioned = rotFull.translate(offset);
          } else {
            fullRepositioned = nested.fullManifold.translate(offset);
          }
          trackIntermediate(ctx, fullRepositioned);
        }

        const nPart: GeometryPart = {
          manifold: repositioned,
          fullManifold: fullRepositioned,
          stmt: nested.stmt,
          from: nested.from,
          to: nested.to,
          isAttach: true,
          isParentBody: nested.isParentBody,
          // Preserve the nested part's own cut flag — a cylinder that cuts
          // from the inner cube is still a cutter after being collected by an
          // outer ADD-mode layout. Losing this flag caused the cutter to
          // render as a visible colored overlay on top of the hole.
          isCut: nested.isCut,
        };
        // When the OUTER mode is cut/intersect, also mark the nested parent
        // body as cut so the viewport keeps the combined mesh visible.
        if ((mode === 'cut' || mode === 'intersect') && nested.isParentBody) {
          nPart.isCut = true;
        }
        // Copy color from nested part, or look it up from manifold
        if (nested.color) {
          nPart.color = nested.color;
          nPart.opacity = nested.opacity;
        } else {
          const nestedColor = ctx.manifoldColors.get(nested.manifold);
          if (nestedColor) {
            nPart.color = nestedColor.color;
            nPart.opacity = nestedColor.opacity;
          }
        }
        childParts.push(nPart);
        newlyCollected.push(nPart);
      }
      ctx.attachChildParts.delete(key);
      for (const g of ctx.groups) if (g.parts === nestedParts) g.consumed = true;
    }

    // Only add the combined part when no nested parts were collected.
    // When nested parts exist, they represent the same geometry broken into
    // individual pieces — adding the combined part would double-render.
    if (newlyCollected.length === 0) {
      childParts.push(part);
    }

    if (mode === 'cut') {
      // Subtract the full positioned child (body + descendants).
      // Then add back nested attach children so they appear inside the opening.
      // Net effect: body is subtracted, nested children are preserved.
      // Skip if combined is null (phantom parent — nothing to cut from)
      if (combined) {
        let next = combined.subtract(positioned);
        trackIntermediate(ctx, next);

        for (const np of newlyCollected) {
          if (np.isParentBody) continue;
          const addM = np.fullManifold ?? np.manifold;
          next = next.add(addM);
          trackIntermediate(ctx, next);
        }
        combined = next;
      }
    } else if (mode === 'intersect') {
      // Skip if combined is null (phantom parent — nothing to intersect with)
      if (combined) {
        const next = combined.intersect(positioned);
        trackIntermediate(ctx, next);
        combined = next;
      }
    } else {
      if (combined) {
        const next = combined.add(positioned);
        trackIntermediate(ctx, next);
        combined = next;
      } else {
        combined = positioned;
      }
    }

    // Re-translate (and rotate for attach) debug manifolds added by children
    for (let i = debugBefore; i < ctx.debugManifolds.length; i++) {
      let transformed: Manifold;
      if (rotationMat) {
        const rotated = ctx.debugManifolds[i].transform(mat3ToMat4Transform(rotationMat));
        trackIntermediate(ctx, rotated);
        transformed = rotated.translate(offset);
      } else {
        transformed = ctx.debugManifolds[i].translate(offset);
      }
      trackIntermediate(ctx, transformed);
      ctx.debugManifolds[i] = transformed;
    }

    // Handle # on the attach statement itself (e.g. #attach(TOP) sphere())
    if (attachStmt.debug) {
      ctx.debugManifolds.push(positioned);
    }
  }

  // Store attach child parts for later retrieval
  if (childParts.length > 0) {
    ctx.attachChildParts.set(parentStmt, childParts);
    registerGroup(ctx, { kind: 'attach', op: 'union', parts: childParts });
  }

  return combined;
}

// --- Align / Group Handlers ---

function handleAlign(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const anchor = getArg(stmt.args, 'anchor', 0, ctx) as number[] ?? [0, 0, 0];
  const from = getArg(stmt.args, 'from', -1, ctx) as number[] | undefined;
  const axis = getArg(stmt.args, 'axis', -1, ctx) as number[] ?? [1, 1, 1];
  const alignAnchor = from ?? anchor;
  // Snapshot which nested-attach/bool parts exist BEFORE child eval so we can
  // translate the newly-added ones by the same offset (otherwise their
  // manifolds stay in the child's pre-align frame and ghost-select highlights
  // appear in the wrong place).
  const groupsBefore = ctx.groups.length;
  const child = evalChildren(stmt.children, ctx);
  if (!child) return null;
  const bbox = child.boundingBox();
  const anchorPt = computeAnchorPoint(bbox, alignAnchor);
  const offset: Vec3 = [
    (axis[0] ?? 1) ? -anchorPt[0] : 0,
    (axis[1] ?? 1) ? -anchorPt[1] : 0,
    (axis[2] ?? 1) ? -anchorPt[2] : 0,
  ];
  const m = child.translate(offset);
  trackIntermediate(ctx, m);
  for (const g of groupsSince(ctx, groupsBefore)) transformGroupParts(g.parts, mat4Translate(offset));
  // Forward color tag from child — translate creates a new manifold object so
  // ctx.manifoldColors.get(m) would otherwise miss the color set during child eval.
  const childColor = ctx.manifoldColors.get(child);
  if (childColor) ctx.manifoldColors.set(m, childColor);
  forwardSubGroup(ctx, child, m);
  return m;
}

/** Standalone position() is a no-op — just evaluate children.
 *  Meaningful positioning only happens inside a layout parent's {} block. */
function handlePosition(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  return evalChildren(stmt.children, ctx);
}

function handleGroup(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const children: Manifold[] = [];
  const childParts: GeometryPart[] = [];
  for (const child of stmt.children) {
    const m = evalStatement(child, ctx);
    if (m) {
      children.push(m);
      const part: GeometryPart = { manifold: m, stmt: child, from: child.from, to: child.to };
      const childColor = ctx.manifoldColors.get(m);
      if (childColor) {
        part.color = childColor.color;
        part.opacity = childColor.opacity;
      }
      childParts.push(part);
    }
  }

  // Store child parts for ghost mesh selection (union semantics)
  if (childParts.length > 1) {
    ctx.booleanChildParts.set(stmt, registerGroup(ctx, { kind: 'bool', op: 'union', parts: childParts }));
  }

  if (children.length === 0) return null;
  if (children.length === 1) return children[0];
  // Implicit union
  let combined = children[0];
  for (let i = 1; i < children.length; i++) {
    const next = combined.add(children[i]);
    trackIntermediate(ctx, next);
    combined = next;
  }
  inheritColor(ctx, combined, 'union', children);
  const grp = ctx.booleanChildParts.get(stmt) as GhostGroup | undefined;
  if (grp) ctx.subGroupOf.set(combined, grp);
  return combined;
}

const MODULE_HANDLERS: Record<string, ModuleHandler> = {
  cube: handleCube,
  sphere: handleSphere,
  cylinder: handleCylinder,
  polyhedron: handlePolyhedron,
  union: handleBoolean('union'),
  difference: handleBoolean('difference'),
  intersection: handleBoolean('intersection'),
  translate: handleTranslate,
  rotate: handleRotate,
  multmatrix: handleMultmatrix,
  scale: handleScale,
  mirror: handleMirror,
  resize: handleResize,
  hull: handleHull,
  minkowski: handleMinkowski,
  children: handleChildrenModule,
  align: handleAlign,
  position: handlePosition,
  group: handleGroup,
  linear_extrude: handleLinearExtrude,
  rotate_extrude: handleRotateExtrude,
  tapered_extrude: handleTaperedExtrude,
  chamfer: handleChamfer,
  fillet: handleFillet,
  import: handleImport,
  color: handleColor,
  circle: wrap2DTo3D(handleCircle2D),
  square: wrap2DTo3D(handleSquare2D),
  polygon: wrap2DTo3D(handlePolygon2D),
  chamfered_cylinder: handleChamferedCylinder,
  spur_gear: handleSpurGear,
  helical_gear: handleHelicalGear,
  herringbone_gear: handleHerringboneGear,
  rack: handleRack,
  bevel_gear: handleBevelGear,
  worm: handleWorm,
  l_profile: handleLProfile,
  u_profile: handleUProfile,
  o_profile: handleOProfile,
  t_profile: handleTProfile,
  i_profile: handleIProfile,
  torus: handleTorus,
  pipe: handlePipe,
  ellipsoid: handleEllipsoid,
  wedge: handleWedge,
  capsule: handleCapsule,
  rounded_cube: handleRoundedCube,
  thread: handleThread,
  spring: handleSpring,
  bolt: handleBolt,
  nut: handleNut,
  enclosure: handleEnclosure,
  standoff: handleStandoff,
  shell: handleShell,
  hole_pattern: handleHolePattern,
  bolt_hole: handleBoltHole,
  nut_hole: handleNutHole,
  regular_polygon: wrap2DTo3D(handleRegularPolygon2D),
  hexagon: wrap2DTo3D(handleHexagon2D),
  slot: wrap2DTo3D(handleSlot2D),
  star: wrap2DTo3D(handleStar2D),
  plus: wrap2DTo3D(handlePlus2D),
  cross: wrap2DTo3D(handlePlus2D),
  x: wrap2DTo3D(handleX2D),
  bowtie: wrap2DTo3D(handleBowtie2D),
  diamond: wrap2DTo3D(handleDiamond2D),
  rhombus: wrap2DTo3D(handleDiamond2D),
  radial: wrap2DTo3D(handleRadial2D),
  spokes: wrap2DTo3D(handleRadial2D),
  text: wrap2DTo3D(handleText2D),
  offset: wrap2DTo3D(handleOffset2D),
  echo: (stmt: ModuleCallStatement, ctx: EvalContext) => {
    const values = stmt.args.map(a => {
      const val = evalExpr(a.value, ctx);
      return a.name ? `${a.name} = ${JSON.stringify(val)}` : JSON.stringify(val);
    });
    const msg = 'ECHO: ' + values.join(', ');
    console.log(msg);
    ctx.echoLog.push(msg);
    return evalChildren(stmt.children, ctx);
  },
  assert: (stmt: ModuleCallStatement, ctx: EvalContext) => {
    const condition = stmt.args.length > 0 ? evalExpr(stmt.args[0].value, ctx) : true;
    if (!condition) {
      const msg = stmt.args.length > 1 ? String(evalExpr(stmt.args[1].value, ctx)) : 'Assertion failed';
      evalError(ctx, msg, stmt.from, stmt.to);
    }
    return evalChildren(stmt.children, ctx);
  },
};

// --- User Module Calls ---

function callUserModule(
  mod: UserModule,
  call: ModuleCallStatement,
  ctx: EvalContext,
): Manifold | null {
  // Check recursion depth
  if (ctx.recursionDepth >= MAX_RECURSION_DEPTH) {
    evalError(ctx, `Maximum recursion depth (${MAX_RECURSION_DEPTH}) exceeded`, call.from, call.to);
    console.error(`SCAD Error: Maximum recursion depth (${MAX_RECURSION_DEPTH}) exceeded at module '${call.name}'`);
    return null;
  }
  ctx.recursionDepth++;

  // Save and create new scope
  const prevVars = ctx.vars;
  const prevCallerChildren = ctx.callerChildren;
  const newVars = new Map(prevVars);

  // Bind parameters from call args
  for (let i = 0; i < mod.params.length; i++) {
    const param = mod.params[i];
    const arg = getArg(call.args, param.name, i, ctx);
    if (arg !== undefined) {
      newVars.set(param.name, arg);
    } else if (param.defaultValue) {
      newVars.set(param.name, evalExpr(param.defaultValue, ctx));
    }
  }

  ctx.vars = newVars;
  ctx.callerChildren = call.children;

  // Evaluate the body statement by statement (same union order as
  // evalChildren) so each shape inside the module becomes selectable, tagged
  // with this call's instance path.
  const groupsBefore = ctx.groups.length;
  const prevPath = ctx.instancePath;
  const instance = (prevPath ? prevPath + '/' : '') + call.from.offset;
  ctx.instancePath = instance;
  hoistDefinitions(mod.body, ctx);
  const results: Manifold[] = [];
  const bodyParts: GeometryPart[] = [];
  for (const child of mod.body) {
    const m = evalStatement(child, ctx);
    if (!m) continue;
    results.push(m);
    const part: GeometryPart = { manifold: m, stmt: child, from: child.from, to: child.to, instance };
    const c = ctx.manifoldColors.get(m);
    if (c) { part.color = c.color; part.opacity = c.opacity; }
    bodyParts.push(part);
  }
  let result: Manifold | null = results[0] ?? null;
  for (let i = 1; i < results.length; i++) {
    const next: Manifold = result!.add(results[i]);
    trackIntermediate(ctx, next);
    result = next;
  }
  if (result && results.length > 1) inheritColor(ctx, result, 'union', results);
  ctx.instancePath = prevPath;

  // Parts created during this call that no deeper call already tagged
  const nested = ctx.groups.slice(groupsBefore);
  for (const g of nested) for (const p of g.parts) p.instance ??= instance;
  // A single-statement body's result IS the module's return value, which the
  // caller wraps (translate(…) m()). Exposing it as a ghost while it is still
  // a lazy CSG node lets evaluating the wrapper rewrite the shared node and
  // empty the ghost — compute it once here so callers wrap finished geometry.
  if (bodyParts.length === 1 && result) result.numVert();
  if (bodyParts.length > 0) {
    const modGroup = registerGroup(ctx, { kind: 'module', op: 'union', parts: bodyParts, nested });
    ctx.moduleInstances.set(call, modGroup);
    if (result && bodyParts.length > 1) ctx.subGroupOf.set(result, modGroup);
  }

  // Restore scope and recursion depth
  ctx.vars = prevVars;
  ctx.callerChildren = prevCallerChildren;
  ctx.recursionDepth--;

  return result;
}

// --- Statement Evaluation ---

const MAX_LOOP_ITERATIONS = 10000;

function evalForStatement(stmt: ForStatement, ctx: EvalContext): Manifold | null {
  const rangeVal = evalExpr(stmt.range, ctx);
  if (!Array.isArray(rangeVal)) {
    evalError(ctx, 'for() range must be an array or range expression', stmt.from, stmt.to);
    return null;
  }
  if (rangeVal.length > MAX_LOOP_ITERATIONS) {
    evalError(ctx, `for() loop exceeds maximum iterations (${MAX_LOOP_ITERATIONS})`, stmt.from, stmt.to);
    console.error(`SCAD Error: for() loop exceeds maximum iterations (${MAX_LOOP_ITERATIONS})`);
    return null;
  }
  const results: Manifold[] = [];
  const prevVars = ctx.vars;
  for (const item of rangeVal) {
    const childVars = new Map(prevVars);
    childVars.set(stmt.variable, item);
    ctx.vars = childVars;
    for (const child of stmt.body) {
      const m = evalStatement(child, ctx);
      if (m) results.push(m);
    }
  }
  ctx.vars = prevVars;
  if (results.length === 0) return null;
  if (results.length === 1) {
    if (stmt.debug || stmt.background) ctx.debugManifolds.push(results[0]);
    return results[0];
  }
  let combined = results[0];
  for (let i = 1; i < results.length; i++) {
    const next = combined.add(results[i]);
    trackIntermediate(ctx, next);
    combined = next;
  }
  if (stmt.debug || stmt.background) ctx.debugManifolds.push(combined);
  return combined;
}

function handleFrameStatement(stmt: ModuleCallStatement, ctx: EvalContext): Manifold | null {
  const { Manifold } = ctx.wasm;

  // Parse size argument
  const sizeArg = getArg(stmt.args, 'size', 0, ctx);
  let sx: number, sy: number, sz: number;
  if (Array.isArray(sizeArg)) {
    sx = (sizeArg[0] as number) || 10;
    sy = (sizeArg[1] as number) || 10;
    sz = (sizeArg[2] as number) || 10;
  } else {
    const s = (sizeArg as number) || 10;
    sx = sy = sz = s;
  }

  // Create phantom cube (centered) — used only for bbox, not rendered
  const phantomCube = Manifold.cube([sx, sy, sz], true);
  trackIntermediate(ctx, phantomCube);

  // Record frame box for wireframe visualization
  ctx.frameBoxes.push({
    min: [-sx / 2, -sy / 2, -sz / 2],
    max: [sx / 2, sy / 2, sz / 2],
  });

  // Partition children: body vs layout children (frame is a layout parent)
  const attachStmts: ModuleCallStatement[] = [];
  const bodyChildren: Statement[] = [];
  for (const c of stmt.children) {
    if (isAttachLike(c, true)) {
      attachStmts.push(c as ModuleCallStatement);
    } else {
      bodyChildren.push(c);
    }
  }

  // Evaluate body children and union them
  let bodyResult: Manifold | null = null;
  const bodyParts: GeometryPart[] = [];
  for (const child of bodyChildren) {
    const m = evalStatement(child, ctx);
    if (m) {
      const part: GeometryPart = { manifold: m, stmt: child, from: child.from, to: child.to };
      const childColor = ctx.manifoldColors.get(m);
      if (childColor) {
        part.color = childColor.color;
        part.opacity = childColor.opacity;
      }
      bodyParts.push(part);
      if (bodyResult) {
        bodyResult = bodyResult.add(m);
        trackIntermediate(ctx, bodyResult);
      } else {
        bodyResult = m;
      }
    }
  }

  // Store body parts for ghost mesh selection
  if (bodyParts.length > 1) {
    ctx.booleanChildParts.set(stmt, registerGroup(ctx, { kind: 'bool', op: 'union', parts: bodyParts }));
  }

  // Process attach children against phantom cube's bbox
  let attachResult: Manifold | null = null;
  if (attachStmts.length > 0) {
    attachResult = processAttachChildren(phantomCube, stmt, attachStmts, ctx, true);
  }

  // Combine body + attach results
  let finalResult: Manifold | null;
  if (bodyResult && attachResult) {
    const combined = bodyResult.add(attachResult);
    trackIntermediate(ctx, combined);
    finalResult = combined;
  } else {
    finalResult = bodyResult ?? attachResult;
  }

  // Expose the phantom cube's bbox so the outer processAttachChildren
  // uses it (instead of the children's bbox) for offset computation.
  ctx.phantomBBox = { min: [-sx / 2, -sy / 2, -sz / 2], max: [sx / 2, sy / 2, sz / 2] };

  return finalResult;
}

function evalStatement(stmt: Statement, ctx: EvalContext): Manifold | null {
  checkTimeout(ctx);
  if (stmt.type === 'AssignmentStatement') {
    const val = evalExpr(stmt.value, ctx);
    ctx.vars.set(stmt.name, val);
    return null;
  }

  if (stmt.type === 'ModuleDefinitionStatement') {
    ctx.modules.set(stmt.name, { params: stmt.params, body: stmt.body });
    return null;
  }

  if (stmt.type === 'FunctionDefinitionStatement') {
    ctx.functions.set(stmt.name, { params: stmt.params, body: stmt.body });
    return null;
  }

  if (stmt.type === 'ForStatement') {
    return evalForStatement(stmt, ctx);
  }

  if (stmt.type === 'IfStatement') {
    const cond = evalExpr(stmt.condition, ctx);
    const body = cond ? stmt.thenBody : stmt.elseBody;
    return evalChildren(body, ctx);
  }

  if (stmt.type === 'ModuleCallStatement') {
    // frame() handles its own attach children with a phantom bounding box
    if (stmt.name === 'frame') {
      const result = handleFrameStatement(stmt, ctx);
      if (result && stmt.debug) {
        ctx.debugManifolds.push(result);
      }
      return result;
    }

    // Partition children into regular and layout children (attach/position/align)
    // position/align are only extracted from layout parents (primitives, group, user modules)
    const isLayoutParent = !NON_LAYOUT_MODULES.has(stmt.name);
    const attachStmts: ModuleCallStatement[] = [];
    const nonAttachChildren: Statement[] = [];
    for (const c of stmt.children) {
      if (isAttachLike(c, isLayoutParent)) {
        attachStmts.push(c as ModuleCallStatement);
      } else {
        nonAttachChildren.push(c);
      }
    }
    const stmtForHandler = attachStmts.length > 0
      ? { ...stmt, children: nonAttachChildren }
      : stmt;

    let result: Manifold | null;
    // User-defined modules take priority (OpenSCAD semantics)
    const userMod = ctx.modules.get(stmtForHandler.name);
    if (userMod) {
      result = callUserModule(userMod, stmtForHandler, ctx);
    } else {
      const handler = MODULE_HANDLERS[stmtForHandler.name];
      if (!handler) {
        unknownNameError(ctx, 'Unknown module', stmt.name,
          [...Object.keys(MODULE_HANDLERS), ...Object.keys(MODULE_HANDLERS_2D), ...ctx.modules.keys()],
          ...callNameRange(stmt), 'warning');
        return null;
      }
      result = handler(stmtForHandler, ctx);
    }

    // Self-anchoring: if a primitive has anchor= param, translate so anchor
    // point is at origin.
    // Skip for layout modules + drill / pattern modules where `anchor` is a
    // face-selector (which face to drill / which face the cutters land on),
    // not a positioning instruction. Applying the generic self-anchor
    // translation to those would move the parent shape AS IF the user
    // asked it to — they didn't.
    if (result && stmtForHandler.type === 'ModuleCallStatement'
        && !LAYOUT_MODULE_NAMES.has(stmtForHandler.name)
        && !SELF_ANCHOR_SKIP.has(stmtForHandler.name)
        && stmtForHandler.name !== 'frame') {
      const selfAnchor = getArg(stmtForHandler.args, 'anchor', -1, ctx) as number[] | undefined;
      if (selfAnchor && Array.isArray(selfAnchor)) {
        const bbox = result.boundingBox();
        const anchorPt = computeAnchorPoint(bbox, selfAnchor);
        const shifted = result.translate([-anchorPt[0], -anchorPt[1], -anchorPt[2]]);
        trackIntermediate(ctx, shifted);
        result = shifted;
      }
    }

    // Re-key booleanChildParts: the handler received stmtForHandler (a copy),
    // but the top-level loop looks up by the original stmt reference.
    if (stmtForHandler !== stmt) {
      const boolEntry = ctx.booleanChildParts.get(stmtForHandler as ModuleCallStatement);
      if (boolEntry) {
        ctx.booleanChildParts.delete(stmtForHandler as ModuleCallStatement);
        ctx.booleanChildParts.set(stmt, boolEntry);
      }
      const instEntry = ctx.moduleInstances.get(stmtForHandler as ModuleCallStatement);
      if (instEntry) {
        ctx.moduleInstances.delete(stmtForHandler as ModuleCallStatement);
        ctx.moduleInstances.set(stmt, instEntry);
      }
    }

    // Process attach children against parent's bounding box
    if (result && attachStmts.length > 0) {
      // Store body-only manifold before merging attach children,
      // so ghost meshes can show just this body (not combined descendants)
      ctx.bodyOnlyResults.set(stmt, result);
      result = processAttachChildren(result, stmt, attachStmts, ctx);
    }

    if (result && (stmt.debug || stmt.background)) {
      ctx.debugManifolds.push(result);
    }

    // Record color/opacity on the manifold (simple push/pop: ctx.currentColor
    // is set by handleColor, so every manifold produced inside color() gets tagged)
    if (result && (ctx.currentColor || ctx.currentOpacity !== undefined)) {
      ctx.manifoldColors.set(result, {
        color: ctx.currentColor,
        opacity: ctx.currentOpacity,
      });
    }

    return result;
  }

  return null;
}

/**
 * Walk down through transform wrappers (translate/rotate/scale) to find
 * nested boolean operations whose children were saved during evaluation.
 */
const TRANSFORM_NAMES = new Set(['translate', 'rotate', 'scale', 'mirror', 'resize', 'multmatrix', 'color']);
const BOOLEAN_NAMES = new Set(['union', 'difference', 'intersection']);

function findBooleanChildren(
  stmt: Statement,
  map: Map<Statement, { op: 'union' | 'difference' | 'intersection'; parts: GeometryPart[] }>,
): { op: 'union' | 'difference' | 'intersection'; parts: GeometryPart[] } | null {
  // Direct boolean match
  const direct = map.get(stmt);
  if (direct) return direct;

  // Walk through transform/boolean/group wrappers to find nested entries
  if (stmt.type === 'ModuleCallStatement') {
    if (TRANSFORM_NAMES.has(stmt.name) || BOOLEAN_NAMES.has(stmt.name) || stmt.name === 'group' || stmt.name === 'frame') {
      for (const child of stmt.children) {
        const found = findBooleanChildren(child, map);
        if (found) return found;
      }
    }
  }
  return null;
}

/**
 * Walk down through transform wrappers to find statements that had
 * attach children saved during evaluation.
 */
function findAttachChildren(
  stmt: Statement,
  map: Map<Statement, GeometryPart[]>,
): GeometryPart[] | null {
  const direct = map.get(stmt);
  if (direct) return direct;

  if (stmt.type === 'ModuleCallStatement') {
    if (TRANSFORM_NAMES.has(stmt.name) || BOOLEAN_NAMES.has(stmt.name) || stmt.name === 'group' || stmt.name === 'frame') {
      for (const child of stmt.children) {
        const found = findAttachChildren(child, map);
        if (found) return found;
      }
    }
  }
  return null;
}

// --- Public API ---

function createEvalContext(wasm: ManifoldToplevel, fn: number, importedManifolds?: Map<string, InstanceType<ManifoldToplevel['Manifold']>>): EvalContext {
  return {
    wasm,
    vars: new Map<string, unknown>([
      ['$fn', fn],
      ['PI', Math.PI],
      ['CENTER', [0, 0, 0]],
      ['TOP', [0, 0, 1]],
      ['BOTTOM', [0, 0, -1]],
      ['LEFT', [-1, 0, 0]],
      ['RIGHT', [1, 0, 0]],
      ['FRONT', [0, -1, 0]],
      ['BACK', [0, 1, 0]],
      ['X', [1, 0, 0]],
      ['Y', [0, 1, 0]],
      ['Z', [0, 0, 1]],
      ['XY', [1, 1, 0]],
      ['XZ', [1, 0, 1]],
      ['YZ', [0, 1, 1]],
    ]),
    fn,
    errors: [],
    intermediates: [],
    modules: new Map(),
    functions: new Map(),
    callerChildren: [],
    debugManifolds: [],
    booleanChildParts: new Map(),
    attachChildParts: new Map(),
    bodyOnlyResults: new Map(),
    groups: [],
    moduleInstances: new Map(),
    subGroupOf: new Map(),
    instancePath: '',
    frameBoxes: [],
    phantomBBox: null,
    recursionDepth: 0,
    manifoldColors: new Map(),
    importedManifolds: importedManifolds ?? new Map(),
    echoLog: [],
    operations: 0,
    deadline: performance.now() + EVAL_TIMEOUT_MS,
  };
}

/**
 * Evaluate standalone expressions in the program's top-level scope (its
 * module/function definitions and top-level assignments, in order) without
 * building geometry. Used by editor tools that need the value of e.g. a
 * transform argument such as `rotate([0, 0, angle])`. Returns undefined
 * when evaluation fails.
 */
export function createExpressionEvaluator(ast: Program, wasm: ManifoldToplevel): (expr: Expression) => unknown {
  const ctx = createEvalContext(wasm, 32);
  try {
    hoistDefinitions(ast.statements, ctx);
    for (const s of ast.statements) {
      if (s.type === 'AssignmentStatement') ctx.vars.set(s.name, evalExpr(s.value, ctx));
    }
  } catch { /* partial scope is still useful */ }
  return (expr: Expression) => {
    try {
      ctx.deadline = performance.now() + EVAL_TIMEOUT_MS;
      return evalExpr(expr, ctx);
    } catch {
      return undefined;
    }
  };
}

/** Every module name the evaluator implements (3D, 2D and layout). */
export function listModuleNames(): string[] {
  return [...new Set([
    ...Object.keys(MODULE_HANDLERS), ...Object.keys(MODULE_HANDLERS_2D),
    'attach', 'align', 'position', 'frame',
  ])].sort();
}

export function evaluate(ast: Program, wasm: ManifoldToplevel, fn: number = 32, importedManifolds?: Map<string, InstanceType<ManifoldToplevel['Manifold']>>): EvalResult {
  const ctx = createEvalContext(wasm, fn, importedManifolds);

  let result: Manifold | null = null;
  const parts: GeometryPart[] = [];
  try {
    hoistDefinitions(ast.statements, ctx);
    const topManifolds: Manifold[] = [];
    // Unroll if/for at the top level so each leaf statement produces its own
    // part — otherwise evalChildren() inside if/for unions everything and
    // per-child colors are lost.
    const processTop = (stmt: Statement): void => {
      if (stmt.type === 'AssignmentStatement' && stmt.name === '$fn') {
        const val = evalExpr(stmt.value, ctx);
        ctx.vars.set('$fn', val);
        ctx.fn = val as number;
        return;
      }
      if (stmt.type === 'IfStatement') {
        const cond = evalExpr(stmt.condition, ctx);
        const body = cond ? stmt.thenBody : stmt.elseBody;
        for (const s of body) processTop(s);
        return;
      }
      if (stmt.type === 'ForStatement') {
        const rangeVal = evalExpr(stmt.range, ctx);
        if (!Array.isArray(rangeVal) || rangeVal.length > MAX_LOOP_ITERATIONS) return;
        const prevVars = ctx.vars;
        for (const item of rangeVal) {
          const childVars = new Map(prevVars);
          childVars.set(stmt.variable, item);
          ctx.vars = childVars;
          for (const s of stmt.body) processTop(s);
        }
        ctx.vars = prevVars;
        return;
      }
      const m = evalStatement(stmt, ctx);
      if (!m) return;
      topManifolds.push(m);
      const part: GeometryPart = { manifold: m, stmt, from: stmt.from, to: stmt.to };
      const boolResult = findBooleanChildren(stmt, ctx.booleanChildParts);
      if (boolResult) {
        part.children = boolResult.parts;
        part.operation = boolResult.op;
      }
      const attachResult = findAttachChildren(stmt, ctx.attachChildParts);
      if (attachResult) {
        const tagged = attachResult.map(c => ({ ...c, isAttach: true as const }));
        part.children = [...(part.children ?? []), ...tagged];
        if (!part.operation) part.operation = 'union';
      }
      // Also expose DEEPLY-NESTED attach/boolean children as ghosts so the
      // user can pick them (e.g. cursor-driven selection of `align(TOP, …,
      // mode="cut") cylinder(…)` that sits three levels deep). Without this,
      // nested layout children get merged into the outermost manifold and
      // become invisible to the selection system.
      const seen = new Set<GeometryPart>((part.children ?? []) as GeometryPart[]);
      const walkNested = (s: Statement): void => {
        const nested = ctx.attachChildParts.get(s);
        if (nested) {
          for (const np of nested) {
            if (!seen.has(np)) {
              seen.add(np);
              part.children = [...(part.children ?? []), { ...np, isAttach: true as const }];
              if (!part.operation) part.operation = 'union';
            }
          }
        }
        // Module call: the shapes of THIS call's body (and everything nested
        // in it), not whichever call of the same module ran last
        const inst = ctx.moduleInstances.get(s);
        if (inst) {
          for (const g of [inst, ...(inst.nested ?? [])]) {
            if (g.consumed) continue;
            for (const np of g.parts) {
              if (seen.has(np)) continue;
              seen.add(np);
              part.children = [...(part.children ?? []), g.kind === 'attach' ? { ...np, isAttach: true as const } : { ...np, nested: true }];
              if (!part.operation) part.operation = 'union';
            }
          }
        }
        const nestedBool = ctx.booleanChildParts.get(s);
        if (nestedBool) {
          for (const bp of nestedBool.parts) {
            if (!seen.has(bp)) {
              seen.add(bp);
              part.children = [...(part.children ?? []), { ...bp, nested: true }];
              if (!part.operation) part.operation = 'union';
            }
          }
        }
        // Recurse into statement subtree
        if (s.type === 'ModuleCallStatement' && s.children) {
          for (const c of s.children) walkNested(c);
        } else if (s.type === 'ModuleDefinitionStatement' && s.body) {
          for (const c of s.body) walkNested(c);
        } else if (s.type === 'ForStatement' && s.body) {
          for (const c of s.body) walkNested(c);
        } else if (s.type === 'IfStatement') {
          if (s.thenBody) for (const c of s.thenBody) walkNested(c);
          if (s.elseBody) for (const c of s.elseBody) walkNested(c);
        }
      };
      walkNested(stmt);
      const colorInfo = ctx.manifoldColors.get(m);
      if (colorInfo) {
        part.color = colorInfo.color;
        part.opacity = colorInfo.opacity;
      }
      parts.push(part);
    };
    for (const stmt of ast.statements) processTop(stmt);

    // Colored pieces to draw per top-level part: descend through unions,
    // { } blocks and module bodies while a piece has no color of its own.
    // (difference/intersection results are not split — they take their first
    // child's color.) Only used when it yields more than one color.
    for (const part of parts) {
      const pieces: GeometryPart[] = [];
      const visit = (p: GeometryPart, depth: number) => {
        const c = ctx.manifoldColors.get(p.manifold);
        const g = c ? undefined : ctx.subGroupOf.get(p.manifold);
        if (!g || depth > 24) {
          pieces.push(c ? { ...p, color: c.color, opacity: c.opacity } : { ...p, color: undefined, opacity: undefined });
          return;
        }
        for (const k of g.parts) visit(k, depth + 1);
      };
      visit({ ...part, matrix: undefined }, 0);
      const colors = new Set(pieces.map(p => `${p.color ?? '-'}|${p.opacity ?? '-'}`));
      if (pieces.length > 1 && colors.size > 1) part.display = pieces;
    }

    // Implicit union of top-level geometry
    if (topManifolds.length === 0) {
      result = null;
    } else if (topManifolds.length === 1) {
      result = topManifolds[0];
    } else {
      let combined = topManifolds[0];
      for (let i = 1; i < topManifolds.length; i++) {
        const next = combined.add(topManifolds[i]);
        trackIntermediate(ctx, next);
        combined = next;
      }
      result = combined;
    }

    // Union debug manifolds
    let debugManifold: Manifold | null = null;
    if (ctx.debugManifolds.length === 1) {
      debugManifold = ctx.debugManifolds[0];
    } else if (ctx.debugManifolds.length > 1) {
      let combined = ctx.debugManifolds[0];
      for (let i = 1; i < ctx.debugManifolds.length; i++) {
        combined = combined.add(ctx.debugManifolds[i]);
      }
      debugManifold = combined;
    }

    // Clean up intermediates — keep result, debug union, individual parts, children, and debug sources.
    // Also keep fullManifold values and bodyOnlyResults since the WASM Manifold library may
    // share internal data between manifolds, and deleting operands can invalidate derived results.
    const partManifolds = parts.map(p => p.manifold as Manifold | null);
    const displayManifolds = parts.flatMap(p => (p.display ?? []).map(d => d.manifold as Manifold | null));
    const childManifolds = parts.flatMap(p => {
      const manifolds: (Manifold | null)[] = [];
      for (const c of p.children ?? []) {
        manifolds.push(c.manifold as Manifold | null);
        if (c.fullManifold) manifolds.push(c.fullManifold as Manifold | null);
      }
      return manifolds;
    });
    const bodyOnlyManifolds = [...ctx.bodyOnlyResults.values()] as (Manifold | null)[];
    const keep: (Manifold | null)[] = [result, debugManifold, ...partManifolds, ...childManifolds, ...displayManifolds, ...bodyOnlyManifolds, ...ctx.debugManifolds];
    cleanupIntermediates(ctx.intermediates, keep);

    // Free individual debug manifolds that were unioned into debugManifold.
    // Skip any that are also used as child manifolds (e.g. #attach() ghosts).
    if (ctx.debugManifolds.length > 1) {
      const keepAll = new Set<Manifold | null>(keep);
      for (const dm of ctx.debugManifolds) {
        if (dm !== result && dm !== debugManifold && !keepAll.has(dm)) {
          try { dm.delete(); } catch { /* ok */ }
        }
      }
    }

    return { manifold: result, parts, debugManifold, errors: ctx.errors, frameBoxes: ctx.frameBoxes, echoLog: ctx.echoLog };
  } catch (err) {
    cleanupIntermediates(ctx.intermediates, []);
    ctx.errors.push({
      message: `Evaluation error: ${(err as Error).message}`,
      from: ast.from,
      to: ast.to,
      severity: 'error',
    });
    return { manifold: null, parts: [], debugManifold: null, errors: ctx.errors, frameBoxes: [], echoLog: ctx.echoLog };
  }
}
